<template>
    <div
      class="reliance-dashboard"
      :class="{ 'reliance-dashboard--print-layout': preparePrintLayout }"
    >
      <div
        v-if="generatingPrintReport"
        class="reliance-dashboard__print-prep-overlay d-print-none"
      >
        <div class="text-center text-white p-4">
          <b-spinner variant="light" />
          <p class="mt-2 mb-0">Preparing report for print…</p>
        </div>
      </div>
      <!-- Header (hidden on paper — print cover carries the title) -->
      <b-row class="mb-2 d-print-none">
        <b-col cols="12">
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center reliance-dashboard__top-bar">
            <div class="mb-2 mb-md-0">
              <div class="d-flex align-items-center">
                <feather-icon icon="BriefcaseIcon" size="16" class="mr-50" />
                <span class="text-muted">Recruitment Analytics</span>
              </div>
            </div>
            <div class="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center flex-wrap d-print-none reliance-dashboard__top-actions w-100 w-md-auto">
              <b-form-group
                class="mb-2 mb-sm-0 mr-sm-2 flex-grow-1 flex-sm-grow-0"
                v-if="isAdmin"
                label="Client Name"
                label-for="dashboard-client-name-input"
                label-cols-sm="auto"
                label-align-sm="right"
                label-size="sm"
              >
                <b-form-input
                  id="dashboard-client-name-input"
                  v-model="clientName"
                  class="reliance-dashboard__client-name-input"
                  placeholder="Enter client"
                  size="sm"
                  @keyup.enter="refreshData"
                  :disabled="loading"
                />
              </b-form-group>
              <div class="d-flex flex-wrap align-items-center justify-content-stretch justify-content-sm-end">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="flex-fill flex-sm-grow-0 mb-1 mb-sm-0"
                  @click="refreshData"
                  :disabled="loading"
                >
                  <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
                  Refresh
                </b-button>
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="ml-sm-1 flex-fill flex-sm-grow-0"
                  @click="generatePrintReport"
                  :disabled="loading || generatingPrintReport"
                  aria-label="Print report"
                >
                  <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
                  Print report
                </b-button>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-card
        no-body
        class="mb-2 reliance-dashboard__filter-panel reliance-dashboard__filter-panel--global d-print-none"
      >
        <b-card-header
          class="reliance-dashboard__filter-panel-header reliance-dashboard__filter-toggle py-2"
          role="button"
          tabindex="0"
          :aria-expanded="globalFiltersOpen ? 'true' : 'false'"
          aria-controls="reliance-global-filters-collapse"
          @click="globalFiltersOpen = !globalFiltersOpen"
          @keydown.enter.prevent="globalFiltersOpen = !globalFiltersOpen"
          @keydown.space.prevent="globalFiltersOpen = !globalFiltersOpen"
        >
          <div class="d-flex align-items-center flex-wrap">
            <span class="font-weight-bold mb-0 mr-50">Dashboard filters</span>
            <b-badge
              v-if="globalFiltersActiveCount > 0"
              pill
              variant="primary"
              class="reliance-dashboard__filter-count-badge"
            >
              {{ globalFiltersActiveCount }}
            </b-badge>
            <small class="text-muted ml-2 d-none d-lg-inline">Applies to every tab — click Filter to refresh data</small>
          </div>
          <feather-icon
            :icon="globalFiltersOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="18"
            class="text-muted"
          />
        </b-card-header>
        <b-collapse id="reliance-global-filters-collapse" v-model="globalFiltersOpen">
          <b-card-body class="reliance-dashboard__filter-panel-body py-3">
            <p class="text-muted small mb-3">
              Job filters are sent to the dashboard API. Position narrows results on the client when selected.
              Submission date range must include both start and end (YYYY-MM-DD).
            </p>
            <b-row class="reliance-dashboard__filter-grid">
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="2"
                class="mb-1"
              >
                <b-form-group
                  label="Position type"
                  label-for="reliance-filter-position-type"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    id="reliance-filter-position-type"
                    v-model="globalFiltersPending.positionType"
                    :options="positionTypeSelectOptions"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="2"
                class="mb-1"
              >
                <b-form-group
                  label="Function"
                  label-for="reliance-filter-function"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    id="reliance-filter-function"
                    v-model="globalFiltersPending.functionName"
                    :options="globalFunctionSelectOptions"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100"
                    :disabled="filterOptionsLoading"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="3"
                class="mb-1"
              >
                <b-form-group
                  label="Position"
                  label-for="reliance-filter-position"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    id="reliance-filter-position"
                    v-model="globalFiltersPending.positionName"
                    :options="globalPositionSelectOptions"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100"
                    :disabled="filterOptionsLoading"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="3"
                class="mb-1"
              >
                <b-form-group
                  label="Drive name"
                  label-for="reliance-filter-drive"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-select
                    id="reliance-filter-drive"
                    v-model="globalFiltersPending.driveName"
                    :options="globalDriveSelectOptions"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100"
                    :disabled="filterOptionsLoading"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="2"
                class="mb-1"
              >
                <b-form-group
                  label="Submission from"
                  label-for="reliance-filter-submission-start"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-input
                    id="reliance-filter-submission-start"
                    v-model="globalFiltersPending.positionSubmissionStartDate"
                    type="date"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100 reliance-dashboard__filter-control--date"
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                md="4"
                lg="2"
                class="mb-1"
              >
                <b-form-group
                  label="Submission to"
                  label-for="reliance-filter-submission-end"
                  label-size="sm"
                  class="mb-0"
                >
                  <b-form-input
                    id="reliance-filter-submission-end"
                    v-model="globalFiltersPending.positionSubmissionEndDate"
                    type="date"
                    size="sm"
                    class="reliance-dashboard__filter-control w-100 reliance-dashboard__filter-control--date"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <div class="reliance-dashboard__filter-actions d-flex flex-wrap align-items-center mt-2 pt-2 border-top">
              <b-button
                size="sm"
                variant="primary"
                class="mr-1 mb-1"
                :disabled="loading || filterOptionsLoading"
                @click="applyGlobalFilters"
              >
                <feather-icon icon="FilterIcon" size="14" class="mr-50" />
                Filter
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="mb-1"
                :disabled="loading"
                @click="clearGlobalFilters"
              >
                Clear
              </b-button>
              <small
                v-if="filterOptionsLoading"
                class="text-muted ml-2 mb-1"
              >Loading filter lists…</small>
            </div>
          </b-card-body>
        </b-collapse>
      </b-card>
  
      <!-- Tabs -->
      <b-card no-body>
        <!-- Print cover: page 1 only — dashboard title sheet; analysis starts page 2 -->
        <div class="reliance-dashboard__print-toc">
          <div class="reliance-dashboard__print-cover">
            <p class="reliance-dashboard__print-cover-eyebrow">
              Sattva Human · Portal
            </p>
            <h1 class="reliance-dashboard__print-cover-title">
              Recruitment dashboard
            </h1>
            <p class="reliance-dashboard__print-cover-lead">
              Reliance RPO analytics — client export
            </p>
            <p class="reliance-dashboard__print-cover-date text-muted">
              {{ printReportGeneratedAt }}
            </p>
            <div
              v-if="clientName"
              class="reliance-dashboard__print-cover-client"
            >
              <span class="reliance-dashboard__print-cover-client-label">Client</span>
              <span class="reliance-dashboard__print-cover-client-value">{{ clientName }}</span>
            </div>
            <div
              class="reliance-dashboard__print-cover-accent"
              aria-hidden="true"
            />
            <p class="reliance-dashboard__print-cover-sections-title">
              Sections in this report
            </p>
            <ol class="reliance-dashboard__print-cover-sections">
              <li
                v-for="label in printReportTabLabels"
                :key="`print-section-${label}`"
              >
                {{ label }}
              </li>
            </ol>
            <p class="reliance-dashboard__print-cover-footnote text-muted">
              Detailed analysis begins on the next page. Large tables omit row data in print (see each section).
            </p>
          </div>
        </div>

        <b-tabs
          v-model="activeTabIndex"
          pills
          card
          class="custom-tabs"
          @input="onDashboardTabInput"
        >
          <!-- Detailed Overview Tab (primary) -->
          <b-tab title="Detailed Overview">
            <h2 class="reliance-dashboard__print-section-title">
              Detailed Overview
            </h2>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
              <p class="mt-2 text-muted">Loading detailed overview...</p>
            </div>
            <div v-else>
              <!-- Total Positions as simple header -->
              <div class="d-flex align-items-center mb-3">
                <b-avatar
                  size="36"
                  variant="light-secondary"
                  class="mr-2"
                >
                  <feather-icon
                    icon="BriefcaseIcon"
                    size="20"
                  />
                </b-avatar>
                <div>
                  <span class="font-weight-bold">Total Positions</span>
                  <span class="h4 font-weight-bolder mb-0 ml-2 align-middle">{{ openPositionsCount }}</span>
                </div>
              </div>
              <!-- Section 1: KPI – Vuexy card-statistics (5 stages) -->
              <b-card
                no-body
                class="card-statistics mb-4"
              >
                <b-card-body class="statistics-body">
                  <b-row>
                    <b-col
                      v-for="card in detailedKpiStageCards"
                      :key="card.key"
                      cols="12"
                      sm="6"
                      md="4"
                      lg
                      class="mb-2 mb-sm-0"
                    >
                      <b-media no-body>
                        <b-media-body class="mr-2">
                          <b-row >
                            <b-col class="mb-1">
                              <b-avatar
                                size="48"
                                :variant="'light-' + (card.color === 'dark' ? 'secondary' : card.color)"
                              >
                                <feather-icon
                                  :icon="card.icon"
                                  size="24"
                                />
                              </b-avatar>
                            </b-col>
                        <!-- </b-media-aside> -->
                            <b-col class="mt-1">
                              <h4 class="font-weight-bolder mb-0 ">
                                {{ card.value }}
                              </h4>
                              <b-card-text class="mb-0  font-weight-bold">
                                {{ card.label }}
                              </b-card-text>
                            </b-col>
                          </b-row>
                          <div
                            v-if="card.sub && card.sub.length"
                            class="pt-1 border-top"
                          >
                            <b-list-group flush class="pl-0">
                              <b-list-group-item
                                v-for="(item, idx) in card.sub"
                                :key="idx"
                                class="d-flex justify-content-between align-items-center py-1 small pl-0 text-left"
                              >
                                <span class="text-left">{{ item.label }}</span>
                                <b-badge
                                  pill
                                  :variant="'light-' + (card.color === 'dark' ? 'secondary' : card.color)"
                                >
                                  {{ item.value }}{{ card.value > 0 ? ' (' + Math.round((item.value / card.value) * 100) + '%)' : '' }}
                                </b-badge>
                              </b-list-group-item>
                            </b-list-group>
                          </div>
                        </b-media-body>
                      </b-media>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <!-- Section 2: Position wise Detailed Summary -->
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="text-muted font-weight-bold mb-0">
                  Position wise Detailed Summary
                </h5>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="d-print-none"
                  @click="downloadPositionWiseSummaryExcel"
                  :disabled="!filteredDetailedPositionSummaryFlattened || filteredDetailedPositionSummaryFlattened.length === 0"
                >
                  <feather-icon
                    icon="DownloadIcon"
                    class="mr-50"
                  />
                  Download Excel
                </b-button>
              </div>
              <div class="table-responsive">
                <b-table
                  :items="printReportTableItems(filteredDetailedPositionSummaryFlattened)"
                  :fields="detailedSummaryFields"
                  striped
                  hover
                  small
                  responsive
                  show-empty
                  :empty-text="printReportTableEmptyText(filteredDetailedPositionSummaryFlattened, 'No position data available')"
                  class="mb-0"
                >
                  <template #thead-top>
                    <tr>
                      <th colspan="2" class="bg-light text-dark border-right text-left pl-3">
                        Position
                      </th>
                      <th colspan="1" class="bg-secondary text-white text-center border-right">
                        Total Positions
                      </th>
                      <th colspan="3" class="bg-info text-white text-center">
                        Sourcing
                      </th>
                      <th colspan="4" class="bg-warning text-dark text-center">
                        Screening
                      </th>
                      <th colspan="5" class="bg-danger text-white text-center">
                        Interview
                      </th>
                      <th colspan="6" class="bg-success text-white text-center">
                        Offer
                      </th>
                      <th colspan="5" class="bg-primary text-white text-center">
                        Onboarding
                      </th>
                    </tr>
                  </template>
                </b-table>
              </div>
            </div>
          </b-tab>

          <!-- Overall Tab -->
          <b-tab title="Overall">
            <h2 class="reliance-dashboard__print-section-title">
              Overall
            </h2>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
              <p class="mt-2 text-muted">Loading dashboard data...</p>
            </div>
  
            <div v-else>
              <!-- KPI Cards -->
              <b-row>
                <b-col
                  v-for="kpi in kpiCards"
                  :key="kpi.key"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  xl="2"
                  class="mb-1"
                >
                  <b-card
                    class="kpi-card"
                    :class="{ 'active': selectedKPI === kpi.key }"
                    
                    role="button"
                    body-class="p-1"
                  >
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1">
                        <h6 class="mb-50 text-muted kpi-title">{{ kpi.label }}</h6>
                        <h3 class="mb-0 font-weight-bolder">{{ kpi.value }}</h3>
                      </div>
                      <div class="avatar avatar-sm" :class="`bg-light-${kpi.color}`">
                        <div class="avatar-content">
                          <feather-icon
                            :icon="kpi.icon"
                            size="20"
                            :class="`text-${kpi.color}`"
                          />
                        </div>
                      </div>
                    </div>
                    <!-- Week-on-Week Comparison -->
                    <div v-if="kpi.wow" class="mt-75 wow-comparison">
                      <div class="d-flex align-items-center">
                        <span 
                          class="wow-badge"
                          :class="kpi.wow.value >= 0 ? 'wow-up' : 'wow-down'"
                      >
                        <feather-icon
                            :icon="kpi.wow.value >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'"
                            size="12"
                          />
                          <span class="wow-value">{{ Math.abs(kpi.wow.value) }}</span>
                        </span>
                        <span 
                          class="wow-percent ml-1"
                          :class="kpi.wow.percentage >= 0 ? 'text-success' : 'text-danger'"
                        >
                          {{ kpi.wow.percentage >= 0 ? '+' : '' }}{{ kpi.wow.percentage }}%
                        </span>
                        <span class="text-muted ml-50 wow-label">vs last week</span>
                      </div>
                    </div>
                    <!-- No WoW data available indicator -->
                    <div v-else class="mt-75">
                      <small class="text-muted" style="font-size: 0.65rem;">
                        <feather-icon icon="MinusIcon" size="10" class="mr-25" />
                        No weekly data
                      </small>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
  
              <!-- Expanded Data Table -->
              <b-collapse v-model="showExpandedData" class="mt-2">
                <b-card>
                  <b-card-header class="pb-1">
                    <h4 class="mb-0">Position Details</h4>
                  </b-card-header>
                    <b-card-body>
                    <div class="d-flex justify-content-end mb-2 flex-wrap align-items-center">
                      <b-button
                        size="sm"
                        variant="primary"
                        class="d-inline-flex align-items-center d-print-none"
                        @click="downloadPositionDetails"
                        :disabled="filteredPositionData.length === 0"
                      >
                        <feather-icon icon="DownloadIcon" size="14" class="mr-50" />
                        <span>Download</span>
                      </b-button>
                      <small class="text-muted align-self-center ml-sm-2 mt-1 mt-sm-0">{{ filteredPositionData.length }} of {{ positionData.length }} positions</small>
                    </div>
                    <!-- Table -->
                    <div class="table-responsive">
                      <b-table
                        :items="printReportTableItems(filteredPositionData)"
                        :fields="positionFields"
                        striped
                        hover
                        responsive
                        show-empty
                        :empty-text="printReportTableEmptyText(filteredPositionData, 'No positions match the current filters')"
                        class="mb-0"
                      >
                        <template #cell(position_name)="data">
                          <div class="font-weight-bold">{{ data.value }}</div>
                        </template>

                        <template #cell(function)="data">
                          <b-badge variant="light-primary">{{ data.value || 'N/A' }}</b-badge>
                        </template>
  
                        <template #cell(open_positions)="data">
                          <div class="position-metric">
                          <b-badge variant="light-info">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'open_positions')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'open_positions').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'open_positions'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'open_positions').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(profile_shared)="data">
                          <div class="position-metric">
                          <b-badge variant="light-primary">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'profile_shared')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'profile_shared').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'profile_shared'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'profile_shared').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(screen_select)="data">
                          <div class="position-metric">
                          <b-badge variant="light-success">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'screen_select')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'screen_select').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'screen_select'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'screen_select').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(interview)="data">
                          <div class="position-metric">
                          <b-badge variant="light-warning">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'interview')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'interview').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'interview'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'interview').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(selections)="data">
                          <div class="position-metric">
                          <b-badge variant="success">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'selections')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'selections').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'selections'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'selections').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(documentation)="data">
                          <div class="position-metric">
                          <b-badge variant="light-secondary">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'documentation')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'documentation').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'documentation'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'documentation').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(medical)="data">
                          <div class="position-metric">
                          <b-badge variant="light-info">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'medical')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'medical').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'medical'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'medical').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(offered)="data">
                          <div class="position-metric">
                          <b-badge variant="primary">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'offered')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'offered').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'offered'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'offered').value) }}
                            </span>
                          </div>
                        </template>
  
                        <template #cell(closed_positions)="data">
                          <div class="position-metric">
                          <b-badge variant="dark">{{ data.value }}</b-badge>
                            <span v-if="getPositionWow(data.item, 'closed_positions')" 
                              class="wow-inline"
                              :class="getPositionWow(data.item, 'closed_positions').value >= 0 ? 'wow-up' : 'wow-down'"
                              v-b-tooltip.hover 
                              :title="getWowTooltip(getPositionWow(data.item, 'closed_positions'))"
                            >
                              {{ formatWowValue(getPositionWow(data.item, 'closed_positions').value) }}
                            </span>
                          </div>
                        </template>
                      </b-table>
                    </div>
                  </b-card-body>
                </b-card>
              </b-collapse>

              <!-- Rejection Analysis Section -->
              <b-row class="mt-3" v-if="rejectionRatiosDisplay && rejectionRatiosDisplay.length > 0">
                <!-- Rejection Funnel Chart -->
                <b-col cols="12" lg="5" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">
                        <feather-icon icon="AlertCircleIcon" size="18" class="mr-2 text-danger" />
                        Rejection Funnel
                      </h4>
                    </b-card-header>
                    <b-card-body>
                      <div id="rejectionFunnelChart" class="reliance-dashboard__main-chart-host" />
                    </b-card-body>
                  </b-card>
                </b-col>

                <!-- Rejection Ratios Table -->
                <b-col cols="12" lg="7" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">
                        <feather-icon icon="TrendingDownIcon" size="18" class="mr-2 text-danger" />
                        Rejection Conversion Ratios
                      </h4>
                    </b-card-header>
                    <b-card-body class="p-0">
                      <div class="table-responsive">
                        <b-table
                          :items="printReportTableItems(rejectionRatiosDisplay)"
                          :fields="rejectionFields"
                          striped
                          hover
                          responsive
                          show-empty
                          :empty-text="printReportTableEmptyText(rejectionRatiosDisplay, 'No rejection funnel data')"
                          class="mb-0"
                        >
                          <template #cell(stage_transition)="data">
                            <div class="d-flex align-items-center">
                              <feather-icon icon="ArrowRightIcon" size="14" class="mr-50 text-danger" />
                              <span class="font-weight-bold">{{ data.value }}</span>
                            </div>
                          </template>

                          <template #cell(rejection_count)="data">
                            <b-badge variant="danger">{{ data.value }}</b-badge>
                          </template>

                          <template #cell(total_at_stage)="data">
                            <b-badge variant="light-secondary">{{ data.value }}</b-badge>
                          </template>

                          <template #cell(conversion_rate)="data">
                            <div class="d-flex align-items-center">
                              <b-progress
                                :value="data.value"
                                max="100"
                                height="8px"
                                class="flex-grow-1 mr-1"
                                variant="danger"
                              />
                              <span class="font-weight-bold text-danger">{{ data.value }}%</span>
                            </div>
                          </template>
                        </b-table>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-tab>
  
          <!-- Selections Tab -->
          <b-tab title="Selections">
            <h2 class="reliance-dashboard__print-section-title">
              Selections
            </h2>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
            </div>
  
            <div v-else>
              <b-row>
                <!-- Funnel Chart -->
                <b-col cols="12" lg="6" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">Selection Funnel</h4>
                    </b-card-header>
                    <b-card-body>
                      <div id="selectionFunnelChart" class="reliance-dashboard__selection-funnel-host" />
                    </b-card-body>
                  </b-card>
                </b-col>
  
                <!-- Conversion Ratios -->
                <b-col cols="12" lg="6" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">Conversion Ratios</h4>
                    </b-card-header>
                    <b-card-body>
                      <div
                        v-for="ratio in conversionRatios"
                        :key="ratio.stage"
                        class="d-flex justify-content-between align-items-center mb-2"
                      >
                        <div class="d-flex align-items-center">
                          <div class="avatar avatar-sm mr-1" :class="`bg-light-${ratio.color}`">
                            <feather-icon :icon="ratio.icon" size="18" :class="`text-${ratio.color}`" />
                          </div>
                          <span class="font-weight-bold">{{ ratio.stage }}</span>
                        </div>
                        <div class="text-right">
                          <h4 class="mb-0 font-weight-bolder" :class="`text-${ratio.color}`">
                            {{ ratio.percentage }}%
                          </h4>
                          <small class="text-muted">{{ ratio.count }} / {{ ratio.total }}</small>
                        </div>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
  
              <!-- Selection Details Table (using positionSelectionFunnel) -->
              <b-row>
                <b-col cols="12">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">Selection Details by Position</h4>
                    </b-card-header>
                    <b-card-body class="p-0">
                      <div class="table-responsive">
                        <b-table
                          :items="printReportTableItems(filteredPositionSelectionFunnel)"
                          :fields="selectionFields"
                          striped
                          hover
                          responsive
                          show-empty
                          :empty-text="printReportTableEmptyText(filteredPositionSelectionFunnel, 'No selection data for current filters')"
                          class="mb-0"
                        >
                          <template #cell(position_name)="data">
                            <div class="font-weight-bold">{{ data.value }}</div>
                          </template>
  
                          <template #cell(total_selection)="data">
                            <b-badge variant="success">{{ data.value }}</b-badge>
                          </template>
  
                          <template #cell(total_documentation)="data">
                            <b-badge variant="light-secondary">{{ data.value }}</b-badge>
                          </template>
  
                          <template #cell(total_offered)="data">
                            <b-badge variant="primary">{{ data.value }}</b-badge>
                          </template>
  
                          <template #cell(total_joined)="data">
                            <b-badge variant="info">{{ data.value }}</b-badge>
                          </template>
  
                          <template #cell(conversion_rate)="data">
                            <div class="d-flex align-items-center">
                              <b-progress
                                :value="getSelectionConversionRate(data.item)"
                                max="100"
                                height="8px"
                                class="flex-grow-1 mr-1"
                                :variant="getProgressVariant(getSelectionConversionRate(data.item))"
                              />
                              <span class="font-weight-bold">{{ getSelectionConversionRate(data.item) }}%</span>
                            </div>
                          </template>
                        </b-table>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-tab>
  
          <!-- Timelines (TAT) Tab -->
          <b-tab title="Timelines">
            <h2 class="reliance-dashboard__print-section-title">
              Timelines
            </h2>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
            </div>
  
            <div v-else>
              <b-alert
                v-if="globalFilters.positionName && !tatDataHasPositionScope"
                show
                variant="light"
                class="mb-2 py-2 small text-dark border"
              >
                Position filter cannot narrow TAT rows until the API includes position on timeline data. Other dashboard filters are applied server-side.
              </b-alert>
              <!-- TAT Summary Cards -->
              <b-row class="mb-2">
                <b-col
                  v-for="(tat, tatIdx) in displayTatSummary"
                  :key="'tat-sum-' + tatIdx + '-' + (tat.stage || '')"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                  class="mb-2"
                >
                  <b-card class="tat-card">
                    <div class="d-flex align-items-start">
                      <div class="avatar mr-1" :class="`bg-light-${tat.color}`">
                        <feather-icon :icon="tat.icon" size="20" :class="`text-${tat.color}`" />
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="mb-50 text-muted">{{ tat.stage }}</h6>
                        <h3 class="mb-0 font-weight-bolder">{{ tat.avgDays }} days</h3>
                        <small class="text-muted">Average TAT</small>
                      </div>
                    </div>
                  </b-card>
                </b-col>
              </b-row>
  
              <!-- TAT Details Table -->
              <b-row>
                <b-col cols="12">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">Turn Around Time (TAT) Analysis</h4>
                    </b-card-header>
                    <b-card-body class="p-0">
                      <div class="table-responsive">
                        <b-table
                          :items="printReportTableItems(displayTatData)"
                          :fields="tatFields"
                          striped
                          hover
                          responsive
                          show-empty
                          :empty-text="printReportTableEmptyText(displayTatData, 'No TAT data for current filters')"
                          class="mb-0"
                        >
                          <template #cell(stage_transition)="data">
                            <div class="d-flex align-items-center">
                              <feather-icon icon="ArrowRightIcon" size="16" class="mr-50" />
                              <span class="font-weight-bold">{{ data.value }}</span>
                            </div>
                          </template>
  
                          <template #cell(avg_days)="data">
                            <b-badge
                              :variant="getTATVariant(data.value)"
                              class="font-weight-bold"
                            >
                              {{ data.value }} days
                            </b-badge>
                          </template>
  
                          <template #cell(min_days)="data">
                            <span class="text-success font-weight-bold">{{ data.value }} days</span>
                          </template>
  
                          <template #cell(max_days)="data">
                            <span class="text-danger font-weight-bold">{{ data.value }} days</span>
                          </template>
  
                          <template #cell(trend)="data">
                            <b-badge
                              :variant="data.item.trend_direction === 'improving' ? 'light-success' : 'light-danger'"
                            >
                              <feather-icon
                                :icon="data.item.trend_direction === 'improving' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
                                size="12"
                              />
                              {{ data.value }}%
                            </b-badge>
                          </template>
                        </b-table>
                      </div>
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
  
              <!-- TAT Timeline Chart -->
              <b-row class="mt-2">
                <b-col cols="12">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h4 class="mb-0">TAT Timeline Overview</h4>
                    </b-card-header>
                    <b-card-body>
                      <div id="tatTimelineChart" class="reliance-dashboard__tat-chart-host" />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-tab>

          <!-- Diversity Tab -->
          <b-tab title="Diversity">
            <h2 class="reliance-dashboard__print-section-title">
              Diversity
            </h2>
            <div v-if="loading" class="text-center py-5">
              <b-spinner variant="primary" label="Loading..." />
            </div>
            <div v-else>
              <b-card
                no-body
                class="card-statistics mb-3 reliance-dashboard__gender-pipeline"
              >
                <b-card-body class="statistics-body py-3">
                  <h5 class="mb-1 font-weight-bold d-flex align-items-center">
                    <feather-icon icon="UsersIcon" size="18" class="mr-50 text-info" />
                    Pipeline by gender
                  </h5>
                  <p class="text-muted small mb-3 mb-md-2">
                    After Diversity filters: current hiring status. Header total = all candidates of that gender in scope; row percentages are share of that total.
                  </p>
                  <b-row>
                    <b-col
                      v-for="col in diversityGenderPipelineColumns"
                      :key="col.gender"
                      cols="12"
                      sm="6"
                      md="4"
                      lg
                      class="mb-2 mb-lg-0"
                    >
                      <div class="reliance-dashboard__gender-pipeline-column h-100 border rounded p-2 bg-white">
                        <b-row class="align-items-center mb-1 no-gutters">
                          <b-col cols="auto" class="pr-1">
                            <b-avatar :variant="col.avatarVariant" size="44">
                              <feather-icon :icon="col.icon" size="22" />
                            </b-avatar>
                          </b-col>
                          <b-col>
                            <h4 class="font-weight-bolder mb-0">{{ col.total }}</h4>
                            <span class="font-weight-bold">{{ col.gender }}</span>
                          </b-col>
                        </b-row>
                        <div class="pt-1 border-top">
                          <b-list-group flush class="pl-0">
                            <b-list-group-item
                              v-for="(item, idx) in col.items"
                              :key="idx"
                              class="d-flex justify-content-between align-items-center py-1 small pl-0 text-left border-0"
                            >
                              <span>{{ item.label }}</span>
                              <b-badge pill :variant="item.badgeVariant">
                                {{ item.value }} ({{ item.pct }}%)
                              </b-badge>
                            </b-list-group-item>
                          </b-list-group>
                        </div>
                      </div>
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>

              <b-row>
                <b-col cols="12" md="6" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h5 class="mb-0">Gender mix</h5>
                      <small class="text-muted reliance-dashboard__chart-help">API <code>gender</code> (M/F as Male/Female)</small>
                    </b-card-header>
                    <b-card-body class="reliance-dashboard__chart-card-body">
                      <div id="diversityGenderPieChart" class="reliance-dashboard__diversity-chart" />
                    </b-card-body>
                  </b-card>
                </b-col>
                <b-col cols="12" md="6" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h5 class="mb-0">Selection → offer process</h5>
                      <small class="text-muted reliance-dashboard__chart-help">
                        % of the <strong>selection cohort</strong> (Selected, Shortlisted, Offered, Offer Accepted, Joined) who reached each milestone in <code>offerProcess</code>.
                        Tooltip includes gender split (diversity signal). Candidates without <code>offerProcess</code> count as not yet reached.
                      </small>
                    </b-card-header>
                    <b-card-body class="reliance-dashboard__chart-card-body">
                      <div id="diversitySelectionOfferStageBarChart" class="reliance-dashboard__diversity-chart" />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
              <b-row class="mb-2">
                <b-col cols="12" lg="7" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h5 class="mb-0">Pipeline by gender</h5>
                      <small class="text-muted reliance-dashboard__chart-help">Shortlisted = Selected or Shortlisted · Offered includes Offer Accepted</small>
                    </b-card-header>
                    <b-card-body class="reliance-dashboard__chart-card-body">
                      <div id="diversityPipelineByGenderBarChart" class="reliance-dashboard__diversity-pipeline-chart" />
                    </b-card-body>
                  </b-card>
                </b-col>
                <b-col cols="12" lg="5" class="mb-2">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h5 class="mb-0">Pipeline mix (all)</h5>
                      <small class="text-muted reliance-dashboard__chart-help">Shortlisted / Offered / Joined in current scope</small>
                    </b-card-header>
                    <b-card-body class="reliance-dashboard__chart-card-body">
                      <div id="diversityPipelinePieChart" class="reliance-dashboard__diversity-pipeline-chart" />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
              <b-row>
                <b-col cols="12">
                  <b-card>
                    <b-card-header class="pb-1">
                      <h5 class="mb-0">Summary</h5>
                    </b-card-header>
                    <b-card-body class="p-0">
                      <b-table
                        :items="printReportTableItems(diversitySummaryRows)"
                        :fields="diversitySummaryFields"
                        small
                        striped
                        hover
                        show-empty
                        :empty-text="printReportTableEmptyText(diversitySummaryRows, 'No candidates in scope — adjust filters or refresh candidate data')"
                        class="mb-0"
                      />
                    </b-card-body>
                  </b-card>
                </b-col>
              </b-row>
            </div>
          </b-tab>

        <!-- Candidate Data Tab -->
        <b-tab title="Candidate Data">
          <h2 class="reliance-dashboard__print-section-title">
            Candidate Data
          </h2>
          <div v-if="candidatesLoading" class="text-center py-5">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <p class="mt-2 text-muted">Loading candidates...</p>
          </div>

          <div v-else>
            <b-row class="mb-2">
              <b-col lg="3" md="6" sm="12" class="mb-1">
                <b-card class="metric-card primary-gradient">
                  <div class="metric-content">
                    <div class="metric-info">
                      <h3 class="metric-value">{{ candidateDataTabKpiSummary.total }}</h3>
                      <p class="metric-label">Total Candidates</p>
                    </div>
                    <div class="metric-icon">
                      <feather-icon icon="UsersIcon" size="24" />
                    </div>
                  </div>
                </b-card>
              </b-col>

              <b-col lg="3" md="6" sm="12" class="mb-1">
                <b-card class="metric-card success-gradient">
                  <div class="metric-content">
                    <div class="metric-info">
                      <h3 class="metric-value">{{ candidateDataTabKpiSummary.joined }}</h3>
                      <p class="metric-label">Joined</p>
                    </div>
                    <div class="metric-icon">
                      <feather-icon icon="UserCheckIcon" size="24" />
                    </div>
                  </div>
                </b-card>
              </b-col>

              <b-col lg="3" md="6" sm="12" class="mb-1">
                <b-card class="metric-card info-gradient">
                  <div class="metric-content">
                    <div class="metric-info">
                      <h3 class="metric-value">{{ candidateDataTabKpiSummary.offered }}</h3>
                      <p class="metric-label">Offered</p>
                    </div>
                    <div class="metric-icon">
                      <feather-icon icon="UserPlusIcon" size="24" />
                    </div>
                  </div>
                </b-card>
              </b-col>

              <b-col lg="3" md="6" sm="12" class="mb-1">
                <b-card class="metric-card warning-gradient">
                  <div class="metric-content">
                    <div class="metric-info">
                      <h3 class="metric-value">{{ candidateDataTabKpiSummary.selected }}</h3>
                      <p class="metric-label">Selected + Shortlisted</p>
                    </div>
                    <div class="metric-icon">
                      <feather-icon icon="UserIcon" size="24" />
                    </div>
                  </div>
                </b-card>
              </b-col>
            </b-row>

            <!-- Weekwise Analytics Row -->
            <b-row class="mb-2">
              <!-- Upcoming Joining -->
              <b-col cols="12" md="6" lg="3" class="mb-2">
                <b-card class="weekwise-card">
                  <b-card-header class="pb-1">
                    <h6 class="mb-0">
                      <feather-icon icon="CalendarIcon" size="16" class="mr-1 text-primary" />
                      Upcoming Joining
                    </h6>
                  </b-card-header>
                  <b-card-body style="max-height: 200px; overflow-y: auto;">
                    <div v-if="upcomingJoiningWeekwise.length > 0">
                      <div v-for="week in upcomingJoiningWeekwise" :key="week.weekKey" class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-truncate" style="font-size: 0.85em;">{{ week.weekLabel }}</span>
                        <b-badge variant="primary">{{ week.count }}</b-badge>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-2">
                      <small>No upcoming joinings</small>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Joined Weekwise -->
              <b-col cols="12" md="6" lg="3" class="mb-2">
                <b-card class="weekwise-card">
                  <b-card-header class="pb-1">
                    <h6 class="mb-0">
                      <feather-icon icon="UserCheckIcon" size="16" class="mr-1 text-success" />
                      Joined (by Week)
                    </h6>
                  </b-card-header>
                  <b-card-body style="max-height: 200px; overflow-y: auto;">
                    <div v-if="joinedWeekwise.length > 0">
                      <div v-for="week in joinedWeekwise" :key="week.weekKey" class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-truncate" style="font-size: 0.85em;">{{ week.weekLabel }}</span>
                        <b-badge variant="success">{{ week.count }}</b-badge>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-2">
                      <small>No joined candidates</small>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Selections Weekwise -->
              <b-col cols="12" md="6" lg="3" class="mb-2">
                <b-card class="weekwise-card">
                  <b-card-header class="pb-1">
                    <h6 class="mb-0">
                      <feather-icon icon="StarIcon" size="16" class="mr-1 text-warning" />
                      Selections (by Week)
                    </h6>
                  </b-card-header>
                  <b-card-body style="max-height: 200px; overflow-y: auto;">
                    <div v-if="selectionsWeekwise.length > 0">
                      <div v-for="week in selectionsWeekwise" :key="week.weekKey" class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-truncate" style="font-size: 0.85em;">{{ week.weekLabel }}</span>
                        <b-badge variant="warning">{{ week.count }}</b-badge>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-2">
                      <small>No selections</small>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>

              <!-- Offered Weekwise -->
              <b-col cols="12" md="6" lg="3" class="mb-2">
                <b-card class="weekwise-card">
                  <b-card-header class="pb-1">
                    <h6 class="mb-0">
                      <feather-icon icon="GiftIcon" size="16" class="mr-1 text-info" />
                      Offered (by Week)
                    </h6>
                  </b-card-header>
                  <b-card-body style="max-height: 200px; overflow-y: auto;">
                    <div v-if="offeredWeekwise.length > 0">
                      <div v-for="week in offeredWeekwise" :key="week.weekKey" class="d-flex justify-content-between align-items-center mb-1">
                        <span class="text-truncate" style="font-size: 0.85em;">{{ week.weekLabel }}</span>
                        <b-badge variant="info">{{ week.count }}</b-badge>
                      </div>
                    </div>
                    <div v-else class="text-center text-muted py-2">
                      <small>No offers</small>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <!-- Weekwise Status Summary Table -->
            <b-row class="mb-3">
              <b-col cols="12">
                <b-card>
                  <b-card-header class="pb-1">
                    <h5 class="mb-0">
                      <feather-icon icon="CalendarIcon" size="18" class="mr-2" />
                      Weekwise Status Summary
                    </h5>
                  </b-card-header>
                  <b-card-body class="p-0">
                    <div class="table-responsive">
                      <b-table
                        :items="printReportTableItems(weekwiseStatusSummary)"
                        :fields="weekwiseStatusFields"
                        striped
                        hover
                        responsive
                        show-empty
                        :empty-text="printReportTableEmptyText(weekwiseStatusSummary, 'No weekwise data available')"
                        class="mb-0"
                      >
                        <template #cell(weekLabel)="data">
                          <div class="font-weight-bold">
                            <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
                            {{ data.value }}
                          </div>
                        </template>

                        <template #cell(profileShared)="data">
                          <b-badge variant="light-primary">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(screenSelect)="data">
                          <b-badge variant="light-success">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(interview)="data">
                          <b-badge variant="light-warning">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(selected)="data">
                          <b-badge variant="success">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(offered)="data">
                          <b-badge variant="info">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(joined)="data">
                          <b-badge variant="primary">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(screenReject)="data">
                          <b-badge variant="light-danger">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(drop)="data">
                          <b-badge variant="warning">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(rejected)="data">
                          <b-badge variant="danger">{{ data.value }}</b-badge>
                        </template>

                        <template #cell(total)="data">
                          <b-badge variant="secondary">{{ data.value }}</b-badge>
                        </template>
                      </b-table>
                    </div>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <!-- Week-wise hiring charts (Diversity filters) -->
            <b-row class="mb-3 reliance-dashboard__charts-row">
              <b-col cols="12" md="6" lg="4" class="mb-3 mb-lg-0">
                <b-card>
                  <b-card-header class="pb-1">
                    <h5 class="mb-0">
                      <feather-icon icon="GiftIcon" size="18" class="mr-2" />
                      Offered (by week)
                    </h5>
                    <small class="text-muted d-block mt-50 px-2 pb-1">
                      Offered + Offer Accepted
                    </small>
                  </b-card-header>
                  <b-card-body>
                    <div id="candidateOfferSelectionBarChart" class="reliance-dashboard__week-chart-host" />
                  </b-card-body>
                </b-card>
              </b-col>
              <b-col cols="12" md="6" lg="4" class="mb-3 mb-lg-0">
                <b-card>
                  <b-card-header class="pb-1">
                    <h5 class="mb-0">
                      <feather-icon icon="StarIcon" size="18" class="mr-2" />
                      Selection (by week)
                    </h5>
                    <small class="text-muted d-block mt-50 px-2 pb-1">
                      Selected + Shortlisted
                    </small>
                  </b-card-header>
                  <b-card-body>
                    <div id="candidateSelectionWeekBarChart" class="reliance-dashboard__week-chart-host" />
                  </b-card-body>
                </b-card>
              </b-col>
              <b-col cols="12" md="12" lg="4">
                <b-card>
                  <b-card-header class="pb-1">
                    <h5 class="mb-0">
                      <feather-icon icon="UserCheckIcon" size="18" class="mr-2" />
                      Joiners (by week)
                    </h5>
                    <small class="text-muted d-block mt-50 px-2 pb-1">
                      Joined, bucketed by joining-date week
                    </small>
                  </b-card-header>
                  <b-card-body>
                    <div id="candidateJoinedWeekBarChart" class="reliance-dashboard__week-chart-host" />
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>
            <b-row class="mb-3 reliance-dashboard__charts-row justify-content-center">
              <b-col cols="12" class="d-flex justify-content-center px-2">
                <b-card class="w-100 reliance-dashboard__drop-reason-card mb-0">
                  <b-card-header class="pb-1 text-center reliance-dashboard__drop-reason-card-header">
                    <h5 class="mb-0 d-inline-flex align-items-center justify-content-center flex-wrap reliance-dashboard__drop-reason-card-title">
                      <feather-icon icon="PieChartIcon" size="18" class="mr-50 reliance-dashboard__drop-reason-card-title-icon" />
                      <span>Drop reasons</span>
                    </h5>
                  </b-card-header>
                  <b-card-body class="pt-2">
                    <div id="dropReasonPieChart" class="reliance-dashboard__drop-reason-chart" />
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <!-- Latest Reliance Updates & Offer Process Stage Analysis (above candidates table) -->
            <b-row class="mb-3">
              <b-col cols="12" lg="5" class="mb-3 mb-lg-0">
                <b-card title="Latest Reliance Updates" class="timeline-card h-100">
                  <div class="timeline-scroll" style="max-height: 400px; overflow-y: auto;">
                    <app-timeline v-if="latestUpdates.length > 0">
                      <app-timeline-item
                        v-for="update in latestUpdates"
                        :key="update.id"
                        :variant="update.variant"
                      >
                        <template #default>
                          <div class="timeline-details d-flex flex-wrap justify-content-between align-items-start">
                            <div class="timeline-main-info flex-grow-1 pr-2" style="min-width: 0;">
                              <div class="candidate-name font-weight-bold text-truncate">{{ getCandidateName(update.candidate) }}</div>
                              <div class="position-name text-muted text-truncate" style="font-size: 0.85em;">{{ getPositionName(update.candidate) }}</div>
                            </div>
                            <div class="timeline-side-info text-right flex-shrink-0">
                              <div class="status-info mb-50">
                                <b-badge :variant="getStatusVariant(update.status)">
                                  {{ update.status }}
                                </b-badge>
                              </div>
                              <div class="update-time">
                                <small class="text-muted">{{ update.timeAgo }}</small>
                              </div>
                            </div>
                          </div>
                        </template>
                      </app-timeline-item>
                    </app-timeline>
                    <div v-else class="text-center text-muted py-3">
                      <feather-icon icon="InfoIcon" size="24" class="mb-1" />
                      <p class="mb-0">No recent updates</p>
                    </div>
                  </div>
                </b-card>
              </b-col>

              <b-col cols="12" lg="7">
                <b-card class="analysis-card h-100">
                  <b-card-header class="pb-1">
                    <h5 class="mb-0">
                      <feather-icon icon="BarChart2Icon" size="18" class="mr-2" />
                      Offer Process Stage Analysis
                    </h5>
                    <small class="text-muted d-block mt-50 px-0">
                      Bar fill = share of candidates with offer-process data ({{ offerProcessPipelineCandidateTotal }}).
                    </small>
                  </b-card-header>
                  <b-card-body>
                    <b-row class="align-items-start">
                      <b-col cols="12" md="6" class="mb-3 mb-md-0" style="min-width: 0;">
                        <div class="stage-analysis">
                          <div
                            v-for="stage in relianceOfferProcessStages"
                            :key="stage.key"
                            class="stage-analysis-item mb-3"
                          >
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <span class="font-weight-bold">{{ stage.name }}</span>
                              <span class="text-muted text-right ml-2">{{ getStageCount(stage.key) }} candidates</span>
                            </div>
                            <b-progress
                              :value="getStagePercentage(stage.key)"
                              max="100"
                              height="14px"
                              :variant="stage.variant"
                              class="reliance-dashboard__stage-progress"
                            />
                          </div>
                        </div>
                      </b-col>

                      <b-col cols="12" md="6" class="reliance-dashboard__completion-summary-col" style="min-width: 0;">
                        <div class="completion-summary">
                          <div class="summary-item d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                              <div class="avatar avatar-sm bg-light-success mr-2">
                                <feather-icon icon="CheckCircleIcon" size="18" class="text-success" />
                              </div>
                              <span>Fully Completed</span>
                            </div>
                            <h4 class="mb-0 font-weight-bolder text-success">{{ getCompletedCount() }}</h4>
                          </div>
                          <div class="summary-item d-flex justify-content-between align-items-center mb-3">
                            <div class="d-flex align-items-center">
                              <div class="avatar avatar-sm bg-light-warning mr-2">
                                <feather-icon icon="ClockIcon" size="18" class="text-warning" />
                              </div>
                              <span>In Progress</span>
                            </div>
                            <h4 class="mb-0 font-weight-bolder text-warning">{{ getInProgressCount() }}</h4>
                          </div>
                          <div class="summary-item d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center">
                              <div class="avatar avatar-sm bg-light-secondary mr-2">
                                <feather-icon icon="MinusCircleIcon" size="18" class="text-secondary" />
                              </div>
                              <span>Not Started</span>
                            </div>
                            <h4 class="mb-0 font-weight-bolder text-secondary">{{ getNotStartedCount() }}</h4>
                          </div>
                        </div>
                      </b-col>
                    </b-row>
                  </b-card-body>
                </b-card>
              </b-col>
            </b-row>

            <b-card title="Reliance RPO Candidates" class="candidates-card">
              <div
                class="d-flex justify-content-between align-items-center mb-2 py-1 reliance-dashboard__filter-toggle border-bottom d-print-none"
                role="button"
                tabindex="0"
                :aria-expanded="candidateTableFiltersOpen ? 'true' : 'false'"
                aria-controls="reliance-candidate-table-filters-collapse"
                @click="candidateTableFiltersOpen = !candidateTableFiltersOpen"
                @keydown.enter.prevent="candidateTableFiltersOpen = !candidateTableFiltersOpen"
                @keydown.space.prevent="candidateTableFiltersOpen = !candidateTableFiltersOpen"
              >
                <span class="small font-weight-bold text-secondary mb-0">Table filters</span>
                <feather-icon
                  :icon="candidateTableFiltersOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                  size="18"
                  class="text-muted"
                />
              </div>
              <b-collapse id="reliance-candidate-table-filters-collapse" v-model="candidateTableFiltersOpen" class="d-print-none">
                <div class="table-controls mb-3">
                  <div class="d-flex justify-content-between align-items-center flex-wrap">
                    <div class="d-flex align-items-center mb-1">
                      <span class="mr-2">Entries:</span>
                      <b-form-select
                        v-model="candidatesPerPage"
                        :options="[25, 50, 75, 100]"
                        size="sm"
                        style="width: 80px;"
                        @change="onCandidatesPageSizeChange"
                      />
                    </div>
                    <div class="d-flex align-items-center flex-wrap">
                      <div class="search-container mr-2 mb-1">
                        <div class="input-group">
                          <b-form-input
                            v-model="candidateFilters.search"
                            placeholder="Search candidates (min 4 chars)..."
                            size="sm"
                            style="width: 200px;"
                            :class="{ 'is-invalid': candidateFilters.search.length > 0 && candidateFilters.search.length < 4 }"
                            @keyup.enter="onCandidateSearchInput"
                          />
                          <div class="input-group-append">
                            <b-button
                              v-if="candidateFilters.search.length >= 4"
                              size="sm"
                              variant="outline-danger"
                              @click="clearCandidateSearch"
                              title="Clear search"
                            >
                              <feather-icon icon="XIcon" size="16" />
                            </b-button>
                            <b-button
                              size="sm"
                              variant="outline-primary"
                              @click="onCandidateSearchInput"
                              :disabled="candidateFilters.search.length > 0 && candidateFilters.search.length < 4"
                              title="Search"
                            >
                              <feather-icon icon="SearchIcon" size="16" />
                            </b-button>
                          </div>
                        </div>
                      </div>

                      <v-select
                        v-model="candidateFilters.status"
                        :options="candidateStatusOptions"
                        multiple
                        clearable
                        label="text"
                        :reduce="option => option.value"
                        :close-on-select="false"
                        class="mr-2 mb-1"
                        style="min-width: 200px;"
                        @input="loadCandidates"
                        placeholder="Filter by status"
                      />

                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        @click="clearCandidateFilters"
                        title="Clear all filters"
                        class="mb-1"
                      >
                        <feather-icon icon="XIcon" size="16" />
                      </b-button>
                    </div>
                  </div>
                </div>
              </b-collapse>

              <div class="table-header-info mb-2">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="selection-info">
                    <feather-icon icon="ListIcon" size="16" class="text-primary mr-2" />
                    <span class="text-muted">3-stage offer process</span>
                  </div>
                  <div class="table-stats">
                    <small class="text-muted">Showing {{ candidates.length }} of {{ candidatePagination.totalRecords }} candidates</small>
                  </div>
                </div>
              </div>
              <div class="table-responsive">
                <b-table
                  ref="candidatesTable"
                  :items="printReportTableItems(candidates)"
                  :fields="candidateTableFields"
                  primary-key="_rowKey"
                  :busy="candidatesLoading"
                  hover
                  responsive
                  class="candidates-table"
                  show-empty
                  :empty-text="printReportTableEmptyText(candidates, 'No candidates found')"
                  :empty-filtered-text="printReportTableEmptyText(candidates, 'No candidates match your filters')"
                >
                  <template #table-busy>
                    <div class="text-center my-2">
                      <b-spinner class="align-middle"></b-spinner>
                      <strong class="ml-2">Loading...</strong>
                    </div>
                  </template>

                  <template #cell(candidate_name)="data">
                    <a
                      v-if="$can('read','candidates')"
                      href="#"
                      class="text-body"
                      @click.prevent="navigateToCandidateView(data.item)"
                    >
                      <div class="candidate-info">
                        <div>
                          <div class="font-weight-bold candidate-name" v-b-tooltip.hover :title="getCandidateName(data.item)">
                            {{ getCandidateName(data.item) }}
                          </div>
                          <small class="text-muted">{{ data.item.email }}</small>
                        </div>
                      </div>
                    </a>
                    <div v-else class="candidate-info">
                      <div>
                        <div class="font-weight-bold candidate-name" v-b-tooltip.hover :title="getCandidateName(data.item)">
                          {{ getCandidateName(data.item) }}
                        </div>
                        <small class="text-muted">{{ data.item.email }}</small>
                      </div>
                    </div>
                  </template>

                  <template #cell(candidateStatus)="data">
                    <div class="status-indicator">
                      <b-badge :variant="getStatusVariant(data.item.candidateStatus)">
                        {{ data.item.candidateStatus }}
                      </b-badge>
                    </div>
                  </template>

                  <template #cell(position_name)="data">
                    <a
                      href="#"
                      class="text-body"
                      @click.prevent="navigateToJobView(data.item.jid)"
                    >
                      <div class="position-info" v-b-tooltip.hover :title="getPositionName(data.item)">
                        <div class="position-name">{{ getPositionName(data.item) }}</div>
                        <small class="text-muted">{{ data.item.client }}</small>
                      </div>
                    </a>
                  </template>

                  <template #cell(offerProcessStage)="data">
                    <div class="offer-process-stage-info">
                      <div class="d-flex flex-column">
                        <!-- Documentation -->
                        <div class="stage-item mb-1">
                          <span class="stage-label">Doc:</span>
                          <b-badge 
                            :variant="getOfferStageVariant(getOfferProcessStage(data.item, 'documentation'))"
                            size="sm"
                          >
                            {{ getOfferProcessStage(data.item, 'documentation') }}
                          </b-badge>
                        </div>
                        <!-- Confirmation -->
                        <div class="stage-item mb-1">
                          <span class="stage-label">Conf:</span>
                          <b-badge 
                            :variant="getOfferStageVariant(getOfferProcessStage(data.item, 'confirmation'))"
                            size="sm"
                          >
                            {{ getOfferProcessStage(data.item, 'confirmation') }}
                          </b-badge>
                        </div>
                        <!-- Medical -->
                        <div class="stage-item">
                          <span class="stage-label">Med:</span>
                          <b-badge 
                            :variant="getOfferStageVariant(getOfferProcessStage(data.item, 'medical'))"
                            size="sm"
                          >
                            {{ getOfferProcessStage(data.item, 'medical') }}
                          </b-badge>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template #cell(offerProcessProgress)="data">
                    <div class="offer-process-progress">
                      <b-progress 
                        :value="getOfferProcessProgress(data.item)" 
                        max="100" 
                        height="8px"
                        :variant="getProgressVariant(getOfferProcessProgress(data.item))"
                      />
                      <small class="text-muted mt-1">{{ getOfferProcessProgress(data.item) }}% Complete</small>
                    </div>
                  </template>

                  <template #cell(updatedAt)="data">
                    <small class="text-muted">{{ formatDate(data.item.updated_at || data.item.updatedAt) }}</small>
                  </template>

                  <template #cell(actions)="data">
                    <div class="action-buttons">
                      <b-button
                        v-if="$can('read','candidates')"
                        size="sm"
                        variant="outline-primary"
                        @click="navigateToCandidateView(data.item)"
                      >
                        <feather-icon icon="EyeIcon" size="14" />
                      </b-button>
                    </div>
                  </template>
                </b-table>
              </div>

              <!-- Pagination (summary prints; controls hidden on paper) -->
              <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap">
                <div class="mb-1">
                  <span v-if="candidatePagination.totalRecords > 0">
                    Showing {{ (candidatePagination.currentPage - 1) * candidatePagination.limit + 1 }} to {{ Math.min(candidatePagination.currentPage * candidatePagination.limit, candidatePagination.totalRecords) }} of {{ candidatePagination.totalRecords }} candidates
                  </span>
                  <span v-else>No candidates found</span>
                </div>
                <div class="d-flex align-items-center d-print-none">
                  <span class="mr-3 text-muted">
                    Page {{ candidatePagination.currentPage }} of {{ candidatePagination.totalPages }}
                  </span>
                  <b-pagination
                    v-model="candidatesCurrentPage"
                    :total-rows="candidatePagination.totalRecords"
                    :per-page="candidatePagination.limit"
                    @change="onCandidatesPageChange"
                    align="right"
                    :limit="7"
                    first-text="«"
                    last-text="»"
                    next-text="›"
                    prev-text="‹"
                  />
                </div>
              </div>
            </b-card>

            <!-- Download Report Button -->
            <b-row class="d-print-none">
              <b-col cols="12">
                <div class="d-flex justify-content-end">
                  <b-button 
                    variant="success" 
                    size="sm"
                    @click="downloadCandidateReport"
                    :disabled="candidatesLoading"
                  >
                    <feather-icon icon="DownloadIcon" size="16" class="mr-2" />
                    Download Excel Report
                  </b-button>
                </div>
                </b-col>
              </b-row>
            </div>
          </b-tab>

          <!-- Cost Module Tab -->
          <b-tab title="Cost Module">
            <cost-module-tab
              :client-name="clientName"
              :filters="globalFilters"
              :loading="loading"
            />
          </b-tab>
        </b-tabs>
      </b-card>
    </div>
  </template>
  
  <script>
  import {
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BCardText,
    BButton,
    BBadge,
    BTabs,
    BTab,
    BTable,
    BCollapse,
    BSpinner,
    BProgress,
    BAlert,
    BPagination,
    BFormSelect,
    BFormInput,
    BFormGroup,
    BMedia,
    BMediaAside,
    BMediaBody,
    BAvatar,
    BListGroup,
    BListGroupItem,
  } from 'bootstrap-vue'
import { VBTooltip } from 'bootstrap-vue'
import vSelect from 'vue-select'
import RelianceService from '@/services/reliance-service'
import candidateService from '@/services/candidate-service'
import * as echarts from 'echarts'
import * as XLSX from 'xlsx'
import Excel from 'exceljs'
import { saveAs } from 'file-saver'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import CostModuleTab from './CostModuleTab.vue'

const RELIANCE_DASHBOARD_TAB_SLUGS = [
  'detailed-overview',
  'overall',
  'selections',
  'timelines',
  'diversity',
  'candidate-data',
  'cost-module',
]
const RELIANCE_DASHBOARD_TAB_TITLES = [
  'Detailed Overview',
  'Overall',
  'Selections',
  'Timelines',
  'Diversity',
  'Candidate Data',
  'Cost Module',
]
/** Pre–tab-reorder slug order (for localStorage values saved before reorder). */
const LEGACY_RELIANCE_TAB_SLUGS = [
  'overall',
  'selections',
  'timelines',
  'diversity',
  'candidate-data',
  'detailed-overview',
]
const RELIANCE_DASHBOARD_UI_TAB_KEY = 'reliance-dashboard-tab'
const RELIANCE_DASHBOARD_UI_TAB_SESSION_KEY = 'reliance-dashboard-tab-session'
const RELIANCE_DASHBOARD_UI_SCROLL_KEY = 'reliance-dashboard-scroll-y'

function relianceTabIndexFromSlug(slug) {
  if (slug == null || slug === '') return -1
  const normalized = String(slug).toLowerCase().trim().replace(/\s+/g, '-')
  let idx = RELIANCE_DASHBOARD_TAB_SLUGS.indexOf(normalized)
  if (idx >= 0) return idx
  idx = RELIANCE_DASHBOARD_TAB_TITLES.findIndex(
    t => t.toLowerCase().replace(/\s+/g, '-') === normalized,
  )
  if (idx >= 0) return idx
  const legacyIdx = LEGACY_RELIANCE_TAB_SLUGS.indexOf(normalized)
  if (legacyIdx >= 0) {
    return RELIANCE_DASHBOARD_TAB_SLUGS.indexOf(LEGACY_RELIANCE_TAB_SLUGS[legacyIdx])
  }
  const legacyNum = parseInt(normalized, 10)
  if (
    Number.isFinite(legacyNum)
    && legacyNum >= 0
    && legacyNum < LEGACY_RELIANCE_TAB_SLUGS.length
  ) {
    return RELIANCE_DASHBOARD_TAB_SLUGS.indexOf(LEGACY_RELIANCE_TAB_SLUGS[legacyNum])
  }
  return -1
}

function getTabQueryFromWindowHash() {
  try {
    const hash = window.location.hash || ''
    const qIndex = hash.indexOf('?')
    if (qIndex < 0) return null
    const params = new URLSearchParams(hash.slice(qIndex + 1))
    return params.get('tab')
  } catch (e) {
    return null
  }
}

/** Resolve tab before first render (reload / direct URL). */
function getInitialRelianceTabIndex(route) {
  const routeTab = route && route.query ? route.query.tab : null
  if (routeTab) {
    const idx = relianceTabIndexFromSlug(routeTab)
    if (idx >= 0) return idx
  }
  const hashTab = getTabQueryFromWindowHash()
  if (hashTab) {
    const idx = relianceTabIndexFromSlug(hashTab)
    if (idx >= 0) return idx
  }
  try {
    const sessionTab = sessionStorage.getItem(RELIANCE_DASHBOARD_UI_TAB_SESSION_KEY)
    if (sessionTab) {
      const idx = relianceTabIndexFromSlug(sessionTab)
      if (idx >= 0) return idx
    }
    const stored = localStorage.getItem(RELIANCE_DASHBOARD_UI_TAB_KEY)
    if (stored) {
      const idx = relianceTabIndexFromSlug(stored)
      if (idx >= 0) return idx
    }
  } catch (e) { /* noop */ }
  return 0
}
  
  export default {
    name: 'RelianceDashboard',
    components: {
      BRow,
      BCol,
      BCard,
      BCardHeader,
      BCardBody,
      BCardText,
      BButton,
      BBadge,
      BTabs,
      BTab,
      BTable,
      BCollapse,
      BSpinner,
      BProgress,
      BAlert,
      BPagination,
      BFormSelect,
      BFormInput,
      BFormGroup,
      BMedia,
      BMediaAside,
      BMediaBody,
      BAvatar,
      BListGroup,
      BListGroupItem,
      vSelect,
      AppTimeline,
      AppTimelineItem,
      CostModuleTab,
    },
  directives: {
    'b-tooltip': VBTooltip,
    },
    data() {
      return {
        loading: false,
        /** Plan B: print / Save as PDF — prefetch all tabs, then browser print */
        generatingPrintReport: false,
        /** Max rows per b-table when printing (Save as PDF / print dialog) */
        printReportTableRowCap: 15,
        preparePrintLayout: false,
        savedTabForPrint: 0,
        activeTabIndex: getInitialRelianceTabIndex(),
        /** Skip persisting tab while restoring route/localStorage state on load */
        suppressTabPersistence: false,
        scrollRestoreScheduled: false,
        selectedKPI: null,
        showExpandedData: true,
        globalFiltersOpen: true,
        filterOptionsLoading: false,
        filterOptionsFromApi: {
          functions: [],
          positions: [],
          driveNames: [],
        },
        candidateTableFiltersOpen: false,

        isAdmin: false,
        clientName: '',

        kpiData: {
          open_positions: 0,
          profile_shared: 0,
          screen_select: 0,
          interview: 0,
          selections: 0,
          documentation: 0,
          medical: 0,
          offered: 0,
          closed_positions: 0,
        },

        positionData: [],

        globalFilters: {
          positionType: '',
          functionName: '',
          positionName: '',
          driveName: '',
          positionSubmissionStartDate: '',
          positionSubmissionEndDate: '',
        },
        globalFiltersPending: {
          positionType: '',
          functionName: '',
          positionName: '',
          driveName: '',
          positionSubmissionStartDate: '',
          positionSubmissionEndDate: '',
        },

        selectionData: [],

        tatData: [],
        tatSummary: [],

        selectionFunnelTotals: {
          total_selection: 0,
          total_documentation: 0,
          total_confirmation: 0,
          total_medical: 0,
          total_offered: 0,
          total_joined: 0
        },
        positionSelectionFunnel: [],

        rejectionRatios: [],

        detailedKpiData: null,
        detailedPositionSummary: [],

        positionFields: [
          { key: 'position_name', label: 'Position Name', sortable: true },
          { key: 'function', label: 'Function', sortable: true },
          { key: 'open_positions', label: 'Total Positions', sortable: true },
          { key: 'profile_shared', label: 'ProfileShared', sortable: true },
          { key: 'screen_select', label: 'Screened', sortable: true },
          { key: 'interview', label: 'Interview', sortable: true },
          { key: 'selections', label: 'Selected', sortable: true },
          { key: 'documentation', label: 'Docs', sortable: true },
          { key: 'medical', label: 'Medical', sortable: true },
          { key: 'offered', label: 'Offered', sortable: true },
          { key: 'closed_positions', label: 'Joined', sortable: true },
        ],

        selectionFields: [
          { key: 'position_name', label: 'Position Name', sortable: true },
          { key: 'total_selection', label: 'Selections', sortable: true },
          { key: 'total_documentation', label: 'Documentation', sortable: true },
          { key: 'total_offered', label: 'Offered', sortable: true },
          { key: 'total_joined', label: 'Joined', sortable: true },
          { key: 'conversion_rate', label: 'Conversion Rate', sortable: true },
        ],
  
        tatFields: [
          { key: 'stage_transition', label: 'Stage Transition', sortable: false },
          { key: 'avg_days', label: 'Average', sortable: true },
          { key: 'min_days', label: 'Minimum', sortable: true },
          { key: 'max_days', label: 'Maximum', sortable: true },
          { key: 'trend', label: 'Trend', sortable: true },
        ],

        rejectionFields: [
          { key: 'stage_transition', label: 'Stage Transition', sortable: false },
          { key: 'rejection_count', label: 'Rejections', sortable: true },
          { key: 'total_at_stage', label: 'Total at Stage', sortable: true },
          { key: 'conversion_rate', label: 'Rejection Rate %', sortable: true },
        ],

        funnelChart: null,
        tatChart: null,
        diversityGenderPieChart: null,
        diversitySelectionOfferStageBarChart: null,
        diversityPipelineBarChart: null,
        diversityPipelinePieChart: null,
        candidateOfferSelectionBarChart: null,
        candidateSelectionWeekBarChart: null,
        candidateJoinedWeekBarChart: null,
        dropReasonPieChart: null,

        analyticsCandidatePool: [],
        analyticsCandidatesLoading: false,

        rejectionFunnelChart: null,
        candidatesLoading: false,
        candidates: [],
        candidatesCurrentPage: 1,
        candidatesPerPage: 100,

        candidateSummary: {
          total: 0,
          selected: 0,
          offered: 0,
          joined: 0,
        },

        candidatePagination: {
          currentPage: 1,
          totalPages: 1,
          totalRecords: 0,
          limit: 25,
        },

        candidateFilters: {
          search: '',
          status: [],
        },

        candidateStatusOptions: [
          { value: 'All', text: 'All' },
          { value: 'ProfileShared', text: 'Profile Shared' },
          { value: 'ScreenSelect', text: 'Screen Select' },
          { value: 'ScreenReject', text: 'Screen Reject' },
          { value: 'Duplicate', text: 'Duplicate' },
          { value: 'Drop', text: 'Drop' },
          { value: 'Rejected', text: 'Rejected' },
          { value: 'Interview', text: 'Interview' },
          { value: 'No Show', text: 'No Show' },
          { value: 'Selected', text: 'Selected' },
          { value: 'Shortlisted', text: 'Shortlisted' },
          { value: 'Draft Offer', text: 'Draft Offer' },
          { value: 'Offered', text: 'Offered' },
          { value: 'Offer Accepted', text: 'Offer Accepted' },
          { value: 'Joined', text: 'Joined' },
          { value: 'NotOffered', text: 'Not Offered' },  
          { value: 'OfferDrop', text: 'Offer Drop' },
          { value: 'Early Attrition', text: 'Early Attrition' },
          { value: 'Attrition', text: 'Attrition' }
        ],

        candidateTableFields: [
          { key: 'candidate_name', label: 'Candidate', sortable: true, thStyle: { width: '180px' } },
          { key: 'candidateStatus', label: 'Status', sortable: true, thStyle: { width: '120px' } },
          { key: 'position_name', label: 'Position', sortable: true, thStyle: { width: '200px' } },
          { key: 'offerProcessStage', label: 'Offer Process', sortable: false, thStyle: { width: '180px' } },
          { key: 'offerProcessProgress', label: 'Progress', sortable: false, thStyle: { width: '120px' } },
          { key: 'updatedAt', label: 'Last Updated', sortable: true, thStyle: { width: '120px' } },
          { key: 'actions', label: 'Actions', thStyle: { width: '80px' } },
        ],

        weekwiseStatusFields: [
          { key: 'weekLabel', label: 'Week', sortable: false },
          { key: 'profileShared', label: 'Profile Shared', sortable: true },
          { key: 'screenSelect', label: 'Screen Select', sortable: true },
          { key: 'interview', label: 'Interview', sortable: true },
          { key: 'noShow', label: 'No Show', sortable: true },
          { key: 'selected', label: 'Selected', sortable: true },
          { key: 'shortlisted', label: 'Shortlisted', sortable: true },
          { key: 'offered', label: 'Offered', sortable: true },
          { key: 'joined', label: 'Joined', sortable: true },
          { key: 'screenReject', label: 'Screen Reject', sortable: true },
          { key: 'drop', label: 'Drop', sortable: true },
          { key: 'rejected', label: 'Rejected', sortable: true },
          { key: 'total', label: 'Total Activity', sortable: true },
        ],

        diversitySummaryFields: [
          { key: 'label', label: 'Metric' },
          { key: 'value', label: 'Count' },
        ],

        relianceOfferProcessStages: [
          { key: 'documentation', name: 'Documentation', completedStatus: 'Completed', variant: 'secondary' },
          { key: 'confirmation', name: 'Confirmation', completedStatus: 'Confirmed', variant: 'info' },
          { key: 'medical', name: 'Medical', completedStatus: 'Fit', variant: 'success' },
        ],

        stageCounts: {
          documentation: { pending: 0, completed: 0 },
          confirmation: { pending: 0, confirmed: 0, notConfirmed: 0 },
          medical: { pending: 0, fit: 0, unfit: 0 },
        },

        latestUpdates: [],
      }
    },
    computed: {
      activeTabSlug() {
        return RELIANCE_DASHBOARD_TAB_SLUGS[this.activeTabIndex] || 'detailed-overview'
      },
      activeTab() {
        return RELIANCE_DASHBOARD_TAB_TITLES[this.activeTabIndex] || 'Detailed Overview'
      },
      /** Tab labels for print cover (PDF page 1) — order matches dashboard tabs */
      printReportTabLabels() {
        return [...RELIANCE_DASHBOARD_TAB_TITLES]
      },
      /** Human-readable timestamp for the print cover sheet. */
      printReportGeneratedAt() {
        try {
          return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'long',
            timeStyle: 'short',
          }).format(new Date())
        } catch (e) {
          return new Date().toLocaleString()
        }
      },
      positionTypeSelectOptions() {
        return [
          { value: '', text: 'All types' },
          { value: 'FTE', text: 'FTE' },
          { value: 'LATERAL', text: 'Lateral' },
        ]
      },
      globalFiltersActiveCount() {
        const f = this.globalFilters
        let n = 0
        if (f.positionType) n += 1
        if (f.functionName) n += 1
        if (f.positionName) n += 1
        if (f.driveName) n += 1
        if (f.positionSubmissionStartDate) n += 1
        if (f.positionSubmissionEndDate) n += 1
        return n
      },
      hasActiveGlobalFilters() {
        return this.globalFiltersActiveCount > 0
      },
      hasApiJobFilters() {
        const f = this.globalFilters
        return !!(
          f.positionType
          || f.functionName
          || f.driveName
          || f.positionSubmissionStartDate
        )
      },
      kpiCards() {
        const wow = this.kpiData.wowComparison || {}
        return [
          {
            key: 'open_positions',
            label: 'Total Positions',
            value: this.kpiData.open_positions,
            icon: 'BriefcaseIcon',
            color: 'info',
            wow: wow.open_positions || null
          },
          {
            key: 'profile_shared',
            label: 'Profile Shared',
            value: this.kpiData.profile_shared,
            icon: 'UsersIcon',
            color: 'primary',
            wow: wow.profile_shared || null
          },
          {
            key: 'screen_select',
            label: 'Screen Select',
            value: this.kpiData.screen_select,
            icon: 'CheckSquareIcon',
            color: 'success',
            wow: wow.screen_select || null
          },
          {
            key: 'interview',
            label: 'Interview',
            value: this.kpiData.interview,
            icon: 'VideoIcon',
            color: 'warning',
            wow: wow.interview || null
          },
          {
            key: 'selections',
            label: 'Selections',
            value: this.kpiData.selections,
            icon: 'UserCheckIcon',
            color: 'success',
            wow: wow.selections || null
          },
          {
            key: 'offered',
            label: 'Offered',
            value: this.kpiData.offered,
            icon: 'AwardIcon',
            color: 'primary',
            wow: wow.offered || null
          },
          {
            key: 'closed_positions',
            label: 'Joined',
            value: this.kpiData.closed_positions,
            icon: 'CheckCircleIcon',
            color: 'dark',
            wow: wow.closed_positions || null
          },
        ]
      },
      selectedKPILabel() {
        const kpi = this.kpiCards.find(k => k.key === this.selectedKPI)
        return kpi ? kpi.label : ''
      },
      selectionFunnelTotalsForCharts() {
        const base = this.selectionFunnelTotals || {}
        if (!this.globalFilters.positionName) {
          return {
            total_selection: base.total_selection || 0,
            total_documentation: base.total_documentation || 0,
            total_confirmation: base.total_confirmation || 0,
            total_medical: base.total_medical || 0,
            total_offered: base.total_offered || 0,
            total_joined: base.total_joined || 0,
          }
        }
        const rows = this.filteredPositionSelectionFunnel || []
        if (!rows.length) {
          return {
            total_selection: 0,
            total_documentation: 0,
            total_confirmation: 0,
            total_medical: 0,
            total_offered: 0,
            total_joined: 0,
          }
        }
        return rows.reduce((a, r) => ({
          total_selection: a.total_selection + (Number(r.total_selection) || 0),
          total_documentation: a.total_documentation + (Number(r.total_documentation) || 0),
          total_confirmation: a.total_confirmation + (Number(r.total_confirmation) || 0),
          total_medical: a.total_medical + (Number(r.total_medical) || 0),
          total_offered: a.total_offered + (Number(r.total_offered) || 0),
          total_joined: a.total_joined + (Number(r.total_joined) || 0),
        }), {
          total_selection: 0,
          total_documentation: 0,
          total_confirmation: 0,
          total_medical: 0,
          total_offered: 0,
          total_joined: 0,
        })
      },
      conversionRatios() {
        const totals = this.selectionFunnelTotalsForCharts
        const totalSelection = totals.total_selection || 1
        const totalDocumentation = totals.total_documentation || 1
        const totalOffered = totals.total_offered || 1
        
        return [
          {
            stage: 'Selection → Documentation',
            percentage: Math.round((totals.total_documentation / totalSelection) * 100) || 0,
            count: totals.total_documentation || 0,
            total: totals.total_selection || 0,
            icon: 'FileTextIcon',
            color: 'secondary'
          },
          {
            stage: 'Documentation → Offered',
            percentage: Math.round((totals.total_offered / totalDocumentation) * 100) || 0,
            count: totals.total_offered || 0,
            total: totals.total_documentation || 0,
            icon: 'AwardIcon',
            color: 'primary'
          },
          {
            stage: 'Offered → Joined',
            percentage: Math.round((totals.total_joined / totalOffered) * 100) || 0,
            count: totals.total_joined || 0,
            total: totals.total_offered || 0,
            icon: 'UserCheckIcon',
            color: 'success'
          },
        ]
      },
      /** Candidates scoped by dashboard filters — all tabs that use the analytics pool. */
      analyticsCandidates() {
        return this.applyDashboardCandidateScope(this.globalFilters)
      },

      /** Diversity tab charts and summary — same dashboard filters as other tabs. */
      diversityAnalyticsCandidates() {
        return this.applyDashboardCandidateScope(this.globalFilters)
      },
      /** Same candidate list as `applyDashboardCandidateScope` — full analytics pool, not paginated table rows. */
      analyticsCandidatePoolForFilterOptions() {
        if (this.analyticsCandidatePool && this.analyticsCandidatePool.length > 0) {
          return this.analyticsCandidatePool
        }
        return this.candidates || []
      },
      /** `position_name` → function from dashboard positions (fallback when candidate has no function field). */
      positionFunctionByPositionName() {
        const m = {}
        ;(this.positionData || []).forEach((p) => {
          const name = (p.position_name || '').toString().trim()
          if (!name) return
          const fn = this.normalizeFunctionLabel(p.function)
          if (fn && fn !== 'N/A') m[name] = fn
        })
        return m
      },
      /** Candidate Data tab KPI strip — dashboard filters. */
      candidateDataTabKpiSummary() {
        const list = this.diversityAnalyticsCandidates || []
        let selected = 0
        let offered = 0
        let joined = 0
        list.forEach(c => {
          const s = c.candidateStatus
          if (s === 'Selected' || s === 'Shortlisted') selected += 1
          if (s === 'Offered' || s === 'Offer Accepted') offered += 1
          if (s === 'Joined') joined += 1
        })
        return { total: list.length, selected, offered, joined }
      },
      /** Candidates with parseable offer-process JSON (denominator for stage progress bars). */
      offerProcessPipelineCandidateTotal() {
        let n = 0
        this.candidatesForOfferProcessMetrics().forEach(c => {
          if (this.parseOfferProcess(c.offerProcess)) n += 1
        })
        return n
      },
      /** True when TAT API rows include a position field so main filters can narrow timelines. */
      tatDataHasPositionScope() {
        const rows = this.tatData || []
        return rows.some(r => (r.position_name || r.positionName || '').toString().trim() !== '')
      },
      displayTatData() {
        const rows = this.tatData || []
        if (!this.globalFilters.positionName || !this.tatDataHasPositionScope) return rows
        const f = this.globalFilters
        return rows.filter(r => {
          const pname = (r.position_name || r.positionName || '').toString().trim()
          return this.positionsNameEquivalent(pname, f.positionName)
        })
      },
      displayTatSummary() {
        const base = this.tatSummary || []
        if (!this.globalFilters.positionName || !this.tatDataHasPositionScope) return base
        const data = this.displayTatData || []
        const metaBy = {}
        base.forEach(t => {
          if (t && t.stage) metaBy[this.normalizeTatStageKey(t.stage)] = t
        })
        return data.map(item => {
          const key = this.normalizeTatStageKey(item.stage_transition)
          const m = metaBy[key] || {}
          return {
            stage: item.stage_transition,
            avgDays: item.avg_days,
            icon: m.icon || 'ClockIcon',
            color: m.color || 'primary',
          }
        })
      },
      /** Gender columns with Shared ahead / ScreenReject / ScreenSelect / Selected (current status). */
      diversityGenderPipelineColumns() {
        const list = this.diversityAnalyticsCandidates || []
        const genders = new Map()
        const ensure = (g) => {
          if (!genders.has(g)) {
            genders.set(g, { gender: g, shared: 0, screenReject: 0, screenSelect: 0, selected: 0, all: 0 })
          }
          return genders.get(g)
        }
        list.forEach(c => {
          const g = this.candidateGenderForDiversity(c)
          const b = ensure(g)
          b.all += 1
          const s = c.candidateStatus
          if (s === 'Profile Shared' || s === 'ProfileShared') b.shared += 1
          else if (s === 'Screen Reject' || s === 'ScreenReject') b.screenReject += 1
          else if (s === 'Screen Select' || s === 'ScreenSelect') b.screenSelect += 1
          else if (s === 'Selected' || s === 'Shortlisted') b.selected += 1
        })
        const orderRank = (name) => {
          if (name === 'Male') return 0
          if (name === 'Female') return 1
          if (name === 'Unspecified') return 99
          return 50
        }
        const keys = [...genders.keys()].sort((a, b) => {
          const ra = orderRank(a)
          const rb = orderRank(b)
          if (ra !== rb) return ra - rb
          return a.localeCompare(b)
        })
        const meta = (name) => {
          if (name === 'Male') return { icon: 'UserIcon', avatarVariant: 'light-primary' }
          if (name === 'Female') return { icon: 'UserIcon', avatarVariant: 'light-danger' }
          return { icon: 'UsersIcon', avatarVariant: 'light-secondary' }
        }
        return keys.map((name) => {
          const b = genders.get(name)
          const m = meta(name)
          const total = b.all
          const pct = v => (total > 0 ? Math.round((v / total) * 100) : 0)
          return {
            gender: name,
            total,
            icon: m.icon,
            avatarVariant: m.avatarVariant,
            items: [
              { label: 'Shared ahead', value: b.shared, pct: pct(b.shared), badgeVariant: 'light-info' },
              { label: 'ScreenReject', value: b.screenReject, pct: pct(b.screenReject), badgeVariant: 'light-danger' },
              { label: 'ScreenSelect', value: b.screenSelect, pct: pct(b.screenSelect), badgeVariant: 'light-warning' },
              { label: 'Selected', value: b.selected, pct: pct(b.selected), badgeVariant: 'light-success' },
            ],
          }
        })
      },
      globalPositionSelectOptions() {
        const opts = [{ value: '', text: 'All positions' }]
        const seen = new Set([''])
        const apiList = this.filterOptionsFromApi.positions || []
        apiList.forEach(item => {
          const v = typeof item === 'string' ? item : (item.position_name || item.name || item.value || '')
          const text = typeof item === 'string' ? item : (item.label || item.text || v)
          if (v && !seen.has(v)) {
            seen.add(v)
            opts.push({ value: v, text })
          }
        })
        if (apiList.length === 0) {
          const add = (rawName) => {
            const v = (rawName || '').toString().trim().replace(/\s+/g, ' ')
            if (!v || seen.has(v)) return
            seen.add(v)
            opts.push({ value: v, text: this.getPositionName({ position_name: v }) || v })
          }
          ;(this.positionData || []).forEach(p => add(p.position_name))
        }
        return opts.sort((a, b) => (a.text || '').localeCompare(b.text || ''))
      },
      globalFunctionSelectOptions() {
        const opts = [{ value: '', text: 'All functions' }]
        const seen = new Set([''])
        const apiList = this.filterOptionsFromApi.functions || []
        apiList.forEach(item => {
          const v = typeof item === 'string' ? item : (item.function || item.name || item.value || '')
          if (v && !seen.has(v)) {
            seen.add(v)
            opts.push({ value: v, text: v })
          }
        })
        if (apiList.length === 0) {
          ;(this.positionData || []).forEach(p => {
            const v = this.normalizeFunctionLabel(p.function)
            if (!seen.has(v)) {
              seen.add(v)
              opts.push({ value: v, text: v })
            }
          })
        }
        return opts.sort((a, b) => a.text.localeCompare(b.text))
      },
      globalDriveSelectOptions() {
        const opts = [{ value: '', text: 'All drives' }]
        const seen = new Set([''])
        const apiList = this.filterOptionsFromApi.driveNames || []
        apiList.forEach(item => {
          const v = typeof item === 'string' ? item : (item.drive_name || item.driveName || item.name || item.value || '')
          if (v && !seen.has(v)) {
            seen.add(v)
            opts.push({ value: v, text: v })
          }
        })
        if (apiList.length === 0) {
          ;(this.positionData || []).forEach(p => {
            const v = this.normalizeDriveLabel(p)
            if (v && !seen.has(v)) {
              seen.add(v)
              opts.push({ value: v, text: v })
            }
          })
        }
        return opts.sort((a, b) => a.text.localeCompare(b.text))
      },
      diversitySummaryRows() {
        const list = this.diversityAnalyticsCandidates
        const rows = [{ label: 'Candidates (after filters)', value: list.length }]
        const g = {}
        list.forEach(c => {
          const key = this.candidateGenderForDiversity(c)
          g[key] = (g[key] || 0) + 1
        })
        Object.keys(g).sort().forEach(k => {
          rows.push({ label: `Gender — ${k}`, value: g[k] })
        })
        const dclass = {}
        list.forEach(c => {
          const key = this.candidateDiversityClassificationLabel(c)
          dclass[key] = (dclass[key] || 0) + 1
        })
        Object.keys(dclass).sort().forEach(k => {
          rows.push({ label: `Diversity classification — ${k}`, value: dclass[k] })
        })
        let pipelineShortlisted = 0
        let pipelineOffered = 0
        let pipelineJoined = 0
        list.forEach(c => {
          const s = (c.candidateStatus || '').toString()
          if (s === 'Selected' || s === 'Shortlisted') pipelineShortlisted += 1
          if (s === 'Offered' || s === 'Offer Accepted') pipelineOffered += 1
          if (s === 'Joined') pipelineJoined += 1
        })
        rows.push({ label: 'Selected / Shortlisted', value: pipelineShortlisted })
        rows.push({ label: 'Offered', value: pipelineOffered })
        rows.push({ label: 'Joined', value: pipelineJoined })
        return rows
      },
      // Weekwise analytics (Candidate Data tab — Diversity filters only)
      upcomingJoiningWeekwise() {
        return this.groupCandidatesByWeek(
          this.diversityAnalyticsCandidates.filter(c => {
            const joiningDate = c.joiningDate || c.joining_date
            return joiningDate
          }),
          'joiningDate',
          'joining_date'
        )
      },
      joinedWeekwise() {
        return this.groupCandidatesByWeek(
          this.diversityAnalyticsCandidates.filter(c => c.candidateStatus === 'Joined'),
          'joiningDate',
          'joining_date'
        )
      },
      selectionsWeekwise() {
        return this.groupCandidatesByWeek(
          this.diversityAnalyticsCandidates.filter(c => {
            const s = c.candidateStatus
            return s === 'Selected' || s === 'Shortlisted'
          }),
          'updatedAt',
          'updated_at'
        )
      },
      offeredWeekwise() {
        return this.groupCandidatesByWeek(
          this.diversityAnalyticsCandidates.filter(c => c.candidateStatus === 'Offered' || c.candidateStatus === 'Offer Accepted'),
          'updatedAt',
          'updated_at'
        )
      },
      filteredPositionData() {
        const rows = this.positionData || []
        if (!this.globalFilters.positionName) return rows
        return rows.filter(p => this.positionsNameEquivalent(p.position_name, this.globalFilters.positionName))
      },
      filteredPositionSelectionFunnel() {
        const rows = this.positionSelectionFunnel || []
        if (!this.globalFilters.positionName) return rows
        return rows.filter(row => this.positionsNameEquivalent(row.position_name, this.globalFilters.positionName))
      },
      filteredDetailedPositionSummaryFlattened() {
        const flat = this.detailedPositionSummaryFlattened || []
        if (!this.globalFilters.positionName) return flat
        return flat.filter(row => this.positionsNameEquivalent(row.position_name, this.globalFilters.positionName))
      },
      // Detailed Overview: API aggregates when job filters applied; client narrow when position selected
      detailedKpiCards() {
        if (this.globalFilters.positionName) {
          return this.buildDetailedKpiCardsFromFlatRows(this.filteredDetailedPositionSummaryFlattened || [])
        }
        if (this.hasApiJobFilters) {
          return this.buildDetailedKpiCardsFromApiData(this.detailedKpiData || {})
        }
        const d = this.detailedKpiData || {}
        const s = d.sourcing || {}
        const sc = d.screening || {}
        const i = d.interview || {}
        const o = d.offer || {}
        const ob = d.onboarding || {}
        return [
          { key: 'open_positions', label: 'Total Positions', value: d.open_positions != null ? d.open_positions : 0, color: 'secondary', icon: 'BriefcaseIcon', sub: null },
          { key: 'sourcing', label: 'Sourcing', value: s.total_profile_shared != null ? s.total_profile_shared : 0, color: 'info', icon: 'UsersIcon', sub: [{ label: 'Feedback Pending', value: s.feedback_pending != null ? s.feedback_pending : 0 }, { label: 'Feedback Received', value: s.feedback_received != null ? s.feedback_received : 0 }] },
          { key: 'screening', label: 'Screening', value: sc.total_screening != null ? sc.total_screening : 0, color: 'warning', icon: 'FilterIcon', sub: [{ label: 'Select', value: sc.screen_select != null ? sc.screen_select : 0 }, { label: 'Reject', value: sc.screen_reject != null ? sc.screen_reject : 0 }, { label: 'On Hold', value: sc.on_hold != null ? sc.on_hold : 0 }] },
          { key: 'interview', label: 'Interview', value: i.total_interview != null ? i.total_interview : 0, color: 'danger', icon: 'MessageSquareIcon', sub: [{ label: 'Scheduled', value: i.interview_current != null ? i.interview_current : 0 }, { label: 'Select', value: i.interview_select != null ? i.interview_select : 0 }, { label: 'Reject', value: i.interview_reject != null ? i.interview_reject : 0 }, { label: 'No Show', value: i.interview_pending != null ? i.interview_pending : 0 }] },
          { key: 'offer', label: 'Offer', value: o.total_selected != null ? o.total_selected : 0, color: 'success', icon: 'AwardIcon', sub: [{ label: 'Doc Pending', value: o.documentation_pending != null ? o.documentation_pending : 0 }, { label: 'Medical Pending', value: o.medical_pending != null ? o.medical_pending : 0 }, { label: 'Confirmation Pending', value: o.confirmation_pending != null ? o.confirmation_pending : 0 },  { label: 'Draft offer', value: o.draft_offer != null ? o.draft_offer : 0 }, { label: 'Not Offered', value: o.client_dropped != null ? o.client_dropped : 0 }, { label: 'Offered', value: ob.total_offered != null ? ob.total_offered : 0 }] },
          { key: 'onboarding', label: 'Onboarding', value: ob.total_offered != null ? ob.total_offered : 0, color: 'primary', icon: 'UserCheckIcon', sub: [{ label: 'Joining Pending', value: ob.joining_pending != null ? ob.joining_pending : 0 }, { label: 'Offer Drop', value: ob.candidate_dropped != null ? ob.candidate_dropped : 0 }, { label: 'Drops After Join', value: ob.drops_after_joining != null ? ob.drops_after_joining : 0 }, { label: 'Joined', value: ob.joined != null ? ob.joined : 0 }] },
        ]
      },
      // Stage cards only (exclude Total Positions for the row)
      detailedKpiStageCards() {
        return (this.detailedKpiCards || []).filter(c => c.key !== 'open_positions')
      },
      openPositionsCount() {
        if (this.globalFilters.positionName) {
          return (this.filteredDetailedPositionSummaryFlattened || []).reduce(
            (s, r) => s + (Number(r.open_positions) || 0),
            0,
          )
        }
        if (this.hasApiJobFilters && this.detailedKpiData) {
          return this.detailedKpiData.open_positions != null ? this.detailedKpiData.open_positions : 0
        }
        const d = this.detailedKpiData || {}
        return d.open_positions != null ? d.open_positions : 0
      },
      // Flattened position summary for Detailed Overview table (one row per position with flat keys)
      detailedPositionSummaryFlattened() {
        return (this.detailedPositionSummary || []).map(row => {
          const s = row.sourcing || {}
          const sc = row.screening || {}
          const i = row.interview || {}
          const o = row.offer || {}
          const ob = row.onboarding || {}
          return {
            position_name: row.position_name,
            function: row.function,
            open_positions: row.open_positions != null ? row.open_positions : (row.open_postions != null ? row.open_postions : 0),
            s_total: s.total_profile_shared != null ? s.total_profile_shared : 0,
            s_pending: s.feedback_pending != null ? s.feedback_pending : 0,
            s_received: s.feedback_received != null ? s.feedback_received : 0,
            sc_total: sc.total_screening != null ? sc.total_screening : 0,
            sc_select: sc.screen_select != null ? sc.screen_select : 0,
            sc_reject: sc.screen_reject != null ? sc.screen_reject : 0,
            sc_hold: sc.on_hold != null ? sc.on_hold : 0,
            i_total: i.total_interview != null ? i.total_interview : 0,
            i_pending: i.interview_pending != null ? i.interview_pending : 0,
            i_current: i.interview_current != null ? i.interview_current : 0,
            i_reject: i.interview_reject != null ? i.interview_reject : 0,
            i_select: i.interview_select != null ? i.interview_select : 0,
            o_selected: o.total_selected != null ? o.total_selected : 0,
            o_doc: o.documentation_pending != null ? o.documentation_pending : 0,
            o_medical: o.medical_pending != null ? o.medical_pending : 0,
            o_conf: o.confirmation_pending != null ? o.confirmation_pending : 0,
            o_offer: o.offer_pending != null ? o.offer_pending : 0,
            o_dropped: o.client_dropped != null ? o.client_dropped : 0,
            ob_offered: ob.total_offered != null ? ob.total_offered : 0,
            ob_joining: ob.joining_pending != null ? ob.joining_pending : 0,
            ob_dropped: ob.candidate_dropped != null ? ob.candidate_dropped : 0,
            ob_joined: ob.joined != null ? ob.joined : 0,
            ob_drops_after: ob.drops_after_joining != null ? ob.drops_after_joining : 0,
          }
        })
      },
      detailedSummaryFields() {
        const base = [
          { key: 'position_name', label: 'Position', sortable: true, thClass: 'bg-white text-dark border-right', tdClass: 'border-right font-weight-bold' },
          { key: 'function', label: 'Function', sortable: true, thClass: 'bg-white text-dark border-right', tdClass: 'border-right' },
          { key: 'open_positions', label: 'Total Positions', sortable: true, thClass: 'table-secondary border-right', tdClass: 'table-secondary border-right' },
          { key: 's_total', label: 'Shared', sortable: true, thClass: 'table-info', tdClass: 'table-info' },
          { key: 's_pending', label: 'Feedback Pending', sortable: true, thClass: 'table-info', tdClass: 'table-info' },
          { key: 's_received', label: 'Feedback Received', sortable: true, thClass: 'table-info', tdClass: 'table-info' },
          { key: 'sc_total', label: 'Total', sortable: true, thClass: 'table-warning', tdClass: 'table-warning' },
          { key: 'sc_select', label: 'Select', sortable: true, thClass: 'table-warning', tdClass: 'table-warning' },
          { key: 'sc_reject', label: 'Reject', sortable: true, thClass: 'table-warning', tdClass: 'table-warning' },
          { key: 'sc_hold', label: 'On Hold', sortable: true, thClass: 'table-warning', tdClass: 'table-warning' },
          { key: 'i_total', label: 'Total', sortable: true, thClass: 'table-danger', tdClass: 'table-danger' },
          { key: 'i_pending', label: 'Pending', sortable: true, thClass: 'table-danger', tdClass: 'table-danger' },
          { key: 'i_current', label: 'Scheduled', sortable: true, thClass: 'table-danger', tdClass: 'table-danger' },
          { key: 'i_reject', label: 'Reject', sortable: true, thClass: 'table-danger', tdClass: 'table-danger' },
          { key: 'i_select', label: 'Select', sortable: true, thClass: 'table-danger', tdClass: 'table-danger' },
          { key: 'o_selected', label: 'Selected', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'o_doc', label: 'Doc Pending', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'o_medical', label: 'Medical Pending', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'o_conf', label: 'Confirmation Pending', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'o_offer', label: 'Offer Pending', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'o_dropped', label: 'Not Offered', sortable: true, thClass: 'table-success', tdClass: 'table-success' },
          { key: 'ob_offered', label: 'Offered', sortable: true, thClass: 'table-primary', tdClass: 'table-primary' },
          { key: 'ob_joining', label: 'Joining Pending', sortable: true, thClass: 'table-primary', tdClass: 'table-primary' },
          { key: 'ob_dropped', label: 'Offer Dropped', sortable: true, thClass: 'table-primary', tdClass: 'table-primary' },
          { key: 'ob_joined', label: 'Joined', sortable: true, thClass: 'table-primary', tdClass: 'table-primary' },
          { key: 'ob_drops_after', label: 'Drops After Join', sortable: true, thClass: 'table-primary', tdClass: 'table-primary' },
        ]
        return base
      },
      // Weekwise status summary for Candidate Data tab (Diversity filters only)
      weekwiseStatusSummary() {
        const weekMap = new Map()
        
        this.diversityAnalyticsCandidates.forEach(candidate => {
          const updatedRaw = candidate.updated_at || candidate.updatedAt
          const ts = this.parseCandidateTimestamp(updatedRaw) ?? this.parseCandidateTimestamp(candidate.createdAt)
          if (ts == null) return

          const date = new Date(ts)
          const weekStart = this.getWeekStartDate(date)
          const weekKey = weekStart.toISOString().slice(0, 10)
          const weekLabel = this.formatWeekLabelRange(weekStart)
          
          if (!weekMap.has(weekKey)) {
            weekMap.set(weekKey, {
              weekKey,
              weekLabel,
              weekStart,
              profileShared: 0,
              screenSelect: 0,
              interview: 0,
              noShow: 0,
              selected: 0,
              shortlisted: 0,
              offered: 0,
              joined: 0,
              screenReject: 0,
              drop: 0,
              rejected: 0,
              total: 0,
            })
          }
          
          const week = weekMap.get(weekKey)
          week.total++
          
          const status = candidate.candidateStatus
          if (status === 'Profile Shared' || status === 'ProfileShared') week.profileShared++
          if (status === 'Screen Select' || status === 'ScreenSelect') week.screenSelect++
          if (status === 'Interview') week.interview++
          if (status === 'No Show') week.noShow++
          if (status === 'Selected') week.selected++
          if (status === 'Shortlisted') week.shortlisted++
          if (status === 'Offered' || status === 'Offer Accepted') week.offered++
          if (status === 'Joined') week.joined++
          if (status === 'Screen Reject' || status === 'ScreenReject') week.screenReject++
          if (status === 'Drop' || status === 'OfferDrop' || status === 'Offer Drop') week.drop++
          if (status === 'Rejected') week.rejected++
        })
        
        return Array.from(weekMap.values())
          .sort((a, b) => new Date(b.weekStart) - new Date(a.weekStart))
          .slice(0, 8) // Last 8 weeks
      },
      /** Rejection funnel: API when job filters applied; re-aggregate when position narrowed on client. */
      rejectionRatiosDisplay() {
        const base = this.rejectionRatios || []
        if (!this.hasActiveGlobalFilters || base.length === 0) return base
        if (this.globalFilters.positionName) {
          return this.rejectionRatiosFromFilteredDetailRows(base)
        }
        if (this.hasApiJobFilters) return base
        return this.rejectionRatiosFromFilteredDetailRows(base)
      },
    },
    created() {
      this.restoreDashboardTabFromRouteOrStorage(this.$route)
    },
    beforeRouteUpdate(to, from, next) {
      if (to.query.tab !== from.query.tab) {
        this.applyTabFromRouteQuery(to)
      }
      next()
    },
    beforeRouteLeave(to, from, next) {
      this.persistDashboardUiState()
      next()
    },
    mounted() {
      this.syncTabToRouteQuery()
      this.loadFilterOptions()
      this.loadDashboardData()
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        this.isAdmin = user.roles && user.roles[0] === 'ADMIN'
        if (!this.clientName) {
          this.clientName = 'Reliance RPO'
        }
      } catch (e) {
        this.isAdmin = false
        if (!this.clientName) {
          this.clientName = 'Reliance RPO'
        }
      }
      window.addEventListener('scroll', this.onDashboardScroll, { passive: true })
    },
    beforeDestroy() {
      this.persistDashboardUiState()
      window.removeEventListener('scroll', this.onDashboardScroll)
      if (this.funnelChart) {
        this.funnelChart.dispose()
      }
      if (this.tatChart) {
        this.tatChart.dispose()
      }
      if (this.rejectionFunnelChart) {
        this.rejectionFunnelChart.dispose()
      }
      if (this.diversityGenderPieChart) {
        this.diversityGenderPieChart.dispose()
      }
      if (this.diversitySelectionOfferStageBarChart) {
        this.diversitySelectionOfferStageBarChart.dispose()
      }
      if (this.diversityPipelineBarChart) {
        this.diversityPipelineBarChart.dispose()
      }
      if (this.diversityPipelinePieChart) {
        this.diversityPipelinePieChart.dispose()
      }
      if (this.candidateOfferSelectionBarChart) {
        this.candidateOfferSelectionBarChart.dispose()
      }
      if (this.candidateSelectionWeekBarChart) {
        this.candidateSelectionWeekBarChart.dispose()
      }
      if (this.candidateJoinedWeekBarChart) {
        this.candidateJoinedWeekBarChart.dispose()
      }
      if (this.dropReasonPieChart) {
        this.dropReasonPieChart.dispose()
      }
      if (this._boundDropReasonPieResize) {
        window.removeEventListener('resize', this._boundDropReasonPieResize)
        this._boundDropReasonPieResize = null
      }
    },
    methods: {
      tabIndexFromSlug(slug) {
        return relianceTabIndexFromSlug(slug)
      },

      saveDashboardTabSlug(slug) {
        try {
          sessionStorage.setItem(RELIANCE_DASHBOARD_UI_TAB_SESSION_KEY, slug)
          localStorage.setItem(RELIANCE_DASHBOARD_UI_TAB_KEY, slug)
        } catch (e) { /* noop */ }
      },

      onDashboardTabInput(index) {
        if (this.suppressTabPersistence) return
        this.persistDashboardUiState()
      },

      tabSlugFromIndex(index) {
        return RELIANCE_DASHBOARD_TAB_SLUGS[index] || 'detailed-overview'
      },

      restoreDashboardTabFromRouteOrStorage(route) {
        this.suppressTabPersistence = true
        const idx = getInitialRelianceTabIndex(route)
        this.activeTabIndex = idx
        this.saveDashboardTabSlug(this.tabSlugFromIndex(idx))
        this.$nextTick(() => {
          this.suppressTabPersistence = false
          this.syncTabToRouteQuery()
        })
      },

      applyTabFromRouteQuery(route) {
        const routeTab = route && route.query ? route.query.tab : null
        let idx = routeTab ? this.tabIndexFromSlug(routeTab) : -1
        if (idx < 0) {
          idx = getInitialRelianceTabIndex(route)
        }
        if (idx >= 0 && idx !== this.activeTabIndex) {
          this.suppressTabPersistence = true
          this.activeTabIndex = idx
          this.saveDashboardTabSlug(this.tabSlugFromIndex(idx))
          this.$nextTick(() => {
            this.suppressTabPersistence = false
          })
        }
      },

      syncTabToRouteQuery() {
        if (this.suppressTabPersistence) return
        const slug = this.tabSlugFromIndex(this.activeTabIndex)
        const current = this.$route.query.tab
        if (current === slug) return
        const query = { ...this.$route.query, tab: slug }
        this.$router.replace({ name: this.$route.name, query }).catch(() => {})
      },

      candidateViewRoute(item) {
        return {
          name: 'apps-candidates-view',
          params: { id: item.cid, jobId: item.jid },
          query: {
            job_id: item.job_id,
            position_name: item.position_name
              ? item.position_name.replace(`${item.job_id} - `, '')
              : '',
            client: item.client,
            returnTo: 'dashboard-reliance',
          },
        }
      },

      persistDashboardUiState() {
        const slug = this.tabSlugFromIndex(this.activeTabIndex)
        this.saveDashboardTabSlug(slug)
        try {
          const y = window.pageYOffset
            || document.documentElement.scrollTop
            || document.body.scrollTop
            || 0
          localStorage.setItem(RELIANCE_DASHBOARD_UI_SCROLL_KEY, String(Math.round(y)))
        } catch (e) { /* noop */ }
        if (!this.suppressTabPersistence) {
          this.syncTabToRouteQuery()
        }
      },

      async ensureDashboardTabInHistory() {
        const slug = this.tabSlugFromIndex(this.activeTabIndex)
        this.saveDashboardTabSlug(slug)
        const query = { ...this.$route.query, tab: slug }
        if (this.$route.name === 'dashboard-reliance' && this.$route.query.tab !== slug) {
          await this.$router.replace({ name: 'dashboard-reliance', query }).catch(() => {})
        }
      },

      async navigateToCandidateView(item) {
        await this.ensureDashboardTabInHistory()
        this.persistDashboardUiState()
        await this.$router.push(this.candidateViewRoute(item)).catch(() => {})
      },

      async navigateToJobView(jid) {
        await this.ensureDashboardTabInHistory()
        this.persistDashboardUiState()
        await this.$router.push(`/apps/view-job/${jid}`).catch(() => {})
      },

      onDashboardScroll() {
        if (this._scrollPersistTimer) clearTimeout(this._scrollPersistTimer)
        this._scrollPersistTimer = setTimeout(() => {
          try {
            const y = window.pageYOffset
              || document.documentElement.scrollTop
              || 0
            localStorage.setItem(RELIANCE_DASHBOARD_UI_SCROLL_KEY, String(Math.round(y)))
          } catch (e) { /* noop */ }
        }, 200)
      },

      scheduleDashboardScrollRestore() {
        if (this.scrollRestoreScheduled) return
        this.scrollRestoreScheduled = true
        let raw = 0
        try {
          raw = parseInt(localStorage.getItem(RELIANCE_DASHBOARD_UI_SCROLL_KEY) || '0', 10)
        } catch (e) { /* noop */ }
        if (!Number.isFinite(raw) || raw < 0) {
          this.scrollRestoreScheduled = false
          return
        }
        const restore = () => {
          window.scrollTo(0, raw)
        }
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            restore()
            setTimeout(restore, 100)
            setTimeout(restore, 400)
            setTimeout(() => {
              this.scrollRestoreScheduled = false
            }, 500)
          })
        })
      },

      renderChartsForTabSlug(slug, options = {}) {
        const { includeCandidateExtras = false } = options
        if (slug === 'overall' && this.rejectionRatiosDisplay && this.rejectionRatiosDisplay.length > 0) {
          this.renderRejectionFunnelChart()
        } else if (slug === 'selections') {
          this.renderFunnelChart()
        } else if (slug === 'timelines') {
          this.renderTATChart()
        } else if (slug === 'diversity') {
          this.renderDiversityPieCharts()
        } else if (slug === 'candidate-data') {
          if (includeCandidateExtras) this.generateLatestUpdates()
          this.renderCandidateDataWeekCharts()
          this.renderDropReasonPie()
        }
      },

      ensureAnalyticsForTabSlug(slug, draw) {
        const ensurePool = () => {
          if (!this.analyticsCandidatePool.length && !this.analyticsCandidatesLoading) {
            this.fetchAnalyticsCandidatePool().finally(draw)
          } else {
            draw()
          }
        }
        if (this.candidates.length === 0) {
          this.loadCandidates().then(ensurePool)
        } else {
          ensurePool()
        }
      },

      syncGlobalFilterCharts() {
        this.$nextTick(() => {
          setTimeout(() => {
            const slug = this.activeTabSlug
            if (slug === 'overall' || slug === 'selections' || slug === 'timelines' || slug === 'diversity') {
              this.renderChartsForTabSlug(slug)
            }
          }, 120)
        })
      },

      normalizeRejectionStageLabel(s) {
        return String(s || '')
          .replace(/[→➜]/g, '->')
          .replace(/\s*->\s*/g, '->')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase()
      },

      rejectionStageBucket(norm) {
        if (!norm) return 'other'
        if (norm.includes('profile') && norm.includes('shared') && norm.includes('screen') && norm.includes('reject')) return 'p2sr'
        if (norm.includes('screen') && norm.includes('select') && norm.includes('reject')) return 'ss2r'
        if (norm.includes('interview') && norm.includes('reject')) return 'iv2r'
        if ((norm.includes('selection') || norm.includes('selected')) && norm.includes('not') && norm.includes('offer')) return 'sel_no'
        if ((norm.includes('selection') || norm.includes('selected')) && norm.includes('offer') && norm.includes('drop')) return 'sel_od'
        if (norm.includes('offer') && norm.includes('drop')) return 'sel_od'
        return 'other'
      },

      sumRejectionDetailRows(rows) {
        const a = {
          s_total: 0,
          sc_reject: 0,
          sc_select: 0,
          i_reject: 0,
          i_total: 0,
          o_dropped: 0,
          o_selected: 0,
          ob_dropped: 0,
        }
        for (const r of rows || []) {
          a.s_total += Number(r.s_total) || 0
          a.sc_reject += Number(r.sc_reject) || 0
          a.sc_select += Number(r.sc_select) || 0
          a.i_reject += Number(r.i_reject) || 0
          a.i_total += Number(r.i_total) || 0
          a.o_dropped += Number(r.o_dropped) || 0
          a.o_selected += Number(r.o_selected) || 0
          a.ob_dropped += Number(r.ob_dropped) || 0
        }
        return a
      },

      buildDetailedKpiCardsFromApiData(d) {
        const s = d.sourcing || {}
        const sc = d.screening || {}
        const i = d.interview || {}
        const o = d.offer || {}
        const ob = d.onboarding || {}
        return [
          { key: 'open_positions', label: 'Total Positions', value: d.open_positions != null ? d.open_positions : 0, color: 'secondary', icon: 'BriefcaseIcon', sub: null },
          { key: 'sourcing', label: 'Sourcing', value: s.total_profile_shared != null ? s.total_profile_shared : 0, color: 'info', icon: 'UsersIcon', sub: [{ label: 'Feedback Pending', value: s.feedback_pending != null ? s.feedback_pending : 0 }, { label: 'Feedback Received', value: s.feedback_received != null ? s.feedback_received : 0 }] },
          { key: 'screening', label: 'Screening', value: sc.total_screening != null ? sc.total_screening : 0, color: 'warning', icon: 'FilterIcon', sub: [{ label: 'Select', value: sc.screen_select != null ? sc.screen_select : 0 }, { label: 'Reject', value: sc.screen_reject != null ? sc.screen_reject : 0 }, { label: 'On Hold', value: sc.on_hold != null ? sc.on_hold : 0 }] },
          { key: 'interview', label: 'Interview', value: i.total_interview != null ? i.total_interview : 0, color: 'danger', icon: 'MessageSquareIcon', sub: [{ label: 'Scheduled', value: i.interview_current != null ? i.interview_current : 0 }, { label: 'Select', value: i.interview_select != null ? i.interview_select : 0 }, { label: 'Reject', value: i.interview_reject != null ? i.interview_reject : 0 }, { label: 'No Show', value: i.interview_pending != null ? i.interview_pending : 0 }] },
          { key: 'offer', label: 'Offer', value: o.total_selected != null ? o.total_selected : 0, color: 'success', icon: 'AwardIcon', sub: [{ label: 'Doc Pending', value: o.documentation_pending != null ? o.documentation_pending : 0 }, { label: 'Medical Pending', value: o.medical_pending != null ? o.medical_pending : 0 }, { label: 'Confirmation Pending', value: o.confirmation_pending != null ? o.confirmation_pending : 0 }, { label: 'Draft offer', value: o.draft_offer != null ? o.draft_offer : 0 }, { label: 'Not Offered', value: o.client_dropped != null ? o.client_dropped : 0 }, { label: 'Offered', value: ob.total_offered != null ? ob.total_offered : 0 }] },
          { key: 'onboarding', label: 'Onboarding', value: ob.total_offered != null ? ob.total_offered : 0, color: 'primary', icon: 'UserCheckIcon', sub: [{ label: 'Joining Pending', value: ob.joining_pending != null ? ob.joining_pending : 0 }, { label: 'Offer Drop', value: ob.candidate_dropped != null ? ob.candidate_dropped : 0 }, { label: 'Drops After Join', value: ob.drops_after_joining != null ? ob.drops_after_joining : 0 }, { label: 'Joined', value: ob.joined != null ? ob.joined : 0 }] },
        ]
      },

      rejectionRatiosFromFilteredDetailRows(base) {
        const flatAll = this.detailedPositionSummaryFlattened || []
        const flatFilt = this.filteredDetailedPositionSummaryFlattened || []
        const allSums = this.sumRejectionDetailRows(flatAll)
        const fSums = this.sumRejectionDetailRows(flatFilt)
        return base.map(apiRow => {
          const norm = this.normalizeRejectionStageLabel(apiRow.stage_transition)
          const bucket = this.rejectionStageBucket(norm)
          let rej = 0
          let tot = 0
          if (bucket === 'p2sr') {
            rej = fSums.sc_reject
            tot = fSums.s_total
          } else if (bucket === 'ss2r') {
            tot = fSums.sc_select
            const rScale = allSums.sc_select > 0 ? fSums.sc_select / allSums.sc_select : 0
            rej = Math.round((Number(apiRow.rejection_count) || 0) * rScale)
          } else if (bucket === 'iv2r') {
            rej = fSums.i_reject
            tot = fSums.i_total
          } else if (bucket === 'sel_no') {
            rej = fSums.o_dropped
            tot = fSums.o_selected
          } else if (bucket === 'sel_od') {
            rej = fSums.ob_dropped
            tot = fSums.o_selected
          } else {
            tot = fSums.s_total
            const rScale = allSums.s_total > 0 ? fSums.s_total / allSums.s_total : 0
            rej = Math.round((Number(apiRow.rejection_count) || 0) * rScale)
          }
          const conversion_rate = tot > 0 ? Math.round((rej / tot) * 10000) / 100 : 0
          return {
            ...apiRow,
            rejection_count: rej,
            total_at_stage: tot,
            conversion_rate,
          }
        })
      },

      normalizeTatStageKey(s) {
        return String(s || '')
          .replace(/[→➜]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase()
      },

      buildDetailedKpiCardsFromFlatRows(rows) {
        const rlist = rows || []
        let open_positions = 0
        let s_total = 0
        let s_pending = 0
        let s_received = 0
        let sc_total = 0
        let sc_select = 0
        let sc_reject = 0
        let sc_hold = 0
        let i_total = 0
        let i_pending = 0
        let i_current = 0
        let i_reject = 0
        let i_select = 0
        let o_selected = 0
        let o_doc = 0
        let o_medical = 0
        let o_conf = 0
        let o_offer = 0
        let o_dropped = 0
        let ob_offered = 0
        let ob_joining = 0
        let ob_dropped = 0
        let ob_joined = 0
        let ob_drops_after = 0
        rlist.forEach(r => {
          open_positions += Number(r.open_positions) || 0
          s_total += Number(r.s_total) || 0
          s_pending += Number(r.s_pending) || 0
          s_received += Number(r.s_received) || 0
          sc_total += Number(r.sc_total) || 0
          sc_select += Number(r.sc_select) || 0
          sc_reject += Number(r.sc_reject) || 0
          sc_hold += Number(r.sc_hold) || 0
          i_total += Number(r.i_total) || 0
          i_pending += Number(r.i_pending) || 0
          i_current += Number(r.i_current) || 0
          i_reject += Number(r.i_reject) || 0
          i_select += Number(r.i_select) || 0
          o_selected += Number(r.o_selected) || 0
          o_doc += Number(r.o_doc) || 0
          o_medical += Number(r.o_medical) || 0
          o_conf += Number(r.o_conf) || 0
          o_offer += Number(r.o_offer) || 0
          o_dropped += Number(r.o_dropped) || 0
          ob_offered += Number(r.ob_offered) || 0
          ob_joining += Number(r.ob_joining) || 0
          ob_dropped += Number(r.ob_dropped) || 0
          ob_joined += Number(r.ob_joined) || 0
          ob_drops_after += Number(r.ob_drops_after) || 0
        })
        return [
          { key: 'open_positions', label: 'Total Positions', value: open_positions, color: 'secondary', icon: 'BriefcaseIcon', sub: null },
          { key: 'sourcing', label: 'Sourcing', value: s_total, color: 'info', icon: 'UsersIcon', sub: [{ label: 'Feedback Pending', value: s_pending }, { label: 'Feedback Received', value: s_received }] },
          { key: 'screening', label: 'Screening', value: sc_total, color: 'warning', icon: 'FilterIcon', sub: [{ label: 'Select', value: sc_select }, { label: 'Reject', value: sc_reject }, { label: 'On Hold', value: sc_hold }] },
          { key: 'interview', label: 'Interview', value: i_total, color: 'danger', icon: 'MessageSquareIcon', sub: [{ label: 'Pending', value: i_pending }, { label: 'Scheduled', value: i_current }, { label: 'Reject', value: i_reject }, { label: 'Select', value: i_select }] },
          { key: 'offer', label: 'Offer', value: o_selected, color: 'success', icon: 'AwardIcon', sub: [{ label: 'Doc Pending', value: o_doc }, { label: 'Medical Pending', value: o_medical }, { label: 'Confirmation Pending', value: o_conf }, { label: 'Offer Pending', value: o_offer }, { label: 'Not Offered', value: o_dropped }] },
          { key: 'onboarding', label: 'Onboarding', value: ob_offered, color: 'primary', icon: 'UserCheckIcon', sub: [{ label: 'Joining Pending', value: ob_joining }, { label: 'Offer Drop', value: ob_dropped }, { label: 'Joined', value: ob_joined }, { label: 'Drops After Join', value: ob_drops_after }] },
        ]
      },

      emptyFilterState() {
        return {
          positionType: '',
          functionName: '',
          positionName: '',
          driveName: '',
          positionSubmissionStartDate: '',
          positionSubmissionEndDate: '',
        }
      },

      buildDashboardApiFilters() {
        const f = this.globalFilters
        const params = {}
        if (f.positionType) params.positionType = f.positionType
        if (f.functionName) params.function = f.functionName
        if (f.driveName) params.driveName = f.driveName
        if (f.positionSubmissionStartDate) {
          params.positionSubmissionStartDate = f.positionSubmissionStartDate
          if (f.positionSubmissionEndDate) {
            params.positionSubmissionEndDate = f.positionSubmissionEndDate
          }
        }
        return params
      },

      validateSubmissionDateRange(pending) {
        const start = (pending.positionSubmissionStartDate || '').trim()
        const end = (pending.positionSubmissionEndDate || '').trim()
        if ((start && !end) || (!start && end)) {
          this.$bvToast.toast('Position submission range requires both start and end dates (YYYY-MM-DD).', {
            title: 'Invalid dates',
            variant: 'warning',
            solid: true,
          })
          return false
        }
        return true
      },

      positionMatchesType(row, positionType) {
        if (!positionType) return true
        const drive = this.normalizeDriveLabel(row).toUpperCase()
        if (positionType === 'FTE') return drive.includes('FTE')
        if (positionType === 'LATERAL') return drive.includes('LATERAL')
        return true
      },

      async loadFilterOptions() {
        if (!this.clientName) return
        this.filterOptionsLoading = true
        try {
          const response = await RelianceService.getDashboardFilterOptions(this.clientName)
          if (response.status === 'SUCCESS' && response.data) {
            const d = response.data
            this.filterOptionsFromApi = {
              functions: d.functions || d.functionList || [],
              positions: d.positions || d.positionList || d.positionNames || [],
              driveNames: d.driveNames || d.drives || d.driveList || [],
            }
          }
        } catch (e) {
          console.error('loadFilterOptions', e)
        } finally {
          this.filterOptionsLoading = false
        }
      },

      applyGlobalFilters() {
        if (!this.validateSubmissionDateRange(this.globalFiltersPending)) return
        this.globalFilters = {
          positionType: this.globalFiltersPending.positionType || '',
          functionName: this.globalFiltersPending.functionName || '',
          positionName: this.globalFiltersPending.positionName || '',
          driveName: this.globalFiltersPending.driveName || '',
          positionSubmissionStartDate: this.globalFiltersPending.positionSubmissionStartDate || '',
          positionSubmissionEndDate: this.globalFiltersPending.positionSubmissionEndDate || '',
        }
        this.globalFiltersPending = { ...this.globalFilters }
        this.loadDashboardData()
        this.fetchAnalyticsCandidatePool()
        if (this.activeTabSlug === 'candidate-data') {
          this.loadCandidates()
        }
      },

      clearGlobalFilters() {
        const z = this.emptyFilterState()
        this.globalFilters = { ...z }
        this.globalFiltersPending = { ...z }
        this.loadDashboardData()
        this.fetchAnalyticsCandidatePool()
        if (this.activeTabSlug === 'candidate-data') {
          this.loadCandidates()
        }
      },

      /**
       * Unique row key for Reliance RPO candidates: the same `cid` can apply to multiple jobs (`jid`).
       * `slotIndex` breaks ties if the API ever returns duplicate cid+jid on one page.
       */
      candidateApplicationRowKey(c, slotIndex = 0) {
        if (!c) return `na-${slotIndex}`
        const cid = c.cid != null ? String(c.cid) : 'nc'
        const jid = c.jid != null ? String(c.jid) : (c.job_id != null ? String(c.job_id) : 'nj')
        return `${cid}-${jid}-${slotIndex}`
      },

      /**
       * Client-side scope by position / function / drive / date.
       * Uses `analyticsCandidatePool` (all pages, no table search/status API filter) when loaded so charts match full data.
       */
      applyDashboardCandidateScope(filterState) {
        const list = (this.analyticsCandidatePool && this.analyticsCandidatePool.length > 0)
          ? this.analyticsCandidatePool
          : (this.candidates || [])
        const f = filterState || {}
        const from = f.positionSubmissionStartDate ? new Date(f.positionSubmissionStartDate) : null
        const to = f.positionSubmissionEndDate ? new Date(f.positionSubmissionEndDate) : null
        if (to) to.setHours(23, 59, 59, 999)
        return list.filter(c => {
          if (f.positionName && !this.positionsNameEquivalent(c.position_name, f.positionName)) return false
          if (f.functionName && this.candidateFunctionLabel(c) !== f.functionName) return false
          if (f.driveName && this.normalizeDriveLabel(c) !== f.driveName) return false
          if (f.positionType && !this.positionMatchesType(c, f.positionType)) return false
          if (from || to) {
            const subRaw = c.position_submission_date || c.positionSubmissionDate
            const ts = this.parseCandidateTimestamp(subRaw)
            if (ts == null) return false
            const dt = new Date(ts)
            if (from && dt < from) return false
            if (to && dt > to) return false
          }
          return true
        })
      },
      async loadDashboardData() {
        this.loading = true
        try {
          const apiFilters = this.buildDashboardApiFilters()
          const response = await RelianceService.getDashboardData(this.clientName, apiFilters)

          if (response.status === 'SUCCESS' && response.data) {
            this.kpiData = response.data.kpiData || this.kpiData
            this.positionData = response.data.positionData || []
            this.detailedKpiData = response.data.detailedKpiData || null
            this.detailedPositionSummary = response.data.detailedPositionData || []
            
            // Use positionData for selection details if selectionData is empty
            // Add conversion_rate calculation for each position
            const selectionDataFromAPI = response.data.selectionData || []
            if (selectionDataFromAPI.length > 0) {
              this.selectionData = selectionDataFromAPI
            } else {
              // Generate selectionData from positionData with conversion rates
              this.selectionData = this.positionData.map(pos => ({
                ...pos,
                conversion_rate: pos.profile_shared > 0 
                  ? Math.round((pos.selections / pos.profile_shared) * 100) 
                  : 0
              }))
            }
            
            // Process TAT data - replace "Closed" with "Joined"
            let tatDataFromAPI = response.data.tatData || []
            this.tatData = tatDataFromAPI.map(item => ({
              ...item,
              stage_transition: item.stage_transition
                ? item.stage_transition
                    .replace(/Closed Position/gi, 'Joined')
                    .replace(/Closed/g, 'Joined')
                : item.stage_transition
            }))

            // Process TAT summary - replace "Closed" with "Joined"
            let tatSummaryFromAPI = response.data.tatSummary || []
            this.tatSummary = tatSummaryFromAPI.map(item => ({
              ...item,
              stage: item.stage
                ? item.stage.replace(/Closed Position/gi, 'Joined').replace(/Closed/g, 'Joined')
                : item.stage
            }))

            // Add summary rows only when matching TAT timeline exists (no synthetic zero rows).
            const hasOfferedToJoined = this.tatSummary.some(t => 
              t.stage && t.stage.includes('Offered') && t.stage.includes('Joined')
            )
            if (!hasOfferedToJoined) {
              const offeredToJoinedData = this.tatData.find(t => 
                t.stage_transition && t.stage_transition.includes('Offered') && t.stage_transition.includes('Joined')
              )
              if (offeredToJoinedData) {
                this.tatSummary.push({
                  stage: 'Offered → Joined',
                  avgDays: offeredToJoinedData.avg_days,
                  icon: 'CheckCircleIcon',
                  color: 'success'
                })
              }
            }

            const hasSelectionToOffer = this.tatSummary.some(t => 
              t.stage && t.stage.includes('Selection') && t.stage.includes('Offer')
            )
            if (!hasSelectionToOffer) {
              const selectionToOfferData = this.tatData.find(t => 
                t.stage_transition && t.stage_transition.includes('Selection') && t.stage_transition.includes('Offered')
              )
              if (selectionToOfferData) {
                this.tatSummary.push({
                  stage: 'Selection → Offer',
                  avgDays: selectionToOfferData.avg_days,
                  icon: 'AwardIcon',
                  color: 'primary'
                })
              }
            }

            // Load selection funnel totals and position selection funnel
            this.selectionFunnelTotals = response.data.selectionFunnelTotals || {
              total_selection: 0,
              total_documentation: 0,
              total_confirmation: 0,
              total_medical: 0,
              total_offered: 0,
              total_joined: 0
            }
            this.positionSelectionFunnel = response.data.positionSelectionFunnel || []

            // Load rejection ratios
            this.rejectionRatios = response.data.rejectionRatios || []

            this.$nextTick(() => {
              setTimeout(() => {
                this.renderChartsForTabSlug(this.activeTabSlug)
              }, 100)
            })
            this.globalFiltersPending = { ...this.globalFilters }
            this.$nextTick(() => {
              setTimeout(() => this.syncGlobalFilterCharts(), 100)
            })
            this.fetchAnalyticsCandidatePool()
          } else if (response.status === 'ERROR') {
            this.$bvToast.toast(response.message || 'Failed to load dashboard data', {
              title: 'Error',
              variant: 'danger',
              solid: true,
            })
          }
        } catch (error) {
          console.error('Error loading dashboard data:', error)
          const msg = error.response?.data?.message || error.message || 'Failed to load dashboard data'
          this.$bvToast.toast(msg, {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        } finally {
          this.loading = false
          this.scheduleDashboardScrollRestore()
        }
      },
      refreshData() {
        this.loadFilterOptions()
        this.loadDashboardData()
        if (this.activeTabSlug === 'diversity' || this.activeTabSlug === 'candidate-data') {
          this.loadCandidates()
        }
      },

      printReportDelay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms))
      },

      resizeDashboardChartsIfPresent() {
        this.$nextTick(() => {
          try {
            if (this.rejectionFunnelChart) this.rejectionFunnelChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.funnelChart) this.funnelChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.tatChart) this.tatChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.diversityGenderPieChart) this.diversityGenderPieChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.diversitySelectionOfferStageBarChart) this.diversitySelectionOfferStageBarChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.diversityPipelineBarChart) this.diversityPipelineBarChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.diversityPipelinePieChart) this.diversityPipelinePieChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.candidateOfferSelectionBarChart) this.candidateOfferSelectionBarChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.candidateSelectionWeekBarChart) this.candidateSelectionWeekBarChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.candidateJoinedWeekBarChart) this.candidateJoinedWeekBarChart.resize()
          } catch (e) { /* noop */ }
          try {
            if (this.dropReasonPieChart) this.dropReasonPieChart.resize()
          } catch (e) { /* noop */ }
        })
      },

      /**
       * Disable ECharts tooltips / scroll legends / toolbox for PDF so output is static and legible.
       * Mutates `option` in place (each render builds a fresh option object).
       */
      patchEchartsForPrint(option) {
        if (!this.preparePrintLayout || !option || typeof option !== 'object') return
        const hideTip = (t) => {
          if (!t) return
          if (Array.isArray(t)) t.forEach(hideTip)
          else if (typeof t === 'object') Object.assign(t, { show: false })
        }
        hideTip(option.tooltip)
        if (option.legend) {
          const L = option.legend
          if (Array.isArray(L)) {
            L.forEach((leg) => {
              if (leg && leg.type === 'scroll') leg.type = 'plain'
            })
          } else if (L.type === 'scroll') {
            L.type = 'plain'
          }
        }
        if (option.toolbox && typeof option.toolbox === 'object') {
          option.toolbox.show = false
        }
        if (Array.isArray(option.dataZoom)) {
          option.dataZoom.length = 0
        }
        if (Array.isArray(option.series)) {
          option.series.forEach((s) => {
            if (s && s.emphasis && s.emphasis.label) {
              s.emphasis.label.show = false
            }
          })
        }
        const liftLegendText = (L) => {
          if (!L || typeof L !== 'object') return
          if (Array.isArray(L)) {
            L.forEach(liftLegendText)
            return
          }
          L.textStyle = L.textStyle && typeof L.textStyle === 'object' ? L.textStyle : {}
          const fs = L.textStyle.fontSize
          if (fs == null || (typeof fs === 'number' && fs < 11)) {
            L.textStyle.fontSize = 12
          }
        }
        liftLegendText(option.legend)
        const bumpPrintFonts = (node, depth = 0) => {
          if (!node || typeof node !== 'object' || depth > 22) return
          if (typeof node.fontSize === 'number') {
            node.fontSize = Math.min(24, Math.round(node.fontSize * 1.22))
          }
          if (Array.isArray(node)) {
            node.forEach((item) => bumpPrintFonts(item, depth + 1))
            return
          }
          Object.keys(node).forEach((k) => bumpPrintFonts(node[k], depth + 1))
        }
        bumpPrintFonts(option)
      },

      /** Rebuild charts after `preparePrintLayout` so canvases pick up print-safe options. */
      redrawChartsForPrintExport() {
        if (!this.preparePrintLayout) return
        this.$nextTick(() => {
          try {
            if (document.getElementById('rejectionFunnelChart')) this.renderRejectionFunnelChart()
          } catch (e) { /* noop */ }
          try {
            if (document.getElementById('selectionFunnelChart')) this.renderFunnelChart()
          } catch (e) { /* noop */ }
          try {
            if (document.getElementById('tatTimelineChart')) this.renderTATChart()
          } catch (e) { /* noop */ }
          try {
            if (document.getElementById('diversityGenderPieChart')) this.renderDiversityPieCharts()
          } catch (e) { /* noop */ }
          try {
            this.renderCandidateDataWeekCharts()
            this.renderDropReasonPie()
          } catch (e) { /* noop */ }
        })
      },

      onAfterPrintDashboardReport() {
        this.preparePrintLayout = false
        const idx = this.savedTabForPrint
        this.activeTabIndex = idx
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderChartsForTabSlug(this.tabSlugFromIndex(idx), { includeCandidateExtras: true })
          }, 250)
        })
      },

      /**
       * Plan B: walk each dashboard tab so charts and tables render, then open the system print dialog.
       * User chooses Save as PDF for a multi-page document (CSS page-breaks between tabs).
       */
      async generatePrintReport() {
        if (this.loading) {
          this.$bvToast.toast('Please wait until the dashboard has finished loading.', {
            title: 'Still loading',
            variant: 'warning',
            solid: true,
          })
          return
        }

        const TAB_COUNT = RELIANCE_DASHBOARD_TAB_SLUGS.length
        this.savedTabForPrint = this.activeTabIndex
        this.generatingPrintReport = true

        try {
          for (let i = 0; i < TAB_COUNT; i += 1) {
            this.activeTabIndex = i
            await this.$nextTick()
            await this.printReportDelay(520)
            const tabSlug = this.tabSlugFromIndex(i)
            if (tabSlug === 'diversity' || tabSlug === 'candidate-data') {
              await this.loadCandidates()
              await this.printReportDelay(400)
            }
          }
        } finally {
          this.generatingPrintReport = false
        }

        this.preparePrintLayout = true
        await this.$nextTick()
        await this.printReportDelay(120)
        this.redrawChartsForPrintExport()
        await this.printReportDelay(280)
        this.resizeDashboardChartsIfPresent()
        await this.printReportDelay(120)

        window.addEventListener('afterprint', this.onAfterPrintDashboardReport, { once: true })
        window.print()
      },

      /** Rows for print: if count exceeds cap, omit all rows (use empty-text message). */
      printReportTableItems(items) {
        const arr = Array.isArray(items) ? items : []
        if (!this.preparePrintLayout) return arr
        const cap = Math.max(1, Number(this.printReportTableRowCap) || 15)
        if (arr.length > cap) return []
        return arr
      },

      /** b-table empty-text while printing vs on screen. */
      printReportTableEmptyText(items, defaultEmpty = 'No data') {
        const arr = Array.isArray(items) ? items : []
        if (!this.preparePrintLayout) return defaultEmpty
        const cap = Math.max(1, Number(this.printReportTableRowCap) || 15)
        if (arr.length > cap) {
          return `Not printed: ${arr.length} rows (limit ${cap} for print/PDF). Open the live dashboard for the full table.`
        }
        return defaultEmpty
      },

      async downloadPositionDetails() {
        if (!this.filteredPositionData || this.filteredPositionData.length === 0) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'No data to download',
              icon: 'EditIcon',
              variant: 'warning',
            },
          })
          return
        }
        const workbook = new Excel.Workbook()
        const worksheet = workbook.addWorksheet('Position Details', { views: [{ state: 'frozen', ySplit: 1 }] })
        const headers = this.positionFields.map(f => ({ header: f.label, key: f.key, width: 14 }))
        worksheet.columns = headers
        this.filteredPositionData.forEach(item => {
          const row = this.positionFields.map(f => item[f.key] != null ? String(item[f.key]) : '')
          worksheet.addRow(row)
        })
        const thinBorder = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        worksheet.eachRow((row, rowNumber) => {
          row.eachCell((cell) => {
            cell.border = thinBorder
            if (rowNumber === 1) {
              cell.font = { bold: true }
            }
          })
        })
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const date = new Date()
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        saveAs(blob, `position-details-${dateStr}.xlsx`)
      },
      async downloadPositionWiseSummaryExcel() {
        const items = this.filteredDetailedPositionSummaryFlattened || []
        if (!items.length) return
        const fields = this.detailedSummaryFields
        const thinBorder = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }
        const borderStyle = thinBorder
        const colors = {
          position: 'FFF8F9FA',
          openPositions: 'FFE2E3E5',
          sourcing: 'FFD1ECF1',
          screening: 'FFFFF3CD',
          interview: 'FFF8D7DA',
          offer: 'FFD4EDDA',
          onboarding: 'FFCCE5FF',
        }
        const stageCols = [
          { start: 1, count: 2, label: 'Position', color: colors.position },
          { start: 3, count: 1, label: 'Total Positions', color: colors.openPositions },
          { start: 4, count: 3, label: 'Sourcing', color: colors.sourcing },
          { start: 7, count: 4, label: 'Screening', color: colors.screening },
          { start: 11, count: 5, label: 'Interview', color: colors.interview },
          { start: 16, count: 6, label: 'Offer', color: colors.offer },
          { start: 22, count: 5, label: 'Onboarding', color: colors.onboarding },
        ]
        const workbook = new Excel.Workbook()
        const worksheet = workbook.addWorksheet('Position Wise Summary', {
          views: [{ state: 'frozen', ySplit: 2 }],
        })
        worksheet.columns = fields.map((f, i) => ({ key: f.key, width: i === 0 ? 28 : 12 }))
        const headerRow1 = worksheet.getRow(1)
        stageCols.forEach(({ start, count, label, color }) => {
          const end = start + count - 1
          if (count > 1) {
            worksheet.mergeCells(1, start, 1, end)
          }
          const cell = worksheet.getCell(1, start)
          cell.value = label
          cell.style = {
            font: { bold: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: color } },
            border: borderStyle,
            alignment: { horizontal: 'center', vertical: 'middle' },
          }
        })
        const headerRow2 = worksheet.getRow(2)
        fields.forEach((f, colIndex) => {
          const col = colIndex + 1
          let color = colors.position
          if (col >= 3 && col <= 3) color = colors.openPositions
          else if (col >= 4 && col <= 6) color = colors.sourcing
          else if (col >= 7 && col <= 10) color = colors.screening
          else if (col >= 11 && col <= 15) color = colors.interview
          else if (col >= 16 && col <= 21) color = colors.offer
          else if (col >= 22 && col <= 26) color = colors.onboarding
          const cell = headerRow2.getCell(col)
          cell.value = f.label
          cell.style = {
            font: { bold: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: color } },
            border: borderStyle,
            alignment: { horizontal: 'center', vertical: 'middle' },
          }
        })
        items.forEach((item, rowIndex) => {
          const rowNum = rowIndex + 3
          const row = worksheet.getRow(rowNum)
          fields.forEach((f, colIndex) => {
            const col = colIndex + 1
            const val = item[f.key]
            const cell = row.getCell(col)
            cell.value = val != null ? (typeof val === 'number' ? val : String(val)) : ''
            let color = colors.position
            if (col >= 3 && col <= 3) color = colors.openPositions
            else if (col >= 4 && col <= 6) color = colors.sourcing
            else if (col >= 7 && col <= 10) color = colors.screening
            else if (col >= 11 && col <= 15) color = colors.interview
            else if (col >= 16 && col <= 21) color = colors.offer
            else if (col >= 22 && col <= 26) color = colors.onboarding
            cell.style = {
              fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: color } },
              border: borderStyle,
            }
          })
        })
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const date = new Date()
        const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        saveAs(blob, `position-wise-summary-${dateStr}.xlsx`)
      },
      getWeekStartDate(date) {
        const d = new Date(date)
        const day = d.getDay()
        const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Adjust to Monday
        d.setDate(diff)
        d.setHours(0, 0, 0, 0)
        return d
      },
      formatWeekLabelRange(date) {
        const endDate = new Date(date)
        endDate.setDate(endDate.getDate() + 6)
        const options = { month: 'short', day: 'numeric' }
        return `${date.toLocaleDateString('en-IN', options)} - ${endDate.toLocaleDateString('en-IN', options)}`
      },
      selectKPI(kpiKey) {
        if (this.selectedKPI === kpiKey) {
          this.selectedKPI = null
          this.showExpandedData = false
        } else {
          this.selectedKPI = kpiKey
          this.showExpandedData = true
        }
      },
      getProgressVariant(value) {
        if (value >= 75) return 'success'
        if (value >= 50) return 'warning'
        return 'danger'
      },
      getTATVariant(days) {
        if (days <= 3) return 'success'
        if (days <= 7) return 'warning'
        return 'danger'
      },
      getSelectionConversionRate(item) {
        // Calculate conversion rate: (total_joined / total_selection) * 100
        if (!item || !item.total_selection || item.total_selection === 0) return 0
        return Math.round((item.total_joined / item.total_selection) * 100)
      },
      /** Outside funnel labels so text is not clipped on narrow segments. */
      buildFunnelOutsideLabel() {
        return {
          label: {
            show: true,
            position: 'right',
            align: 'left',
            distance: 10,
            color: '#5e5873',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 17,
          },
          labelLine: {
            show: true,
            length: 14,
            length2: 10,
            smooth: false,
            lineStyle: {
              color: '#b9b9c3',
              width: 1,
            },
          },
        }
      },

      renderFunnelChart() {
        const chartDom = document.getElementById('selectionFunnelChart')
        if (!chartDom) {
          setTimeout(() => this.renderFunnelChart(), 300)
          return
        }
  
        if (this.funnelChart) {
          this.funnelChart.dispose()
        }
  
        this.funnelChart = echarts.init(chartDom)
        
        const totals = this.selectionFunnelTotalsForCharts
        const outsideLabel = this.buildFunnelOutsideLabel()
        const selectionLabel = {
          ...outsideLabel.label,
          formatter: (params) => `${params.name}: ${Number(params.value) || 0}`,
        }

        const option = {
          tooltip: {
            trigger: 'item',
            formatter: '{b}: {c}'
          },
          legend: {
            show: false,
          },
          series: [
            {
              name: 'Selection Funnel',
              type: 'funnel',
              left: '10%',
              top: 24,
              bottom: 28,
              width: '38%',
              min: 0,
              max: Math.max(totals.total_selection || 1, 100),
              minSize: '0%',
              maxSize: '100%',
              sort: 'descending',
              gap: 4,
              label: selectionLabel,
              labelLine: outsideLabel.labelLine,
              itemStyle: {
                borderColor: '#fff',
                borderWidth: 1
              },
              emphasis: {
                label: {
                  ...selectionLabel,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#252525',
                },
              },
              data: [
                { value: totals.total_selection || 0, name: 'Selections', itemStyle: { color: '#28c76f' } },
                { value: totals.total_documentation || 0, name: 'Documentation', itemStyle: { color: '#82868b' } },
                { value: totals.total_offered || 0, name: 'Offered', itemStyle: { color: '#7367f0' } },
                { value: totals.total_joined || 0, name: 'Joined', itemStyle: { color: '#00cfe8' } },
              ]
            }
          ]
        }

        this.patchEchartsForPrint(option)

        this.funnelChart.setOption(option)
        
        // Make chart responsive
        window.addEventListener('resize', () => {
          if (this.funnelChart) {
            this.funnelChart.resize()
          }
        })

        // Render rejection funnel chart if data is available
        if (this.rejectionRatiosDisplay && this.rejectionRatiosDisplay.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => this.renderRejectionFunnelChart(), 100)
          })
        }
      },
      renderRejectionFunnelChart() {
        const chartDom = document.getElementById('rejectionFunnelChart')
        if (!chartDom) {
          return
        }

        if (this.rejectionFunnelChart) {
          this.rejectionFunnelChart.dispose()
        }

        this.rejectionFunnelChart = echarts.init(chartDom)
        
        const rows = this.rejectionRatiosDisplay || []
        // Prepare data from rejectionRatiosDisplay (respects global filters)
        const chartData = rows.map((item, index) => {
          const colors = ['#ea5455', '#ff9f43', '#ff6b6b', '#ffa8a8', '#ffc9c9', '#ffe8e8']
          return {
            value: item.rejection_count,
            name: item.stage_transition.replace(' → ', ' → '),
            itemStyle: { color: colors[index % colors.length] }
          }
        }).sort((a, b) => b.value - a.value) // Sort descending for funnel

        const maxValue = Math.max(...chartData.map(d => d.value), 1)

        const outsideLabel = this.buildFunnelOutsideLabel()
        const rejectionLabel = {
          ...outsideLabel.label,
          fontSize: 10,
          lineHeight: 15,
          formatter: (params) => {
            const item = rows.find(r => r.stage_transition === params.name)
            const rate = item && item.conversion_rate != null ? ` · ${item.conversion_rate}%` : ''
            const title = String(params.name).replace(/ → /g, '\n→ ')
            return `${title}\n${params.value} rejections${rate}`
          },
        }

        const option = {
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const item = rows.find(r => r.stage_transition === params.name)
              if (item) {
                return `${params.name}<br/>Rejections: ${params.value}<br/>Total: ${item.total_at_stage}<br/>Rate: ${item.conversion_rate}%`
              }
              return `${params.name}: ${params.value}`
            }
          },
          legend: {
            show: false,
          },
          series: [
            {
              name: 'Rejection Funnel',
              type: 'funnel',
              left: '6%',
              top: 16,
              bottom: 24,
              width: '34%',
              min: 0,
              max: maxValue,
              minSize: '0%',
              maxSize: '100%',
              sort: 'descending',
              gap: 4,
              label: rejectionLabel,
              labelLine: outsideLabel.labelLine,
              itemStyle: {
                borderColor: '#fff',
                borderWidth: 1
              },
              emphasis: {
                label: {
                  ...rejectionLabel,
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#252525',
                },
              },
              data: chartData
            }
          ]
        }

        this.patchEchartsForPrint(option)

        this.rejectionFunnelChart.setOption(option)
        
        // Make chart responsive
        window.addEventListener('resize', () => {
          if (this.rejectionFunnelChart) {
            this.rejectionFunnelChart.resize()
          }
        })
      },
      renderTATChart() {
        const chartDom = document.getElementById('tatTimelineChart')

        if (!chartDom) {
          setTimeout(() => this.renderTATChart(), 300)
          return
        }

        const rows = this.displayTatData || []
        if (!rows.length) {
          if (this.tatChart) {
            this.tatChart.dispose()
            this.tatChart = null
          }
          return
        }
  
        if (this.tatChart) {
          this.tatChart.dispose()
        }
  
        this.tatChart = echarts.init(chartDom)
  
        const stages = rows.map(item => item.stage_transition)
        const toNum = v => {
          const n = Number(v)
          return Number.isFinite(n) ? n : 0
        }
        const avgDays = rows.map(item => toNum(item.avg_days))
        const minDays = rows.map(item => toNum(item.min_days))
        const maxDays = rows.map(item => toNum(item.max_days))
        const maxVal = Math.max(1, ...avgDays, ...minDays, ...maxDays)
        const yMax = Math.ceil(maxVal * 1.12)

        const option = {
          color: ['#7367f0', '#28c76f', '#ea5455'],
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross',
              crossStyle: { color: '#b4b7bd' },
            },
          },
          legend: {
            data: ['Average', 'Minimum', 'Maximum'],
            top: 6,
            left: 'center',
            itemGap: 20,
          },
          grid: {
            left: 52,
            right: 28,
            top: 52,
            bottom: 72,
            containLabel: false,
          },
          xAxis: {
            type: 'category',
            data: stages,
            boundaryGap: [0.12, 0.12],
            axisTick: { alignWithLabel: true },
            axisLabel: {
              interval: 0,
              color: '#5e5873',
              fontSize: 11,
              lineHeight: 15,
              rotate: 0,
              formatter(value) {
                const s = String(value || '')
                  .replace(/\s*→\s*/g, '\n')
                  .replace(/\s*->\s*/gi, '\n')
                  .replace(/→/g, '\n')
                return s.length > 42 ? `${s.slice(0, 40)}…` : s
              },
            },
          },
          yAxis: {
            type: 'value',
            name: 'Days',
            nameTextStyle: { color: '#6e6b7b', fontSize: 12, padding: [0, 0, 0, 8] },
            min: 0,
            max: yMax,
            minInterval: 1,
            splitLine: {
              lineStyle: { type: 'dashed', color: '#ebe9f1' },
            },
            axisLabel: { color: '#6e6b7b' },
          },
          series: [
            {
              name: 'Average',
              type: 'bar',
              data: avgDays,
              barMaxWidth: 52,
              barMinHeight: 2,
              barCategoryGap: '28%',
              z: 1,
              itemStyle: {
                color: '#7367f0',
              },
              label: {
                show: true,
                position: 'top',
                fontSize: 10,
                color: '#5e5873',
                formatter: p => (p.value > 0 ? Math.round(p.value) : ''),
              },
            },
            {
              name: 'Minimum',
              type: 'line',
              data: minDays,
              z: 3,
              smooth: false,
              symbol: 'circle',
              symbolSize: 9,
              showSymbol: true,
              lineStyle: { width: 2.5, color: '#28c76f' },
              itemStyle: { color: '#28c76f', borderColor: '#fff', borderWidth: 1 },
            },
            {
              name: 'Maximum',
              type: 'line',
              data: maxDays,
              z: 2,
              smooth: false,
              symbol: 'circle',
              symbolSize: 9,
              showSymbol: true,
              lineStyle: { width: 2.5, color: '#ea5455' },
              itemStyle: { color: '#ea5455', borderColor: '#fff', borderWidth: 1 },
            },
          ],
        }

        this.patchEchartsForPrint(option)

        this.tatChart.setOption(option)
        
        // Make chart responsive
        window.addEventListener('resize', () => {
          if (this.tatChart) {
            this.tatChart.resize()
          }
        })
      },

      /** Diversity tab — gender pie + selection cohort → offer-process milestones (doc / conf / med). */
      renderDiversityPieCharts() {
        const list = this.diversityAnalyticsCandidates || []
        const genderDom = document.getElementById('diversityGenderPieChart')
        const selectionOfferDom = document.getElementById('diversitySelectionOfferStageBarChart')
        if (!genderDom || !selectionOfferDom) {
          setTimeout(() => this.renderDiversityPieCharts(), 250)
          return
        }

        const genderCounts = {}
        list.forEach(c => {
          const g = this.candidateGenderForDiversity(c)
          genderCounts[g] = (genderCounts[g] || 0) + 1
        })

        const toPieData = obj => Object.keys(obj).map(name => ({ name, value: obj[name] }))

        const emptyTitle = {
          text: 'No candidates\n(after filters)',
          left: 'center',
          top: 'center',
          textStyle: { fontSize: 13, color: '#6e6b7b', fontWeight: 'normal' },
        }

        if (!list.length) {
          if (this.diversityGenderPieChart) this.diversityGenderPieChart.dispose()
          this.diversityGenderPieChart = echarts.init(genderDom)
          this.diversityGenderPieChart.setOption({ title: emptyTitle, series: [] })
          if (this.diversitySelectionOfferStageBarChart) this.diversitySelectionOfferStageBarChart.dispose()
          this.diversitySelectionOfferStageBarChart = echarts.init(selectionOfferDom)
          this.diversitySelectionOfferStageBarChart.setOption({ title: emptyTitle, series: [] })
          this.renderDiversityPipelineCharts()
          return
        }

        const pieOptionBase = {
          tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
          legend: { type: 'scroll', bottom: 0 },
          series: [{
            type: 'pie',
            radius: ['38%', '62%'],
            avoidLabelOverlap: true,
            label: { formatter: '{b}\n{d}%' },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
              },
            },
          }],
        }

        this.patchEchartsForPrint(pieOptionBase)

        if (this.diversityGenderPieChart) this.diversityGenderPieChart.dispose()
        this.diversityGenderPieChart = echarts.init(genderDom)
        this.diversityGenderPieChart.setOption({
          ...pieOptionBase,
          color: ['#7367f0', '#00cfe8', '#28c76f', '#ff9f43', '#82868b', '#ea5455'],
          series: [{ ...pieOptionBase.series[0], name: 'Gender', data: toPieData(genderCounts) }],
        })

        if (this.diversitySelectionOfferStageBarChart) this.diversitySelectionOfferStageBarChart.dispose()
        this.diversitySelectionOfferStageBarChart = echarts.init(selectionOfferDom)

        const isSelectionCohort = c => {
          const s = (c.candidateStatus || '').toString()
          return (
            s === 'Selected' ||
            s === 'Shortlisted' ||
            s === 'Offered' ||
            s === 'Offer Accepted' ||
            s === 'Joined'
          )
        }

        const cohort = list.filter(isSelectionCohort)
        const nCohort = cohort.length

        if (nCohort === 0) {
          this.diversitySelectionOfferStageBarChart.setOption({
            title: {
              text: 'No candidates in selection cohort\n(need Selected, Shortlisted, Offered, Offer Accepted, or Joined)',
              left: 'center',
              top: 'center',
              textStyle: { fontSize: 12, color: '#6e6b7b', fontWeight: 'normal' },
            },
            series: [],
          })
          this.renderDiversityPipelineCharts()
          return
        }

        const genderBucket = c => {
          const g = this.candidateGenderForDiversity(c)
          if (g === 'Female' || g === 'Male' || g === 'Other') return g
          return 'Unspecified'
        }

        const summarizeMilestone = testOp => {
          let hit = 0
          const byG = {
            Female: { total: 0, hit: 0 },
            Male: { total: 0, hit: 0 },
            Other: { total: 0, hit: 0 },
            Unspecified: { total: 0, hit: 0 },
          }
          cohort.forEach(c => {
            const b = genderBucket(c)
            byG[b].total += 1
            const op = this.parseOfferProcess(c.offerProcess)
            if (op && testOp(op)) {
              hit += 1
              byG[b].hit += 1
            }
          })
          const pct = Math.round((hit / nCohort) * 1000) / 10
          const lines = []
          ;['Female', 'Male', 'Other', 'Unspecified'].forEach(key => {
            const { total: tg, hit: hg } = byG[key]
            if (tg === 0) return
            const p = Math.round((hg / tg) * 1000) / 10
            lines.push(`${key}: ${hg}/${tg} in cohort (${p}% of ${key})`)
          })
          return { hit, pct, lines, nCohort }
        }

        const stageDefs = [
          {
            label: 'Selection → Documentation',
            test: op => (op.documentation?.status || '') === 'Completed',
          },
          {
            label: 'Selection → Confirmation',
            test: op => (op.confirmation?.status || '') === 'Confirmed',
          },
          {
            label: 'Selection → Medical',
            test: op => (op.medical?.status || '') === 'Fit',
          },
        ]

        const labels = stageDefs.map(s => s.label)
        const values = []
        const tooltips = []

        stageDefs.forEach(({ test }) => {
          const { hit, pct, lines, nCohort: n } = summarizeMilestone(test)
          values.push(pct)
          tooltips.push(
            `<strong>${hit} / ${n}</strong> in selection cohort (${pct}%)<br/>`
            + '<span style="font-size:11px">' + lines.join('<br/>') + '</span>',
          )
        })

        const selectionCohortBarOpt = {
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
            formatter: params => {
              const p = params[0]
              const i = p.dataIndex
              return `${p.marker}<strong>${p.name}</strong><br/>${tooltips[i]}`
            },
          },
          grid: { left: 8, right: 52, top: 10, bottom: 8, containLabel: true },
          xAxis: {
            type: 'value',
            min: 0,
            max: 100,
            axisLabel: { formatter: '{value}%' },
            splitLine: { lineStyle: { type: 'dashed', color: '#ebe9f1' } },
          },
          yAxis: {
            type: 'category',
            data: labels,
            inverse: true,
            axisLabel: { color: '#5e5873', fontSize: 11 },
          },
          series: [{
            name: '% of selection cohort',
            type: 'bar',
            data: values,
            barMaxWidth: 32,
            itemStyle: { color: '#7367f0' },
            label: {
              show: true,
              position: 'right',
              color: '#5e5873',
              fontSize: 11,
              formatter: p => `${p.value}%`,
            },
          }],
        }
        this.patchEchartsForPrint(selectionCohortBarOpt)
        this.diversitySelectionOfferStageBarChart.setOption(selectionCohortBarOpt)

        this.renderDiversityPipelineCharts()
      },

      /** Shortlisted (Selected + Shortlisted) / Offered / Joined — by gender (bar) and overall mix (pie). */
      renderDiversityPipelineCharts() {
        const barDom = document.getElementById('diversityPipelineByGenderBarChart')
        const pieDom = document.getElementById('diversityPipelinePieChart')
        if (!barDom || !pieDom) {
          setTimeout(() => this.renderDiversityPipelineCharts(), 250)
          return
        }

        const list = this.diversityAnalyticsCandidates || []
        const emptyTitle = msg => ({
          text: msg,
          left: 'center',
          top: 'center',
          textStyle: { fontSize: 13, color: '#6e6b7b', fontWeight: 'normal' },
        })

        if (!list.length) {
          if (this.diversityPipelineBarChart) this.diversityPipelineBarChart.dispose()
          this.diversityPipelineBarChart = echarts.init(barDom)
          this.diversityPipelineBarChart.setOption({ title: emptyTitle('No candidates\n(after filters)'), series: [] })
          if (this.diversityPipelinePieChart) this.diversityPipelinePieChart.dispose()
          this.diversityPipelinePieChart = echarts.init(pieDom)
          this.diversityPipelinePieChart.setOption({ title: emptyTitle('No candidates\n(after filters)'), series: [] })
          return
        }

        const status = c => (c.candidateStatus || '').toString()
        const isShortlisted = s => s === 'Selected' || s === 'Shortlisted'
        const isOffered = s => s === 'Offered' || s === 'Offer Accepted'
        const isJoined = s => s === 'Joined'

        const genders = new Set()
        list.forEach(c => genders.add(this.candidateGenderForDiversity(c)))
        const order = ['Male', 'Female', 'Other', 'Unspecified']
        const categories = Array.from(genders).sort((a, b) => {
          const ia = order.indexOf(a)
          const ib = order.indexOf(b)
          const sa = ia === -1 ? 100 : ia
          const sb = ib === -1 ? 100 : ib
          if (sa !== sb) return sa - sb
          return a.localeCompare(b)
        })

        const shortlistedByG = categories.map(() => 0)
        const offeredByG = categories.map(() => 0)
        const joinedByG = categories.map(() => 0)
        list.forEach(c => {
          const g = this.candidateGenderForDiversity(c)
          const idx = categories.indexOf(g)
          if (idx < 0) return
          const s = status(c)
          if (isShortlisted(s)) shortlistedByG[idx] += 1
          if (isOffered(s)) offeredByG[idx] += 1
          if (isJoined(s)) joinedByG[idx] += 1
        })

        let totalShortlisted = 0
        let totalOffered = 0
        let totalJoined = 0
        list.forEach(c => {
          const s = status(c)
          if (isShortlisted(s)) totalShortlisted += 1
          if (isOffered(s)) totalOffered += 1
          if (isJoined(s)) totalJoined += 1
        })

        const pipelineSum = totalShortlisted + totalOffered + totalJoined
        const scopeStatusLine = pipelineSum === 0 && list.length
          ? this.pipelineScopeStatusSummaryLine(list)
          : ''

        if (this.diversityPipelineBarChart) this.diversityPipelineBarChart.dispose()
        this.diversityPipelineBarChart = echarts.init(barDom)
        const barOption = {
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          legend: { data: ['Shortlisted', 'Offered', 'Joined'], bottom: 0 },
          grid: { left: '3%', right: '4%', bottom: '14%', containLabel: true },
          xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11 } },
          yAxis: { type: 'value', minInterval: 1, name: 'Count' },
          series: [
            { name: 'Shortlisted', type: 'bar', data: shortlistedByG, itemStyle: { color: '#ff9f43' } },
            { name: 'Offered', type: 'bar', data: offeredByG, itemStyle: { color: '#00cfe8' } },
            { name: 'Joined', type: 'bar', data: joinedByG, itemStyle: { color: '#28c76f' } },
          ],
        }
        if (pipelineSum === 0 && list.length) {
          barOption.title = {
            text: `No rows in Selected / Shortlisted / Offered / Joined\n(${list.length} in scope — ${scopeStatusLine})`,
            left: 'center',
            top: '2%',
            textStyle: { fontSize: 11, color: '#6e6b7b', fontWeight: 'normal' },
          }
        }
        this.patchEchartsForPrint(barOption)
        this.diversityPipelineBarChart.setOption(barOption)

        if (this.diversityPipelinePieChart) this.diversityPipelinePieChart.dispose()
        this.diversityPipelinePieChart = echarts.init(pieDom)
        if (pipelineSum === 0) {
          this.diversityPipelinePieChart.setOption({
            title: emptyTitle(
              list.length
                ? `No Selected / Shortlisted / Offered / Joined\nin this scope (${list.length} candidates)\n${scopeStatusLine}`
                : 'No Shortlisted / Offered / Joined\nin current filters'
            ),
            series: [],
          })
        } else {
          const pipelinePieOpt = {
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: { type: 'scroll', bottom: 0 },
            series: [{
              type: 'pie',
              radius: ['38%', '62%'],
              data: [
                { name: 'Shortlisted', value: totalShortlisted, itemStyle: { color: '#ff9f43' } },
                { name: 'Offered', value: totalOffered, itemStyle: { color: '#00cfe8' } },
                { name: 'Joined', value: totalJoined, itemStyle: { color: '#28c76f' } },
              ],
              label: { formatter: '{b}\n{d}%' },
            }],
          }
          this.patchEchartsForPrint(pipelinePieOpt)
          this.diversityPipelinePieChart.setOption(pipelinePieOpt)
        }

        window.addEventListener('resize', () => {
          if (this.diversityPipelineBarChart) this.diversityPipelineBarChart.resize()
          if (this.diversityPipelinePieChart) this.diversityPipelinePieChart.resize()
        })
      },
      renderCandidateDataWeekCharts() {
        this.renderCandidateOfferSelectionBar()
        this.renderCandidateSelectionWeekBar()
        this.renderCandidateJoinedWeekBar()
      },

      /**
       * Single-series weekly bar chart for Candidate Data tab (Diversity-scoped week lists).
       * @param {string} domId
       * @param {string} instanceKey — key on `this` holding the ECharts instance
       */
      renderWeekCountBarChart(domId, instanceKey, weekRows, seriesName, barColor, retryFn) {
        const chartDom = document.getElementById(domId)
        if (!chartDom) {
          setTimeout(retryFn, 250)
          return
        }

        const rows = (weekRows || [])
          .slice()
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
          .slice(-10)
        const categories = rows.map(r => r.weekLabel)
        const seriesData = rows.map(r => Number(r.count) || 0)

        const prev = this[instanceKey]
        if (prev) prev.dispose()
        const chart = echarts.init(chartDom)
        this[instanceKey] = chart

        if (!rows.length) {
          const emptyWeekOpt = {
            title: {
              text: 'No weekly data\nin current filters',
              left: 'center',
              top: 'center',
              textStyle: { fontSize: 13, color: '#6e6b7b', fontWeight: 'normal' },
            },
            xAxis: { type: 'category', data: [] },
            yAxis: { type: 'value', name: 'Count', minInterval: 1 },
            series: [],
          }
          this.patchEchartsForPrint(emptyWeekOpt)
          chart.setOption(emptyWeekOpt)
        } else {
          const weekBarOpt = {
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { data: [seriesName], bottom: 0 },
            grid: { left: '3%', right: '4%', bottom: '14%', containLabel: true },
            xAxis: {
              type: 'category',
              data: categories,
              axisLabel: { rotate: categories.some(l => l.length > 18) ? 28 : 0, fontSize: 10 },
            },
            yAxis: { type: 'value', minInterval: 1, name: 'Count' },
            series: [
              { name: seriesName, type: 'bar', data: seriesData, barMaxWidth: 44, itemStyle: { color: barColor } },
            ],
          }
          this.patchEchartsForPrint(weekBarOpt)
          chart.setOption(weekBarOpt)
        }

        window.addEventListener('resize', () => {
          if (this[instanceKey]) this[instanceKey].resize()
        })
      },

      renderCandidateOfferSelectionBar() {
        this.renderWeekCountBarChart(
          'candidateOfferSelectionBarChart',
          'candidateOfferSelectionBarChart',
          this.offeredWeekwise,
          'Offered',
          '#00cfe8',
          () => this.renderCandidateOfferSelectionBar(),
        )
      },

      renderCandidateSelectionWeekBar() {
        this.renderWeekCountBarChart(
          'candidateSelectionWeekBarChart',
          'candidateSelectionWeekBarChart',
          this.selectionsWeekwise,
          'Selection',
          '#ff9f43',
          () => this.renderCandidateSelectionWeekBar(),
        )
      },

      renderCandidateJoinedWeekBar() {
        this.renderWeekCountBarChart(
          'candidateJoinedWeekBarChart',
          'candidateJoinedWeekBarChart',
          this.joinedWeekwise,
          'Joined',
          '#28c76f',
          () => this.renderCandidateJoinedWeekBar(),
        )
      },

      /** Drop / offer-drop reasons: pie with top slices + Other. */
      renderDropReasonPie() {
        const chartDom = document.getElementById('dropReasonPieChart')
        if (!chartDom) {
          setTimeout(() => this.renderDropReasonPie(), 250)
          return
        }

        if (!this._boundDropReasonPieResize) {
          this._boundDropReasonPieResize = () => {
            if (this.dropReasonPieChart) this.dropReasonPieChart.resize()
          }
          window.addEventListener('resize', this._boundDropReasonPieResize)
        }

        const wrapLegendName = (name, softMax = 30) => {
          if (!name || name.length <= softMax) return name
          const mid = Math.floor(name.length / 2)
          let cut = name.lastIndexOf(' ', mid + 10)
          if (cut < 10) cut = name.lastIndexOf('/', mid + 6)
          if (cut < 6) cut = mid
          const rest = name.slice(cut).replace(/^[\s/]+/, '')
          return `${name.slice(0, cut).trim()}\n${rest}`
        }

        const dropStatuses = new Set(['Drop', 'OfferDrop', 'Offer Drop'])
        const list = (this.diversityAnalyticsCandidates || []).filter(c => dropStatuses.has(c.candidateStatus))
        const rawCounts = {}
        list.forEach(c => {
          const r = (c.drop_reason || c.dropReason || c.rejection_reason || c.reason || 'Not specified').toString().trim() || 'Not specified'
          rawCounts[r] = (rawCounts[r] || 0) + 1
        })
        const entries = Object.entries(rawCounts).sort((a, b) => b[1] - a[1])
        const MAX_SLICES = 8
        let pieData = []
        if (entries.length <= MAX_SLICES) {
          pieData = entries.map(([name, value]) => ({ name, value }))
        } else {
          pieData = entries.slice(0, MAX_SLICES - 1).map(([name, value]) => ({ name, value }))
          const otherSum = entries.slice(MAX_SLICES - 1).reduce((s, [, v]) => s + v, 0)
          pieData.push({ name: 'Other', value: otherSum })
        }

        if (this.dropReasonPieChart) this.dropReasonPieChart.dispose()
        this.dropReasonPieChart = echarts.init(chartDom)
        const chartW = chartDom.clientWidth || 360
        const narrow = chartW < 420

        if (!pieData.length) {
          const dropEmptyOpt = {
            title: {
              text: 'No drop records\nin current filters',
              left: 'center',
              top: 'center',
              textStyle: { fontSize: 14, color: '#6e6b7b', fontWeight: 'normal' },
            },
            series: [],
          }
          this.patchEchartsForPrint(dropEmptyOpt)
          this.dropReasonPieChart.setOption(dropEmptyOpt)
        } else {
          // Slice labels: percent only (avoids clipping long names). Full names in legend with soft wrap.
          const dropPieOpt = {
            tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
            legend: {
              type: 'plain',
              orient: 'horizontal',
              left: 'center',
              bottom: narrow ? 0 : 4,
              width: '96%',
              itemGap: 14,
              itemWidth: 10,
              itemHeight: 10,
              formatter: wrapLegendName,
              textStyle: { fontSize: narrow ? 10 : 11, lineHeight: 15 },
            },
            series: [{
              name: 'Drop reason',
              type: 'pie',
              center: ['50%', narrow ? '40%' : '42%'],
              radius: narrow ? ['30%', '52%'] : ['28%', '48%'],
              avoidLabelOverlap: true,
              data: pieData,
              label: {
                show: true,
                position: 'outside',
                formatter: '{d}%',
                fontSize: 12,
                fontWeight: 600,
                color: '#5e5873',
              },
              labelLine: {
                show: true,
                length: 10,
                length2: 6,
                smooth: true,
              },
              emphasis: {
                label: {
                  show: true,
                  formatter: (p) => `${wrapLegendName(p.name).replace(/\n/g, ' ')}\n${p.percent}%`,
                  fontSize: 10,
                },
              },
            }],
          }
          this.patchEchartsForPrint(dropPieOpt)
          this.dropReasonPieChart.setOption(dropPieOpt)
        }
        this.$nextTick(() => {
          if (this.dropReasonPieChart) this.dropReasonPieChart.resize()
        })
      },

      async fetchRelianceRPOCandidatesAllPages({ status = null, filter = null, pageSize = 500, maxPages = 400 } = {}) {
        const merged = []
        let page = 1
        while (page <= maxPages) {
          const response = await candidateService.getRelianceRPOCandidates({
            page,
            limit: pageSize,
            status,
            filter,
          })
          if (!response || response.status !== 'OK' || !response.data) break
          const raw = response.data.candidates || []
          const offset = merged.length
          raw.forEach((c, i) => {
            merged.push({
              ...c,
              _rowKey: this.candidateApplicationRowKey(c, offset + i),
            })
          })
          const pagination = response.data.pagination || {}
          const totalPages = parseInt(pagination.totalPages, 10) || 1
          if (raw.length === 0 || page >= totalPages) break
          page += 1
        }
        return merged
      },

      async fetchAnalyticsCandidatePool() {
        if (this.analyticsCandidatesLoading) return
        this.analyticsCandidatesLoading = true
        try {
          const merged = await this.fetchRelianceRPOCandidatesAllPages({
            status: null,
            filter: null,
            pageSize: 500,
          })
          this.analyticsCandidatePool = merged
          this.calculateStageCounts()
          this.$nextTick(() => {
            setTimeout(() => {
              const slug = this.activeTabSlug
              if (slug === 'overall' || slug === 'diversity' || slug === 'candidate-data') {
                this.renderChartsForTabSlug(slug, { includeCandidateExtras: slug === 'candidate-data' })
              }
            }, 120)
          })
        } catch (e) {
          console.error('fetchAnalyticsCandidatePool', e)
        } finally {
          this.analyticsCandidatesLoading = false
        }
      },

      candidatesForOfferProcessMetrics() {
        if (this.analyticsCandidatePool && this.analyticsCandidatePool.length > 0) {
          return this.diversityAnalyticsCandidates
        }
        return this.candidates || []
      },

      async loadCandidates() {
        this.candidatesLoading = true
        try {
          const params = {
            page: this.candidatesCurrentPage,
            limit: this.candidatesPerPage,
            status: this.candidateFilters.status && this.candidateFilters.status.length > 0 
              ? this.candidateFilters.status 
              : null,
            filter: this.candidateFilters.search.length >= 4 ? this.candidateFilters.search : null,
          }

          const response = await candidateService.getRelianceRPOCandidates(params)
          
          if (response.status === 'OK') {
            const raw = response.data.candidates || []
            this.candidates = raw.map((c, i) => ({
              ...c,
              _rowKey: this.candidateApplicationRowKey(c, i),
            }))

            const pagination = response.data.pagination || {}
            this.candidatePagination = {
              currentPage: pagination.currentPage || 1,
              totalPages: pagination.totalPages || 1,
              totalRecords: pagination.totalRecords || 0,
              limit: pagination.limit || this.candidatesPerPage,
            }
            
            // Update summary counts from API or calculate
            if (response.data.statusWise) {
              this.calculateSummaryFromStatusWise(response.data.statusWise)
            } else {
              this.calculateSummaryFromCandidates()
            }
            
            // Calculate stage counts
            this.calculateStageCounts()

            // Generate latest updates from candidates
            this.generateLatestUpdates()

            this.$nextTick(() => {
              setTimeout(() => {
                const slug = this.activeTabSlug
                if (slug === 'diversity' || slug === 'candidate-data') {
                  this.renderChartsForTabSlug(slug, { includeCandidateExtras: slug === 'candidate-data' })
                }
              }, 150)
            })
            
          } else {
            console.error('API error:', response)
            this.$bvToast.toast('Error loading candidates', {
              title: 'Error',
              variant: 'danger',
              solid: true
            })
          }
        } catch (error) {
          console.error('Error loading candidates:', error)
          this.$bvToast.toast('Failed to load candidates', {
            title: 'Error',
            variant: 'danger',
            solid: true
          })
        } finally {
          this.candidatesLoading = false
        }
      },
      
      calculateSummaryFromStatusWise(statusWise) {
        this.candidateSummary.total = statusWise.reduce((total, item) => total + parseInt(item.count || 0), 0)
        const sel = parseInt(statusWise.find(item => item.candidateStatus === 'Selected')?.count || 0, 10)
        const sh = parseInt(statusWise.find(item => item.candidateStatus === 'Shortlisted')?.count || 0, 10)
        this.candidateSummary.selected = sel + sh
        this.candidateSummary.offered = parseInt(statusWise.find(item => item.candidateStatus === 'Offered')?.count || 0)
        this.candidateSummary.joined = parseInt(statusWise.find(item => item.candidateStatus === 'Joined')?.count || 0)
      },
      
      calculateSummaryFromCandidates() {
        this.candidateSummary.total = this.candidatePagination.totalRecords
        this.candidateSummary.selected = this.candidates.filter(c => c.candidateStatus === 'Selected' || c.candidateStatus === 'Shortlisted').length
        this.candidateSummary.offered = this.candidates.filter(c => c.candidateStatus === 'Offered').length
        this.candidateSummary.joined = this.candidates.filter(c => c.candidateStatus === 'Joined').length
      },
      
      calculateStageCounts() {
        this.stageCounts = {
          documentation: { pending: 0, completed: 0 },
          confirmation: { pending: 0, confirmed: 0, notConfirmed: 0 },
          medical: { pending: 0, fit: 0, unfit: 0 },
        }

        const source = this.candidatesForOfferProcessMetrics()
        source.forEach(candidate => {
          const offerProcess = this.parseOfferProcess(candidate.offerProcess)
          if (!offerProcess) return
          
          // Documentation
          const docStatus = offerProcess.documentation?.status || 'Pending'
          if (docStatus === 'Completed') {
            this.stageCounts.documentation.completed++
          } else {
            this.stageCounts.documentation.pending++
          }
          
          // Confirmation
          const confStatus = offerProcess.confirmation?.status || 'Pending'
          if (confStatus === 'Confirmed') {
            this.stageCounts.confirmation.confirmed++
          } else if (confStatus === 'Not Confirmed') {
            this.stageCounts.confirmation.notConfirmed++
          } else {
            this.stageCounts.confirmation.pending++
          }
          
          // Medical
          const medStatus = offerProcess.medical?.status || 'Pending'
          if (medStatus === 'Fit') {
            this.stageCounts.medical.fit++
          } else if (medStatus === 'Unfit') {
            this.stageCounts.medical.unfit++
          } else {
            this.stageCounts.medical.pending++
          }
        })
      },

      // ======= Latest Updates & Weekwise Analytics Methods =======
      generateLatestUpdates() {
        const base = this.candidatesForOfferProcessMetrics()
        if (!base || base.length === 0) {
          this.latestUpdates = []
          return
        }

        const sorted = [...base]
          .filter(c => c.updatedAt || c.updated_at || c.createdAt)
          .sort((a, b) => {
            const tb = this.parseCandidateTimestamp(b.updatedAt || b.updated_at) ?? this.parseCandidateTimestamp(b.createdAt) ?? 0
            const ta = this.parseCandidateTimestamp(a.updatedAt || a.updated_at) ?? this.parseCandidateTimestamp(a.createdAt) ?? 0
            return tb - ta
          })
          .slice(0, 10)

        this.latestUpdates = sorted.map((c, index) => ({
          id: this.candidateApplicationRowKey(c, index),
          title: this.getUpdateTitle(c.candidateStatus),
          description: `${this.getCandidateName(c)} - ${this.getPositionName(c)}`,
          icon: this.getStatusIcon(c.candidateStatus),
          variant: this.getStatusVariant(c.candidateStatus),
          timeAgo: this.getTimeAgo(c.updatedAt || c.updated_at || c.createdAt),
          candidate: c,
          status: c.candidateStatus,
          details: true
        }))
      },

      getUpdateTitle(status) {
        const titles = {
          'Joined': 'Candidate Successfully Joined',
          'Offered': 'Offer Extended',
          'Offer Accepted': 'Offer Accepted',
          'Selected': 'Candidate Selected',
          'Shortlisted': 'Candidate Shortlisted',
          'NotOffered': 'Candidate Not Offered',
          'OfferDrop': 'Candidate Dropped Offer',
          'Early Attrition': 'Early Attrition',
          'Attrition': 'Attrition Recorded',
          'ProfileShared': 'Profile Shared',
          'ScreenSelect': 'Screen Select',
          'Interview': 'Interview Scheduled',
          'No Show': 'Candidate No Show',
        }
        return titles[status] || 'Status Updated'
      },

      getStatusIcon(status) {
        const icons = {
          'Joined': 'UserCheckIcon',
          'Offered': 'GiftIcon',
          'Offer Accepted': 'CheckCircleIcon',
          'Selected': 'StarIcon',
          'Shortlisted': 'StarIcon',
          'NotOffered': 'XCircleIcon',
          'OfferDrop': 'UserMinusIcon',
          'Early Attrition': 'AlertTriangleIcon',
          'Attrition': 'AlertCircleIcon',
          'ProfileShared': 'SendIcon',
          'ScreenSelect': 'FilterIcon',
          'Interview': 'VideoIcon',
          'No Show': 'UserXIcon',
        }
        return icons[status] || 'UserIcon'
      },

      getTimeAgo(date) {
        if (!date) return 'N/A'
        const ts = this.parseCandidateTimestamp(date)
        if (ts == null) return 'N/A'
        const now = new Date()
        const then = new Date(ts)
        const diffMs = now - then
        if (Number.isNaN(diffMs)) return 'N/A'
        const diffSecs = Math.floor(diffMs / 1000)
        const diffMins = Math.floor(diffSecs / 60)
        const diffHours = Math.floor(diffMins / 60)
        const diffDays = Math.floor(diffHours / 24)

        if (diffDays > 30) {
          return `${Math.floor(diffDays / 30)} month${diffDays > 60 ? 's' : ''} ago`
        } else if (diffDays > 0) {
          return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
        } else if (diffHours > 0) {
          return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
        } else if (diffMins > 0) {
          return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`
        } else {
          return 'Just now'
        }
      },

      groupCandidatesByWeek(candidates, dateField, altDateField) {
        const weeks = {}
        
        candidates.forEach(c => {
          const dateRaw = c[dateField] || c[altDateField]
          let ts = this.parseCandidateTimestamp(dateRaw)
          if (ts == null && (dateField === 'updatedAt' || altDateField === 'updated_at')) {
            ts = this.parseCandidateTimestamp(c.createdAt)
          }
          if (ts == null) return

          const date = new Date(ts)
          const weekKey = this.getWeekKey(date)
          const weekLabel = this.getWeekLabel(date)
          
          if (!weeks[weekKey]) {
            weeks[weekKey] = {
              weekKey,
              weekLabel,
              count: 0,
              candidates: [],
              startDate: this.getWeekStart(date)
            }
          }
          
          weeks[weekKey].count++
          weeks[weekKey].candidates.push(c)
        })
        
        // Sort weeks by start date (most recent first for past, earliest first for future)
        return Object.values(weeks).sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
      },

      getWeekKey(date) {
        const d = new Date(date)
        const firstDayOfYear = new Date(d.getFullYear(), 0, 1)
        const pastDaysOfYear = (d - firstDayOfYear) / 86400000
        const weekNumber = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
        return `${d.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`
      },

      getWeekLabel(date) {
        const d = new Date(date)
        const dayOfWeek = d.getDay()
        const monday = new Date(d)
        monday.setDate(d.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))
        
        const sunday = new Date(monday)
        sunday.setDate(monday.getDate() + 6)
        
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        
        return `${monthNames[monday.getMonth()]} ${monday.getDate()} - ${monthNames[sunday.getMonth()]} ${sunday.getDate()}`
      },

      getWeekStart(date) {
        const d = new Date(date)
        const dayOfWeek = d.getDay()
        const monday = new Date(d)
        monday.setDate(d.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))
        return monday.toISOString()
      },
      
      onCandidatesPageChange(page) {
        this.candidatesCurrentPage = page
        this.loadCandidates()
      },
      
      onCandidatesPageSizeChange() {
        this.candidatesCurrentPage = 1
        this.loadCandidates()
      },
      
      onCandidateSearchInput() {
        if (this.candidateFilters.search.length >= 4 || this.candidateFilters.search.length === 0) {
          this.candidatesCurrentPage = 1
          this.loadCandidates()
        }
      },
      
      clearCandidateSearch() {
        this.candidateFilters.search = ''
        this.candidatesCurrentPage = 1
        this.loadCandidates()
      },
      
      clearCandidateFilters() {
        this.candidateFilters = {
          search: '',
          status: [],
        }
        this.candidatesCurrentPage = 1
        this.loadCandidates()
      },
      
      // Helper methods
      getCandidateName(candidate) {
        const fullName = candidate.candidate_name || candidate.candidateName || ''
        return fullName.replace(/^SHC-\d+\s*-\s*/, '').trim()
      },
      
      getPositionName(candidate) {
        const positionName = candidate.position_name || ''
        return positionName.replace(/^[A-Z]+-\d+\s*-\s*/, '').trim()
      },

      /**
       * True when two `position_name` values are the same string, or match after the same
       * display normalization (avoids duplicate dropdown rows for coded vs plain titles).
       */
      positionsNameEquivalent(a, b) {
        const ra = (a || '').toString().trim()
        const rb = (b || '').toString().trim()
        if (ra === rb) return true
        const la = this.getPositionName({ position_name: ra }).toLowerCase().trim()
        const lb = this.getPositionName({ position_name: rb }).toLowerCase().trim()
        return Boolean(la && lb && la === lb)
      },

      /** Aligns with Reliance API: `drive_name` / `driveName` plus legacy `drive` / `project` / `hiring_drive`. */
      normalizeDriveLabel(obj) {
        if (!obj) return ''
        return String(
          obj.drive_name ?? obj.driveName ?? obj.drive ?? obj.project ?? obj.hiring_drive ?? '',
        ).trim()
      },

      /** Trims messy function strings from ATS/API (e.g. embedded CR/LF). */
      normalizeFunctionLabel(val) {
        return String(val ?? '')
          .replace(/\r\n/g, ' ')
          .replace(/\s+/g, ' ')
          .trim() || 'N/A'
      },

      /**
       * Candidate `function` for filters: API `function` / `function_name` / `position_function`,
       * else function inherited from `positionData` for `position_name`.
       */
      candidateFunctionLabel(c) {
        if (!c) return 'N/A'
        const pname = (c.position_name || '').toString().trim()
        const fromPosition = pname ? this.positionFunctionByPositionName[pname] : ''
        const raw = c.function ?? c.function_name ?? c.position_function ?? fromPosition
        return this.normalizeFunctionLabel(raw)
      },

      positionMetaByName(positionName) {
        const needle = (positionName || '').toString().trim()
        if (!needle) return {}
        const rows = this.positionData || []
        const exact = rows.find(p => (p.position_name || '') === needle)
        if (exact) return exact
        return rows.find(p => this.positionsNameEquivalent(p.position_name, needle)) || {}
      },

      /**
       * `gender` on each candidate from `reliance-rpo-candidates` (see API response).
       * `Gender` accepted for alternate casing; M/F/Male/Female normalized for chart labels.
       */
      candidateGenderForDiversity(c) {
        if (!c) return 'Unspecified'
        const raw = (c.gender ?? c.Gender ?? '').toString().trim()
        if (!raw) return 'Unspecified'
        const u = raw.toUpperCase()
        if (u === 'M' || u === 'MALE') return 'Male'
        if (u === 'F' || u === 'FEMALE') return 'Female'
        return raw
      },

      /**
       * Official diversity classification only (`diversity_category` / `diversityCategory`).
       * Does not use employee `category` (e.g. White Collar).
       */
      candidateDiversityClassificationLabel(c) {
        if (!c) return 'Unspecified'
        const raw = c.diversity_category ?? c.diversityCategory
        if (raw == null || String(raw).trim() === '') return 'Unspecified'
        return String(raw).replace(/\r\n/g, ' ').replace(/\s+/g, ' ').trim()
      },

      /** Top candidateStatus counts in scope (empty-pipeline UX; status labels only, no PII). */
      pipelineScopeStatusSummaryLine(list) {
        const h = {}
        ;(list || []).forEach(c => {
          const k = (c.candidateStatus != null && String(c.candidateStatus).trim() !== '')
            ? String(c.candidateStatus)
            : 'N/A'
          h[k] = (h[k] || 0) + 1
        })
        return Object.entries(h)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([k, v]) => `${k}: ${v}`)
          .join(' · ')
      },

      getStatusVariant(status) {
        const variants = {
          'ProfileShared': 'light-info',
          'ScreenSelect': 'light-info',
          'ScreenReject':'danger',
          'Rejected':'danger',
          'Interview': 'light-info',
          'No Show': 'warning',
          'Documentation': 'info',
          'Confirmation': 'info',
          'Medical': 'info',
          'Joined': 'success',
          'Selected': 'primary',
          'Shortlisted': 'primary',
          'Offered': 'primary',
          'Offer Accepted': 'primary',
          'NotOffered': 'danger',
          'OfferDrop': 'warning',
          'Early Attrition': 'danger',
          'Attrition': 'danger',
        }
        return variants[status] || 'secondary'
      },
      
      parseOfferProcess(offerProcess) {
        if (!offerProcess) return null
        try {
          return typeof offerProcess === 'string' ? JSON.parse(offerProcess) : offerProcess
        } catch (error) {
          console.error('Error parsing offerProcess:', error)
          return null
        }
      },
      
      getOfferProcessStage(candidate, stageKey) {
        const offerProcess = this.parseOfferProcess(candidate.offerProcess)
        if (!offerProcess || !offerProcess[stageKey]) return 'Pending'
        return offerProcess[stageKey].status || 'Pending'
      },
      
      getOfferStageVariant(status) {
        const variants = {
          'Pending': 'warning',
          'Completed': 'success',
          'Confirmed': 'success',
          'Not Confirmed': 'danger',
          'Fit': 'success',
          'Unfit': 'danger',
        }
        return variants[status] || 'secondary'
      },
      
      getOfferProcessProgress(candidate) {
        const offerProcess = this.parseOfferProcess(candidate.offerProcess)
        if (!offerProcess) return 0
        
        let completed = 0
        const total = 3
        
        if (offerProcess.documentation?.status === 'Completed') completed++
        if (offerProcess.confirmation?.status === 'Confirmed') completed++
        if (offerProcess.medical?.status === 'Fit') completed++
        
        return Math.round((completed / total) * 100)
      },
      
      formatDate(date) {
        if (!date) return 'N/A'
        const ts = this.parseCandidateTimestamp(date)
        if (ts == null) return String(date)
        return new Date(ts).toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      },

      /**
       * Parse Reliance RPO candidate timestamps: ISO (`createdAt`) or display `updatedAt` like `12 May 2026`.
       * @returns {number|null} epoch ms, or null if unparseable
       */
      parseCandidateTimestamp(val) {
        if (val == null) return null
        const s = String(val).trim()
        if (!s) return null
        let d = new Date(s)
        if (!Number.isNaN(d.getTime())) return d.getTime()
        const m = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/i)
        if (m) {
          const day = parseInt(m[1], 10)
          const monStr = m[2].slice(0, 3).toLowerCase()
          const months = {
            jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
            jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
          }
          const mon = months[monStr]
          if (mon != null && !Number.isNaN(day)) {
            d = new Date(parseInt(m[3], 10), mon, day, 12, 0, 0, 0)
            if (!Number.isNaN(d.getTime())) return d.getTime()
          }
        }
        return null
      },
      
      // ========== Week-on-Week Comparison Helper Methods ==========
      
      /**
       * Get WoW comparison data for a specific position and metric
       */
      getPositionWow(position, metricKey) {
        if (!position || !position.wowComparison) return null
        return position.wowComparison[metricKey] || null
      },
      
      /**
       * Format WoW value with + or - sign
       */
      formatWowValue(value) {
        if (value === 0 || value === null || value === undefined) return ''
        return value > 0 ? `+${value}` : `${value}`
      },
      
      /**
       * Get tooltip text for WoW comparison
       */
      getWowTooltip(wow) {
        if (!wow) return ''
        const direction = wow.value >= 0 ? 'increase' : 'decrease'
        return `${Math.abs(wow.value)} ${direction} (${wow.percentage >= 0 ? '+' : ''}${wow.percentage}%) vs last week`
      },
      
      // ========== End Week-on-Week Comparison Helper Methods ==========
      
      getStageCount(stageKey) {
        if (stageKey === 'documentation') {
          return this.stageCounts.documentation.completed
        } else if (stageKey === 'confirmation') {
          return this.stageCounts.confirmation.confirmed
        } else if (stageKey === 'medical') {
          return this.stageCounts.medical.fit
        }
        return 0
      },
      
      getStagePercentage(stageKey) {
        const denom = this.offerProcessPipelineCandidateTotal
        if (!denom) return 0
        return Math.min(100, Math.round((this.getStageCount(stageKey) / denom) * 100))
      },
      
      getCompletedCount() {
        return this.candidatesForOfferProcessMetrics().filter(candidate => {
          const offerProcess = this.parseOfferProcess(candidate.offerProcess)
          if (!offerProcess) return false
          return offerProcess.documentation?.status === 'Completed' &&
                 offerProcess.confirmation?.status === 'Confirmed' &&
                 offerProcess.medical?.status === 'Fit'
        }).length
      },
      
      getInProgressCount() {
        return this.candidatesForOfferProcessMetrics().filter(candidate => {
          const offerProcess = this.parseOfferProcess(candidate.offerProcess)
          if (!offerProcess) return false
          
          const docComplete = offerProcess.documentation?.status === 'Completed'
          const confComplete = offerProcess.confirmation?.status === 'Confirmed'
          const medComplete = offerProcess.medical?.status === 'Fit'
          
          const hasAtLeastOne = docComplete || confComplete || medComplete
          const hasAll = docComplete && confComplete && medComplete
          
          return hasAtLeastOne && !hasAll
        }).length
      },
      
      getNotStartedCount() {
        return this.candidatesForOfferProcessMetrics().filter(candidate => {
          const offerProcess = this.parseOfferProcess(candidate.offerProcess)
          if (!offerProcess) return true
          
          return offerProcess.documentation?.status !== 'Completed' &&
                 offerProcess.confirmation?.status !== 'Confirmed' &&
                 offerProcess.medical?.status !== 'Fit'
        }).length
      },
      
      async downloadCandidateReport() {
        try {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Generating Report',
              text: 'Please wait while we prepare your Excel report...',
              icon: 'LoaderIcon',
              variant: 'info',
            },
          })
          
          const allCandidates = await this.fetchRelianceRPOCandidatesAllPages({
            status: this.candidateFilters.status && this.candidateFilters.status.length > 0
              ? this.candidateFilters.status
              : null,
            filter: this.candidateFilters.search.length >= 4 ? this.candidateFilters.search : null,
            pageSize: 500,
          })

          if (!allCandidates.length) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'No data',
                text: 'No candidates returned for the current filters.',
                icon: 'AlertCircleIcon',
                variant: 'warning',
              },
            })
            return
          }

          const workbook = XLSX.utils.book_new()
          
          // Prepare data for Excel
          const excelData = allCandidates.map(candidate => {
            const offerProcess = this.parseOfferProcess(candidate.offerProcess)
            return {
              'Candidate Name': this.getCandidateName(candidate),
              'Email': candidate.email || '',
              'Contact': candidate.contact_no || '',
              'Position': this.getPositionName(candidate),
              'Client': candidate.client || '',
              'Status': candidate.candidateStatus || '',
              'Documentation': offerProcess?.documentation?.status || 'Pending',
              'Confirmation': offerProcess?.confirmation?.status || 'Pending',
              'Medical': offerProcess?.medical?.status || 'Pending',
              'Progress (%)': this.getOfferProcessProgress(candidate),
              'Last Updated': this.formatDate(candidate.updated_at || candidate.updatedAt),
            }
          })
          
          const worksheet = XLSX.utils.json_to_sheet(excelData)
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Reliance RPO Candidates')
          
          // Generate filename
          const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
          const filename = `Reliance_RPO_Candidates_${timestamp}.xlsx`
          
          // Download
          XLSX.writeFile(workbook, filename)
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Report Downloaded',
              text: `Excel report "${filename}" has been downloaded`,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          })
        } catch (error) {
          console.error('Error downloading report:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Download Failed',
              text: 'Error generating Excel report. Please try again.',
              icon: 'AlertCircleIcon',
              variant: 'danger',
            },
          })
        }
      },
    },
    watch: {
      activeTabIndex(newVal) {
        const slug = this.tabSlugFromIndex(newVal)
        if (!this.suppressTabPersistence) {
          this.persistDashboardUiState()
        }
        if (slug === 'diversity' || slug === 'candidate-data') {
          this.globalFiltersOpen = true
        }
        this.$nextTick(() => {
          setTimeout(() => {
            if (slug === 'overall' || slug === 'selections' || slug === 'timelines') {
              this.renderChartsForTabSlug(slug)
            } else if (slug === 'diversity' || slug === 'candidate-data') {
              const draw = () => {
                this.$nextTick(() => {
                  setTimeout(() => {
                    this.renderChartsForTabSlug(slug, { includeCandidateExtras: slug === 'candidate-data' })
                  }, slug === 'candidate-data' ? 200 : 120)
                })
              }
              this.ensureAnalyticsForTabSlug(slug, draw)
            }
          }, 200)
        })
      },
      '$route.query.tab'() {
        if (!this.suppressTabPersistence) {
          this.applyTabFromRouteQuery(this.$route)
        }
      },
    },
  }
  </script>
  
  <style lang="scss" scoped>
  @import 'vue-select/src/scss/vue-select.scss';

  .reliance-dashboard {
    @media (max-width: 767.98px) {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      overflow-x: hidden;
    }

    &__top-actions {
      @media (max-width: 575.98px) {
        .btn {
          min-height: 2.25rem;
        }
      }
    }

    &__client-name-input {
      width: 180px;
      max-width: 100%;

      @media (max-width: 767.98px) {
        width: 100% !important;
      }
    }

    &__chart-help {
      display: block;
      line-height: 1.4;

      @media (max-width: 767.98px) {
        font-size: 0.7rem;
      }
    }

    &__chart-card-body {
      @media (max-width: 767.98px) {
        padding: 0.5rem 0.65rem !important;
      }
    }

    &__diversity-chart {
      width: 100%;
      height: 300px;
      min-height: 240px;
    }

    &__diversity-pipeline-chart {
      width: 100%;
      height: 320px;
      min-height: 260px;
    }

    .kpi-card {
      cursor: pointer;
      transition: all 0.3s ease;
      border: 1px solid #e8e8e8;
      box-shadow: 0 2px 4px 0 rgba(34, 41, 47, 0.05);
      
      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 12px 0 rgba(34, 41, 47, 0.12);
        border-color: #d4d4d4;
      }
      
      &.active {
        border-color: #7367f0;
        box-shadow: 0 4px 16px 0 rgba(115, 103, 240, 0.25);
      }
  
      ::v-deep .card-body {
        padding: 1rem !important;

        @media (max-width: 767.98px) {
          padding: 0.75rem !important;
        }
      }
    }

    &__filter-toggle {
      cursor: pointer;
      user-select: none;
      &:hover {
        background-color: rgba(115, 103, 240, 0.06);
      }
      &:focus-visible {
        outline: 2px solid rgba(115, 103, 240, 0.45);
        outline-offset: 2px;
      }
    }

    &__filter-panel {
      border: 1px solid #e8e7ed;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px rgba(34, 41, 47, 0.06);
      overflow: hidden;
      background: #fff;

      &--global {
        border-left: 4px solid #7367f0;
      }

      &--diversity {
        border-left: 4px solid #00cfe8;
      }
    }

    &__filter-panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(90deg, #f8f8fa 0%, #fff 100%);
      border-bottom: 1px solid #ebe9f1;
    }

    &__filter-count-badge {
      font-size: 0.75rem;
      min-width: 1.35rem;
    }

    &__filter-panel-body {
      background: #fcfcfd;

      @media (max-width: 767.98px) {
        padding-top: 0.75rem !important;
        padding-bottom: 0.75rem !important;
      }
    }

    &__filter-grid {
      margin-left: -0.25rem;
      margin-right: -0.25rem;
    }

    &__filter-fields {
      gap: 0.35rem 0.5rem;
    }

    &__filter-field {
      min-width: 0;
    }

    &__filter-control {
      min-width: 140px;

      &--date {
        min-width: 150px;
        max-width: 160px;
      }
    }

    &__filter-panel-body .form-group label {
      font-weight: 600;
      color: #5e5873;
      margin-bottom: 0.2rem;
    }

    &__filter-actions {
      border-color: #ebe9f1 !important;
    }

    &__gender-pipeline {
      @media (max-width: 767.98px) {
        ::v-deep .statistics-body {
          padding-left: 0.65rem !important;
          padding-right: 0.65rem !important;
          padding-top: 0.75rem !important;
          padding-bottom: 0.75rem !important;
        }

        h5 {
          font-size: 1rem;
        }
      }

      .reliance-dashboard__gender-pipeline-column {
        box-shadow: 0 1px 3px rgba(34, 41, 47, 0.06);
      }
    }

    .kpi-title {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      line-height: 1.2;
    }

    // Week-on-Week Comparison Styles
    .wow-comparison {
      .wow-badge {
        display: inline-flex;
        align-items: center;
        padding: 0.15rem 0.4rem;
        border-radius: 4px;
        font-size: 0.7rem;
        font-weight: 600;
        
        &.wow-up {
          background-color: rgba(40, 199, 111, 0.12);
          color: #28c76f;
        }
        
        &.wow-down {
          background-color: rgba(234, 84, 85, 0.12);
          color: #ea5455;
        }
        
        .wow-value {
          margin-left: 2px;
        }
      }
      
      .wow-percent {
        font-size: 0.7rem;
        font-weight: 600;
      }
      
      .wow-label {
        font-size: 0.65rem;
      }
    }

    // Position table WoW inline styles
    .position-metric {
      display: flex;
      align-items: center;
      gap: 4px;
      
      .wow-inline {
        font-size: 0.65rem;
        font-weight: 600;
        padding: 1px 3px;
        border-radius: 3px;
        
        &.wow-up {
          color: #28c76f;
          background-color: rgba(40, 199, 111, 0.1);
        }
        
        &.wow-down {
          color: #ea5455;
          background-color: rgba(234, 84, 85, 0.1);
        }
      }
    }
  
    .tat-card {
      border-left: 3px solid #7367f0;
      border: 1px solid #e8e8e8;
    }

    .position-filters {
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #e9ecef;
    }
  
    .custom-tabs {
      ::v-deep .nav-pills {
        .nav-link {
          font-weight: 600;
          font-size: 1rem;
        }
      }

      @media (max-width: 767.98px) {
        ::v-deep .card > .card-header {
          padding: 0.5rem 0.5rem !important;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        ::v-deep .nav-pills {
          flex-wrap: nowrap;
          min-height: 2.5rem;

          .nav-item {
            flex-shrink: 0;
          }

          .nav-link {
            font-size: 0.72rem;
            padding: 0.35rem 0.55rem;
            white-space: nowrap;
          }
        }
      }
    }
  
    .avatar {
      width: 44px;
      height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.5rem;
      
      &-sm {
        width: 36px;
        height: 36px;
      }
    }
  
    .badge-sm {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
    }

    // Metric Cards
    .metric-card {
      border: none;
      border-radius: 12px;
      transition: all 0.3s ease;
      padding: 1rem;

      @media (max-width: 767.98px) {
        padding: 0.65rem 0.75rem;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      }

      .metric-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .metric-info {
        flex: 1;
      }

      .metric-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

        @media (max-width: 767.98px) {
          width: 40px;
          height: 40px;
        }
      }

      .metric-value {
        font-size: 1.75rem;
        font-weight: 700;
        margin: 0;
        color: #4a5568;

        @media (max-width: 767.98px) {
          font-size: 1.35rem;
        }
      }

      .metric-label {
        margin: 0;
        color: #718096;
        font-size: 0.85rem;
        font-weight: 500;
      }

      &.primary-gradient {
        background: linear-gradient(135deg, #f8f9ff 0%, #e8ecff 100%);
        border: 1px solid #e3e8ff;
      }

      &.success-gradient {
        background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
        border: 1px solid #c6f6d5;
      }

      &.info-gradient {
        background: linear-gradient(135deg, #f0f9ff 0%, #e6f3ff 100%);
        border: 1px solid #bee3f8;
      }

      &.warning-gradient {
        background: linear-gradient(135deg, #fffbeb 0%, #fef5e7 100%);
        border: 1px solid #fed7aa;
      }
    }

    // Timeline Card (Latest Updates - similar to KPMG)
    .timeline-card {
      border: none;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

      .timeline-scroll {
        padding-right: 0.5rem;
      }

      .timeline-details {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
      }

      .timeline-main-info {
        flex: 1;

        .candidate-name {
          font-size: 0.9rem;
          color: #333;
        }

        .position-name {
          font-size: 0.8rem;
        }
      }

      .timeline-side-info {
        flex-shrink: 0;
        text-align: right;
      }
    }

    // Analysis Card
    .analysis-card {
      border: none;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      overflow-x: hidden;

      .stage-analysis,
      .completion-summary {
        min-width: 0;
      }

      ::v-deep .reliance-dashboard__stage-progress .progress {
        min-height: 14px;
        border-radius: 6px;
      }

      ::v-deep .reliance-dashboard__stage-progress .progress-bar {
        min-width: 4px;
      }
    }

    &__completion-summary-col {
      @media (min-width: 768px) {
        border-left: 1px solid #ebe9f1;
        padding-left: 1rem !important;
      }
    }

    &__drop-reason-card {
      max-width: min(100%, 720px);
      margin-left: auto;
      margin-right: auto;
    }

    &__drop-reason-card-header {
      .reliance-dashboard__drop-reason-card-title {
        gap: 0.35rem;
      }
    }

    &__drop-reason-chart {
      width: 100%;
      height: 340px;
      min-height: 280px;
    }

    &__week-chart-host {
      width: 100%;
      height: 280px;
    }

    &__main-chart-host {
      width: 100%;
      height: 350px;
      overflow: visible;
    }

    &__tat-chart-host {
      width: 100%;
      height: 380px;
      min-height: 320px;
    }

    &__selection-funnel-host {
      width: 100%;
      height: 400px;
      overflow: visible;
    }

    @media (max-width: 767.98px) {
      &__week-chart-host {
        height: 220px;
      }

      &__main-chart-host {
        height: 260px !important;
      }

      &__tat-chart-host {
        height: 280px !important;
        min-height: 240px !important;
      }

      &__drop-reason-chart {
        height: 300px;
        min-height: 260px;
      }

      .analysis-card {
        ::v-deep .card-body {
          padding: 0.75rem !important;
        }
      }

      .stage-analysis .stage-analysis-item {
        margin-bottom: 0.75rem !important;
      }

      &__diversity-chart {
        height: 220px !important;
        min-height: 200px;
      }

      &__diversity-pipeline-chart {
        height: 240px !important;
        min-height: 200px;
      }
    }

    // Candidates Card
    .candidates-card {
      border: none;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

      .table-controls {
        background: #f8f9fa;
        padding: 1rem;
        border-radius: 8px;
        border: 1px solid #e9ecef;
      }

      .table-header-info {
        background: #f8f9fa;
        padding: 0.75rem 1rem;
        border-radius: 6px;
        border: 1px solid #e9ecef;
      }

      .candidates-table {
        .candidate-info {
          .candidate-name {
            max-width: 150px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .position-info {
          max-width: 180px;
          
          .position-name {
            font-weight: 600;
            color: #495057;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .status-indicator {
          display: flex;
          align-items: center;
        }

        .offer-process-stage-info {
          .stage-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            
            .stage-label {
              font-size: 0.75rem;
              color: #6c757d;
              min-width: 35px;
            }
          }
        }

        .offer-process-progress {
          display: flex;
          flex-direction: column;
          min-width: 100px;
        }

        .action-buttons {
          display: flex;
          gap: 0.25rem;
        }
      }
    }

    // Stage Analysis
    .stage-analysis {
      .stage-analysis-item {
        padding: 0.5rem 0;
      }
    }

    // Stage Analysis Card
    .stage-analysis-card {
      padding: 1rem;
      background-color: #f8f9fa;
      border-radius: 8px;
      border: 1px solid #e9ecef;
      height: 100%;

      .progress-stack {
        .progress {
          border-radius: 4px;
        }
      }
    }

    // Weekwise Card
    .weekwise-card {
      height: 100%;
      
      ::v-deep .card-body {
        padding: 0.75rem !important;
      }

      ::v-deep .card-header {
        padding: 0.75rem 1rem !important;
        background-color: transparent;
        border-bottom: 1px solid #e9ecef;
      }
    }

    // Timeline styles
    .timeline-time {
      font-size: 0.75rem;
      white-space: nowrap;
    }

    // Completion Summary
    .completion-summary {
      .summary-item {
        padding: 0.5rem 0;
        border-bottom: 1px solid #f1f3f4;
        
        &:last-child {
          border-bottom: none;
        }
      }
    }

    // Search container
    .search-container {
      .input-group {
        flex-wrap: nowrap;
      }
      
      .is-invalid {
        border-color: #dc3545;
      }
    }

    // Plan B: print / Save as PDF
    &__print-prep-overlay {
      position: fixed;
      inset: 0;
      z-index: 10050;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--print-layout {
      ::v-deep .custom-tabs.card > .card-header {
        display: none !important;
      }

      ::v-deep .custom-tabs .tab-content > .tab-pane {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
    }
  }

  // v-select styling
  .v-select {
    min-width: 180px;
    
    .vs__dropdown-toggle {
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      background-color: #fff;
      min-height: 38px;
    }
    
    .vs__dropdown-menu {
      border: 1px solid #ced4da;
      border-radius: 0.375rem;
      box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    }
  }

  </style>

<style lang="scss">
/* Screen: hide print-only cover & per-tab headings (do not use d-none+d-print-block — bundle order can hide them in PDF → blank first page). */
.reliance-dashboard .reliance-dashboard__print-toc,
.reliance-dashboard .reliance-dashboard__print-section-title {
  display: none !important;
}

/* Plan B: ensure all tabs print as separate pages (Bootstrap-Vue tab panes live in child DOM) */
@media print {
  @page {
    margin: 0;
    size: auto;
  }

  /* Full-bleed sheet; tiny inset keeps text off the physical non-printable edge */
  .reliance-dashboard {
    padding: 3mm 4mm 4mm !important;
    box-sizing: border-box !important;
    font-size: 10.5pt !important;
    line-height: 1.45 !important;
    color: #1a1a1a !important;
  }

  .reliance-dashboard small,
  .reliance-dashboard .text-muted {
    color: #454545 !important;
    font-size: 0.82rem !important;
  }

  .reliance-dashboard .card-header small {
    font-size: 0.8rem !important;
    line-height: 1.35 !important;
    color: #3d3d3d !important;
  }

  .reliance-dashboard .custom-tabs.card > .card-header {
    display: none !important;
  }

  /* ─── Page 1: cover sheet (analysis starts page 2; no forced 100vh — avoids an empty-looking first sheet) ─── */
  .reliance-dashboard .reliance-dashboard__print-toc {
    display: flex !important;
    min-height: 0;
    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    padding: 14mm 12mm;
    margin: 0 !important;
    page-break-after: always !important;
    break-after: page !important;
    page-break-inside: avoid;
    background: #f4f3f9 !important;
    border: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard__print-cover {
    width: 100%;
    max-width: 520px;
    margin: 0 auto;
    text-align: center;
  }

  .reliance-dashboard__print-cover-eyebrow {
    margin: 0 0 0.65rem;
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: #7367f0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .reliance-dashboard__print-cover-title {
    margin: 0 0 0.5rem;
    font-size: 1.85rem;
    font-weight: 800;
    line-height: 1.15;
    color: #2c2c2c !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .reliance-dashboard__print-cover-lead {
    margin: 0 0 0.35rem;
    font-size: 0.98rem;
    font-weight: 500;
    color: #5e5873 !important;
  }

  .reliance-dashboard__print-cover-date {
    margin: 0 0 1.1rem;
    font-size: 0.82rem;
  }

  .reliance-dashboard__print-cover-client {
    display: inline-block;
    margin: 0 auto 1.25rem;
    padding: 0.5rem 1rem;
    text-align: center;
    border: 1px solid #d0cee0 !important;
    border-radius: 0.35rem;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .reliance-dashboard__print-cover-client-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #6e6b7b !important;
    margin-bottom: 0.2rem;
  }

  .reliance-dashboard__print-cover-client-value {
    font-size: 0.95rem;
    font-weight: 600;
    color: #2c2c2c !important;
  }

  .reliance-dashboard__print-cover-accent {
    width: 88px;
    height: 4px;
    margin: 0 auto 1.35rem;
    border-radius: 2px;
    background: linear-gradient(90deg, #7367f0 0%, #00cfe8 100%) !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard__print-cover-sections-title {
    margin: 0 0 0.55rem;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #5e5873 !important;
  }

  .reliance-dashboard__print-cover-sections {
    margin: 0 auto 1.25rem;
    padding: 0.85rem 1.1rem 0.85rem 1.85rem;
    text-align: left;
    display: inline-block;
    max-width: 100%;
    border: 1px solid #e0dde8 !important;
    border-radius: 0.35rem;
    background: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 0.82rem;
    line-height: 1.45;
    color: #5e5873 !important;
  }

  .reliance-dashboard__print-cover-sections li {
    margin-bottom: 0.25rem;
  }

  .reliance-dashboard__print-cover-sections li:last-child {
    margin-bottom: 0;
  }

  .reliance-dashboard__print-cover-footnote {
    margin: 0;
    font-size: 0.72rem !important;
    line-height: 1.4;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
  }

  /* Per-tab page title (shown only in print) */
  .reliance-dashboard .reliance-dashboard__print-section-title {
    display: block !important;
    font-size: 1.22rem !important;
    font-weight: 800 !important;
    color: #252525 !important;
    margin: 0 0 0.65rem !important;
    padding-bottom: 0.4rem !important;
    border-bottom: 3px solid #7367f0 !important;
    letter-spacing: -0.02em;
    page-break-after: avoid;
  }

  .reliance-dashboard .custom-tabs .tab-content > .tab-pane {
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
    page-break-after: auto !important;
    break-after: auto !important;
  }

  /* One section per page without double breaks (avoids blank pages) */
  .reliance-dashboard .custom-tabs .tab-content > .tab-pane:not(:first-of-type) {
    page-break-before: always;
    break-before: page;
  }

  /* Compact cards & tables */
  .reliance-dashboard .card {
    border: 1px solid #dee2e6 !important;
    box-shadow: none !important;
    margin-bottom: 0.4rem !important;
  }

  .reliance-dashboard .card-header {
    padding: 0.35rem 0.5rem !important;
    min-height: 0 !important;
  }

  .reliance-dashboard .card-header h4,
  .reliance-dashboard .card-header h5,
  .reliance-dashboard .card-header h6 {
    font-size: 0.98rem !important;
    font-weight: 700 !important;
    color: #222 !important;
    margin-bottom: 0 !important;
  }

  .reliance-dashboard .card-body {
    padding: 0.5rem 0.55rem !important;
  }

  .reliance-dashboard .table {
    font-size: 0.92rem !important;
    margin-bottom: 0.25rem !important;
    font-variant-numeric: tabular-nums;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard .table th,
  .reliance-dashboard .table td {
    padding: 0.26rem 0.36rem !important;
    line-height: 1.42 !important;
    vertical-align: middle !important;
    color: #1e1e1e !important;
  }

  .reliance-dashboard .table th {
    font-weight: 700 !important;
    color: #2c2c2c !important;
  }

  .reliance-dashboard .table-sm th,
  .reliance-dashboard .table-sm td {
    padding: 0.16rem 0.26rem !important;
  }

  .reliance-dashboard .badge {
    font-size: 0.72rem !important;
    padding: 0.14em 0.4em !important;
    font-weight: 600 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard .kpi-card h3 {
    font-size: 1.2rem !important;
    font-weight: 800 !important;
    color: #1a1a1a !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard .metric-card .metric-value {
    font-size: 1.45rem !important;
    font-weight: 800 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard .wow-value,
  .reliance-dashboard .wow-percent {
    font-size: 0.78rem !important;
    font-weight: 700 !important;
  }

  .reliance-dashboard__print-table-foot {
    font-size: 0.76rem !important;
    line-height: 1.35 !important;
    margin-top: 0.25rem !important;
    color: #3d3d3d !important;
  }

  .reliance-dashboard .row {
    margin-left: -6px !important;
    margin-right: -6px !important;
  }

  .reliance-dashboard .row > [class*='col-'] {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  .reliance-dashboard .progress {
    height: 9px !important;
  }

  .reliance-dashboard .stage-analysis-item,
  .reliance-dashboard .stage-analysis-item .font-weight-bold {
    font-size: 0.88rem !important;
  }

  .reliance-dashboard .timeline-details,
  .reliance-dashboard .timeline-details .text-muted {
    font-size: 0.8rem !important;
    color: #424242 !important;
  }

  .reliance-dashboard .avatar,
  .reliance-dashboard .b-avatar {
    width: 28px !important;
    height: 28px !important;
  }

  .reliance-dashboard .table-responsive {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Hide controls that are not usable on paper */
  .reliance-dashboard .btn,
  .reliance-dashboard .pagination,
  .reliance-dashboard .page-link {
    display: none !important;
  }

  .reliance-dashboard .table.b-table > thead > tr > th {
    cursor: default !important;
  }

  /* Center chart containers */
  .reliance-dashboard .reliance-dashboard__main-chart-host,
  .reliance-dashboard .reliance-dashboard__tat-chart-host,
  .reliance-dashboard .reliance-dashboard__selection-funnel-host,
  .reliance-dashboard .reliance-dashboard__diversity-chart,
  .reliance-dashboard .reliance-dashboard__diversity-pipeline-chart,
  .reliance-dashboard .reliance-dashboard__week-chart-host,
  .reliance-dashboard [id$="BarChart"],
  .reliance-dashboard #dropReasonPieChart {
    margin-left: auto !important;
    margin-right: auto !important;
  }

  .reliance-dashboard .reliance-dashboard__charts-row > [class*='col-'] > .card,
  .reliance-dashboard .reliance-dashboard__drop-reason-card {
    max-width: 100% !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .reliance-dashboard .reliance-dashboard__main-chart-host,
  .reliance-dashboard .reliance-dashboard__tat-chart-host,
  .reliance-dashboard .reliance-dashboard__selection-funnel-host,
  .reliance-dashboard .reliance-dashboard__diversity-chart,
  .reliance-dashboard .reliance-dashboard__diversity-pipeline-chart,
  .reliance-dashboard .reliance-dashboard__week-chart-host,
  .reliance-dashboard .reliance-dashboard__drop-reason-chart,
  .reliance-dashboard [id$='BarChart'] {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .reliance-dashboard #dropReasonPieChart {
    height: 380px !important;
    min-height: 340px !important;
  }

  .reliance-dashboard .table.b-table > tbody > tr > td.text-center {
    font-size: inherit !important;
    line-height: 1.42 !important;
  }

  /* Charts & icons: reliable color output; no hover chrome */
  .reliance-dashboard canvas {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .reliance-dashboard .card-header feather-icon {
    display: none !important;
  }

  body > .tooltip,
  body > .popover,
  body > .b-toaster {
    display: none !important;
  }
}
</style>